import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClockIcon, CloseIcon, PlusIcon, ChevronDownIcon, ChevronUpIcon } from '../chat/SidebarIcons';
import ReminderItem from './ReminderItem';
import ReminderForm from './ReminderForm';
import ReminderTriggeredModal from './ReminderTriggeredModal';
import ReminderHistoryReport from './ReminderHistoryReport';
import { formatSnoozeDuration } from './SnoozeDropdown';
import * as reminderService from './reminderService';
import { getSortableTriggerMinutes, reminderPendingOnDate, reminderCompletedOnDate, listRecentDates, formatDateBarParts, formatSelectedDayLabel, isReminderTriggerOverdue } from './reminderUtils';
import { addNotification, setChatMappingTarget, clearChatMappingTarget, clearReminderInvite } from '../../store/slices/uiSlice';
import { setCurrentChannel, addReaction } from '../../store/slices/chatSlice';
import { fetchChannels, fetchDirectMessages } from '../../store/slices/channelSlice';
import { API_URL } from '../../config/api';
import { TaskStatus, markTaskAsCompleted, undoTaskCompletion, updateTaskProgress, fetchDailyAnalytics, fetchTasksByMapTaskId, fetchTasks, deleteTask, updateTask, createTask, todayScreenOwnerKey, setTodayPickTasksCache, setTodayWorkflowsCache, applyLiveTaskToTodayCache } from '../../store/slices/taskSlice';
import { loadOrgSpaces, loadSpaceAnalytics, upsertSpace } from '../../store/slices/spaceSlice';
import { fetchUsers } from '../../store/slices/userSlice';
import TaskCreationDialog from '../tasks/TaskCreationDialog';
import TaskViewDialog from '../tasks/TaskViewDialog';
import WorkflowViewDialog from '../chat/WorkflowViewDialog';
import JiraAssignedTicketsList from '../jira/JiraAssignedTicketsList';
import { normalizeTaskProgress } from '../tasks/TaskProgressControl';
import QuickTaskComposer from '../tasks/QuickTaskComposer';
import TaskSubtaskItem, { TaskEstimateBadge } from '../tasks/TaskSubtaskItem';
import ConfirmModal from '../common/ConfirmModal';
import MagicPickModal, { MagicWandIcon } from '../common/MagicPickModal';
import SharedPanelsWidget from './SharedPanelsWidget';
import TodayProductivityChart, { CurrentPresenceStatus, HomePendingCard, DayActivityLog } from './TodayProductivityChart';
import NeedToCallDetailModal from '../../features/notes/components/NeedToCallDetailModal';
import { fetchNeedToCall, saveNeedToCall } from '../../features/notes/services/noteService';
import ChatMessageBubblePreview from '../chat/ChatMessageBubblePreview';
import ImageViewerModal from '../chat/ImageViewerModal';
import WorkSessionWatch, {
  calcOfficeProductivityPercent,
  isActiveWorkSession,
  resolveOfficeMinutesForProductivity,
} from '../attendance/WorkSessionWatch';
import {
  formatProductivityEntryLabel,
  sumCreditedMinutes,
  isDisplayableProductivityEntry,
  countUniqueTasksWithProgress,
  filterProductivityEntriesForCompletedList,
  resolveProgressPercent,
} from '../../utils/productivityEntryUtils';
import { fetchBreakStatus, fetchDayActivityTimeline, fetchLoginDays } from '../../services/attendanceService';
import { subscribeAttendanceSessionChanged } from '../../services/attendanceSessionBus';
import { downloadAttachment, openAttachmentFallback, stripHtmlForTextExtraction } from '../../utils/fileUtils';
import { markPanelEventsSeen } from '../../store/slices/panelAccessSlice';
import { WORKFLOW_CLOSED_STATUS_ID, getReactionDisplay, getActiveWorkflowByCategory, applyOptimisticReaction } from '../../utils/reactionConfig';
import UrgentCountdownBadge from '../chat/UrgentCountdownBadge';
import UrgentTag, { UrgentRowShell } from '../chat/UrgentTag';
import {
  compareUrgentFirst,
  isOpenUrgentTask,
  isOpenUrgentWorkflow,
} from '../../utils/urgentDeadlineUtils';
import { collectTodaySpaceGroups, resolveItemSpace, resolveTaskSpace, shouldShowSpacesTab, spaceTone, workflowSpaceIndex, spaceInitials, SPACE_ICON_PRESETS, OTHER_SPACE_KEY, spaceWorkLabel, spaceWorkCompact, pendingSharePercents } from '../../utils/spaceUtils';
import { updateSpace } from '../../services/spaceService';
import { store } from '../../store';
import { readTasksFromRealm, readAssignedWorkflowsFromRealm, saveAssignedWorkflowsToRealm, upsertRealmDocs, readRemindersFromRealm, realmAvailable } from '../../realm';
import { overlayPendingTasks } from '../../realm/outboundQueue';
import { resolveLocalMessage } from '../../realm/offlineLookups';
import { useCompletionTimeDialog } from '../chat/CompletionTimeDialog';
import WorkItemTimer from '../common/WorkItemTimer';
import {
  compareWorkTimerPriority,
  ensureWorkTimers,
  subscribeWorkTimers,
} from '../../services/workTimerStore';
import { formatEstimateTime } from '../../utils/estimateTime';
import { formatTimeBack } from '../../utils/timeBack';
import {
  isWorkflowLinkedTask,
  isWorkflowMappingParent,
  isWorkflowShellTask,
  taskShowsWorkflowTag,
  resolveTaskWorkflowChannelLabel,
  uniqueTasksById,
  formatTaskAssigneeLabel,
  taskDelegateIds,
  resolveWorkflowCloseMinutes,
  unfinishedWorkflowLinkedCount,
  visibleWorkflowLinkedTasks,
  unfinishedSubtasksError,
  unfinishedSubtasksMessage,
  apiErrorMessage,
  workflowLinkedTasksFrom,
  isTaskTimeRecorded,
} from '../../utils/workflowTaskUtils';

const getWorkflowTicketText = (content, maxLen = 80) => {
  if (!content || !content.trim()) return 'Workflow item';
  const trimmed = content.trim();
  return trimmed.length <= maxLen ? trimmed : `${trimmed.slice(0, maxLen - 1)}…`;
};

const mergeAssignedWorkflowItems = (...lists) => {
  const byId = new Map();
  lists.flat().forEach((item) => {
    const id = item?.messageId;
    if (!id || byId.has(String(id))) return;
    byId.set(String(id), item);
  });
  return Array.from(byId.values());
};

const messageToAssignedWorkflowItem = (msg, userId, channels = [], users = []) => {
  if (!msg || !userId || String(msg.workflowAssignedToUserId || '') !== String(userId)) return null;
  if (msg.hideFromChat) return null;
  const workflowByCategory = msg.workflowByCategory && Object.keys(msg.workflowByCategory).length
    ? msg.workflowByCategory
    : getActiveWorkflowByCategory(msg.reactions);
  if (!workflowByCategory || Object.keys(workflowByCategory).length === 0) return null;
  if (workflowByCategory['workflow-status'] === WORKFLOW_CLOSED_STATUS_ID) return null;
  const messageId = msg.id || msg._id || msg.messageId;
  if (!messageId) return null;
  const item = {
    messageId,
    channelId: msg.channelId,
    senderId: msg.senderId,
    senderName: msg.senderName,
    senderProfilePicture: msg.senderProfilePicture,
    content: stripHtmlForTextExtraction(msg.content) || getWorkflowTicketText(msg.content),
    contentHtml: msg.content,
    workflowTitle: msg.workflowTitle,
    workflowDescription: msg.workflowDescription,
    workflowSpaceId: msg.workflowSpaceId,
    workflowSpaceName: msg.workflowSpaceName,
    replyToMessageId: msg.replyToMessageId,
    workflowTrackId: msg.workflowTrackId,
    workflowTrackLink: msg.workflowTrackLink,
    attachments: msg.attachments || [],
    createdAt: msg.createdAt || msg.timestamp,
    workflowByCategory,
    customTags: msg.customTags || [],
    workflowAssignedToUserId: msg.workflowAssignedToUserId,
    workflowAssignedToName: msg.workflowAssignedToName,
  };
  const source = resolveWorkflowSourceLabel(item, channels, users, userId);
  item.sourceLabel = source?.label || item.sourceLabel;
  item.channelType = source?.kind === 'dm' ? 'DIRECT_MESSAGE' : item.channelType;
  return item;
};

const collectAssignedWorkflowsFromLoadedChats = (userId) => {
  if (!userId) return [];
  const state = store.getState();
  const byChannel = state?.chat?.messages || {};
  const channels = [...(state?.channels?.channels || []), ...(state?.channels?.directMessages || [])];
  const users = state?.users?.users || [];
  const items = [];
  Object.values(byChannel).forEach((list) => {
    (list || []).forEach((msg) => {
      const item = messageToAssignedWorkflowItem(msg, userId, channels, users);
      if (item) items.push(item);
    });
  });
  return items;
};

const MapLinkIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
  </svg>
);

const CompleteTaskIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const DeleteTaskIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

/** Four outward corner arrows — expand Today's Tasks to fill the main area */
const MaximizeSectionIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 9V5h4M20 9V5h-4M4 15v4h4M20 15v4h-4" />
  </svg>
);

/** Four inward corner arrows — exit maximized section */
const MinimizeSectionIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4H5v4M15 4h4v4M9 20H5v-4M15 20h4v-4" />
  </svg>
);

/** Eye — show section */
const ShowSectionIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

/** Eye-off — hide section */
const HideSectionIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const ViewAllRemindersIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h10" />
  </svg>
);

const PendingApprovalsIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const RefreshIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const sectionFocusButtonClass =
  'inline-flex items-center justify-center h-7 w-7 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-white/80 dark:hover:bg-gray-700/70 hover:text-gray-800 dark:hover:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/35 transition-colors flex-shrink-0';

/** One card: header + list body (matches Workflows Assigned screenshot) */
const sectionCardClass =
  'rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-800 shadow-sm min-w-0 overflow-hidden';

const sectionCardHeaderClass =
  'relative z-20 flex items-center gap-1.5 px-3.5 py-2.5 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700/80';

const sectionCardBodyClass =
  'divide-y divide-gray-100/90 dark:divide-gray-700/60 min-w-0 overflow-hidden';

const sectionListItemClass =
  'w-full min-w-0 bg-white dark:bg-gray-800 hover:bg-orange-50/50 dark:hover:bg-gray-700/30 transition-colors';

const sectionRowPadClass = 'flex items-start gap-2.5 px-2.5 py-2 sm:px-3.5 sm:py-3 min-w-0';

const sectionHeadingClass = (completed) =>
  `truncate text-[15px] font-bold tracking-tight ${
    completed ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-900 dark:text-white'
  }`;

const sectionCountPillClass = (completed) =>
  `flex-shrink-0 inline-flex items-center justify-center min-w-[1.375rem] h-5 px-1.5 rounded-full text-[11px] font-semibold tabular-nums ${
    completed
      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
  }`;

const itemTitleClass =
  'min-w-0 flex-1 text-[15px] font-bold tracking-tight text-gray-900 dark:text-white leading-snug';

const sameUserId = (a, b) => {
  if (a == null || b == null) return false;
  return String(a).toLowerCase() === String(b).toLowerCase();
};

const taskAssignedToUser = (task, userId) => {
  if (!task || !userId) return false;
  const delegates = taskDelegateIds(task);
  if (delegates.length) {
    return delegates.some((id) => sameUserId(id, userId));
  }
  return sameUserId(task.createdByUserId, userId);
};

const getTaskListLabel = (task) => {
  const title = task?.title && task.title.trim() ? task.title.trim() : null;
  if (title) return title;
  const desc = task?.description || '';
  if (desc.trim()) {
    if (desc.trim().startsWith('<')) {
    try {
      const d = document.createElement('div');
        d.innerHTML = desc;
        const t = (d.textContent || d.innerText || '').trim();
        return t.slice(0, 50) || 'Task';
    } catch (_) {
        return 'Task';
      }
    }
    return desc.trim().slice(0, 50);
  }
  return 'Task';
};

const isTodayWorkTask = (task) => {
  const status = task?.status || task?.statusString;
  if (status === TaskStatus.PICK_TODAY) return true;
  return status === TaskStatus.PENDING
    && Boolean(task?.messageId && String(task.messageId).trim())
    && Boolean((task?.title || '').trim());
};

const isNamedSubtask = (task) => Boolean(
  task
  && (task.title || '').trim()
  && !(task.messageId && String(task.messageId).trim()),
);

const isSubtaskFinished = (task) => {
  const minutes = Number(task?.completionTimeInMinutes);
  return Number.isFinite(minutes) && minutes > 0;
};

const sumSubtaskActualMinutes = (subtasks = []) => (
  subtasks.reduce((sum, item) => {
    const actual = Number(item.completionTimeInMinutes);
    return sum + (Number.isFinite(actual) && actual > 0 ? actual : 0);
  }, 0)
);

/** Parent time is the sum of subtask actual times when subtasks exist. */
const taskActualMinutes = (task, mappedItems = []) => {
  const subtasks = (mappedItems || []).filter(isNamedSubtask);
  if (subtasks.length > 0) {
    const actualSum = sumSubtaskActualMinutes(subtasks);
    if (actualSum > 0) return actualSum;
    return subtasks.reduce((sum, item) => sum + (Number(item.estimateTimeInMinutes) || 0), 0);
  }
  const completionMinutes = Number(task?.completionTimeInMinutes);
  if (Number.isFinite(completionMinutes) && completionMinutes > 0) {
    return completionMinutes;
  }
  return Number(task?.estimateTimeInMinutes) || 0;
};

const itemTitleMutedClass =
  'min-w-0 flex-1 text-[15px] font-bold tracking-tight text-gray-700 dark:text-gray-200 leading-snug';

const personLabel = (user, currentUserId) => {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
    || user?.username
    || 'User';
  return sameUserId(user?.id, currentUserId) ? `You (${name})` : name;
};

const assigneesForWorkflowItem = (item, { channels = [], directMessages = [], users = [], currentUserId } = {}) => {
  const channel = [...channels, ...directMessages].find((ch) => ch.id === item?.channelId);
  const memberIds = channel?.memberIds?.length
    ? [...channel.memberIds]
    : (users || []).map((user) => user.id);
  if (item?.senderId && !memberIds.some((id) => String(id) === String(item.senderId))) {
    memberIds.unshift(item.senderId);
  }
  if (currentUserId && !memberIds.some((id) => String(id) === String(currentUserId))) {
    memberIds.unshift(currentUserId);
  }
  const seen = new Set();
  return memberIds
    .filter((id) => {
      const key = String(id);
      if (!id || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((id) => (users || []).find((user) => String(user.id) === String(id)))
    .filter((user) => user && user.isActive !== false)
    .map((user) => ({
      id: user.id,
      label: personLabel(user, currentUserId),
    }));
};

const assigneeNameForTask = (task, users = [], currentUserId) => (
  formatTaskAssigneeLabel(task, users, currentUserId)
);

const itemIconWrapClass = (done) =>
  `inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl ${
    done
      ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/35 dark:text-emerald-300'
      : 'bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300'
  }`;

const TaskMiniProgress = ({ percent = 0 }) => {
  const pct = Math.max(0, Math.min(100, Number(percent) || 0));
  return (
    <span
      className="flex-shrink-0 self-center inline-flex h-1.5 w-[50px] rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden"
      title={`${pct}% complete`}
      aria-label={`${pct}% complete`}
    >
      <span
        className={`block h-full rounded-full transition-all duration-300 ${
          pct >= 100 ? 'bg-green-500' : pct > 0 ? 'bg-primary-500' : 'bg-transparent'
        }`}
        style={{ width: `${pct}%` }}
      />
    </span>
  );
};

const SpacePercentBadge = ({ percent = 0, selected = false }) => {
  const pct = Math.max(0, Math.min(100, Math.round(Number(percent) || 0)));
  return (
    <span
      className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center text-[12px] font-bold tabular-nums leading-none ${
        selected
          ? 'bg-white text-primary-700 shadow-sm'
          : 'bg-white/90 text-gray-800 border border-black/10 dark:bg-gray-900/70 dark:text-gray-100 dark:border-white/10'
      }`}
      title={`${pct}% of pending tasks`}
    >
      {pct}%
    </span>
  );
};

const SpaceActivityChart = ({ days = [] }) => {
  const values = Array.isArray(days) ? days.map((value) => Math.max(0, Number(value) || 0)) : [];
  if (!values.length) return null;
  const width = 560;
  const height = 112;
  const padX = 8;
  const padY = 12;
  const max = Math.max(1, ...values);
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const step = innerW / Math.max(1, values.length - 1);
  const points = values.map((value, index) => {
    const x = padX + index * step;
    const y = padY + innerH - (value / max) * innerH;
    return [x, y];
  });
  const line = points.map(([x, y], index) => `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${points[points.length - 1][0].toFixed(1)},${(height - padY).toFixed(1)} L${padX},${(height - padY).toFixed(1)} Z`;
  return (
    <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-gradient-to-b from-sky-50/80 to-white dark:from-sky-950/30 dark:to-gray-900 px-3 pt-3 pb-2">
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <p className="text-xs font-bold text-gray-700 dark:text-gray-200">Activity</p>
        <p className="text-[10px] font-medium text-gray-400">Last 30 days</p>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-[88px]" preserveAspectRatio="none" role="img" aria-label="Space activity last 30 days">
        <defs>
          <linearGradient id="spaceActivityFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#spaceActivityFill)" />
        <path d={line} fill="none" stroke="#0ea5e9" strokeWidth="3.5" strokeLinejoin="round" strokeLinecap="round" />
        {points.filter((_, index) => values[index] > 0).map(([x, y], index) => (
          <circle key={index} cx={x} cy={y} r="3.5" fill="#0284c7" stroke="white" strokeWidth="1.5" />
        ))}
      </svg>
      <div className="flex justify-between text-[10px] font-medium text-gray-400 px-0.5">
        <span>30d ago</span>
        <span>Today</span>
      </div>
    </div>
  );
};

const emptyStateClass = 'flex flex-col items-center justify-center px-4 py-8 text-center';
const emptyTitleClass = 'text-sm font-medium text-gray-500 dark:text-gray-400';
const emptyHintClass = 'mt-1 text-xs text-gray-400 dark:text-gray-500';

const chipBaseClass =
  'inline-flex items-center gap-0.5 max-w-[9rem] min-h-[1.375rem] px-1.5 py-0.5 rounded-md text-[10px] font-semibold leading-tight truncate border';

const CHIP_TONE_CLASS = {
  violet: 'border-violet-200/90 bg-violet-50 text-violet-800 dark:border-violet-700/60 dark:bg-violet-900/25 dark:text-violet-200',
  indigo: 'border-indigo-200/90 bg-indigo-50 text-indigo-800 dark:border-indigo-700/60 dark:bg-indigo-900/25 dark:text-indigo-200',
  blue: 'border-sky-200/90 bg-sky-50 text-sky-800 dark:border-sky-700/60 dark:bg-sky-900/25 dark:text-sky-200',
  cyan: 'border-cyan-200/90 bg-cyan-50 text-cyan-800 dark:border-cyan-700/60 dark:bg-cyan-900/25 dark:text-cyan-200',
  teal: 'border-teal-200/90 bg-teal-50 text-teal-800 dark:border-teal-700/60 dark:bg-teal-900/25 dark:text-teal-200',
  green: 'border-emerald-200/90 bg-emerald-50 text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-900/25 dark:text-emerald-200',
  amber: 'border-amber-200/90 bg-amber-50 text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/25 dark:text-amber-100',
  red: 'border-rose-200/90 bg-rose-50 text-rose-800 dark:border-rose-700/60 dark:bg-rose-900/25 dark:text-rose-200',
  slate: 'border-gray-200/90 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700/40 dark:text-gray-300',
};

const chipToneClass = (tone) => CHIP_TONE_CLASS[tone] || CHIP_TONE_CLASS.slate;

const sectionActionIconClass =
  'mt-0.5 p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors flex-shrink-0';

const workflowFilterChipClass = (active) =>
  `${chipBaseClass} cursor-pointer transition-shadow ${
    active
      ? 'ring-2 ring-primary-500/50 ring-offset-1 dark:ring-offset-gray-800'
      : 'hover:brightness-[0.97] dark:hover:brightness-110'
  }`;

const getMessageDisplayAttachments = (message) => {
  if (!message) return [];
  if (Array.isArray(message.timelineAttachments) && message.timelineAttachments.length > 0) {
    return message.timelineAttachments;
  }
  return Array.isArray(message.attachments) ? message.attachments : [];
};

const resolveUserDisplayName = (userId, users = []) => {
  const user = users.find((u) => u.id === userId);
  if (!user) return 'Unknown User';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return name || user.username || 'Unknown User';
};

/** Channel → #name, DM → @person (other participant). Always shown even when Track ID is missing. */
const resolveWorkflowSourceLabel = (item, allChannels = [], users = [], selfUserId = null) => {
  if (item?.sourceLabel) {
    const kind = item.sourceLabel.startsWith('@') ? 'dm' : 'channel';
    return { kind, label: item.sourceLabel };
  }

  const channel = allChannels.find((ch) => ch.id === item?.channelId);
  const type = channel?.type || item?.channelType || '';
  const isDm = type === 'direct' || type === 'DIRECT_MESSAGE';

  if (isDm) {
    const memberIds = channel?.memberIds || item?.channelMemberIds || [];
    const otherId = memberIds.find((id) => id && selfUserId && String(id) !== String(selfUserId))
      || memberIds.find((id) => id && String(id) !== String(item?.senderId));
    const peerName = otherId
      ? resolveUserDisplayName(otherId, users)
      : (item?.channelName || 'DM');
    return { kind: 'dm', label: `@${peerName}` };
  }

  const name = channel?.name || item?.channelName || 'channel';
  return { kind: 'channel', label: `#${name}` };
};

const resolveUserProfilePicture = (userId, users = []) => {
  const user = users.find((u) => u.id === userId);
  return user?.profilePicture || null;
};

const hasTaskDescription = (task) => {
  const desc = (task?.description || '').trim();
  if (!desc) return false;
  if (desc === '<p><br></p>' || desc === '<p></p>') return false;
  return true;
};

const messageToBubbleProps = (message, { onViewImage, onDownload }) => ({
  contentHtml: message.content,
  attachments: getMessageDisplayAttachments(message),
  attachmentGroupName: message.attachmentGroupName,
  linkPreview: message.linkPreview,
  reactions: message.reactions,
  workflowTitle: message.workflowTitle,
  senderName: message.senderName || 'User',
  senderProfilePicture: message.senderProfilePicture,
  createdAt: message.createdAt || message.timestamp,
  alignLeft: true,
  isOwn: false,
  onViewImage,
  onDownload,
  compact: true,
});

const buildNavigateItemFromMessage = (message) => {
  if (!message?.id || !message?.channelId) return null;
  return {
    messageId: message.id,
    channelId: message.channelId,
    replyToMessageId: message.replyToMessageId || null,
  };
};

const taskToBubbleProps = (task, users, { onViewImage, onDownload }) => {
  const desc = task.description || '';
  const isHtml = desc.trim().startsWith('<') && desc.includes('</');
  return {
    contentHtml: isHtml ? desc : undefined,
    content: !isHtml ? (desc || 'No description provided.') : undefined,
    senderName: resolveUserDisplayName(task.createdByUserId, users),
    senderProfilePicture: resolveUserProfilePicture(task.createdByUserId, users),
    createdAt: task.createdAt,
    alignLeft: true,
    isOwn: false,
    showAvatar: true,
    onViewImage,
    onDownload,
    compact: true,
  };
};

const TaskExpandBubble = ({
  task,
  linkedMessage,
  mappedTasks = [],
  taskMessagesByTaskId = {},
  loadingMessage,
  loadingMappedTasks,
  loadingTaskMessageIds,
  users = [],
  onViewImage,
  onDownload,
  enableChatActions = false,
  onChatClick,
  onRemoveChat,
  removingChatKey = null,
  hiddenChatBubbleKeys = new Set(),
}) => {
  const bubbleHandlers = { onViewImage, onDownload };
  const primaryMessageId = task.messageId?.trim?.() || task.messageId;

  if (primaryMessageId && loadingMessage && !linkedMessage && !hasTaskDescription(task)) {
    return <p className="text-xs text-gray-500 dark:text-gray-400 py-1">Loading linked message…</p>;
  }

  const bubbles = [];

  if (primaryMessageId && linkedMessage) {
    bubbles.push({
      key: `primary-msg-${task.id}`,
      props: messageToBubbleProps(linkedMessage, bubbleHandlers),
      navigateItem: buildNavigateItemFromMessage(linkedMessage),
      removePayload: { parentTaskId: task.id, taskId: task.id, type: 'primary-link' },
    });
  } else if (hasTaskDescription(task)) {
    bubbles.push({
      key: `primary-task-${task.id}`,
      props: taskToBubbleProps(task, users, bubbleHandlers),
      navigateItem: primaryMessageId
        ? { messageId: primaryMessageId, channelId: task.channelId || null, replyToMessageId: null }
        : null,
      removePayload: null,
    });
  }

  const sortedMapped = [...mappedTasks].sort(
    (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
  );

  sortedMapped.forEach((mappedTask) => {
    const mappedMessage = mappedTask.messageId ? taskMessagesByTaskId[mappedTask.id] : null;
    if (mappedTask.messageId && mappedMessage) {
      bubbles.push({
        key: `mapped-msg-${mappedTask.id}`,
        props: messageToBubbleProps(mappedMessage, bubbleHandlers),
        navigateItem: buildNavigateItemFromMessage(mappedMessage),
        removePayload: { parentTaskId: task.id, taskId: mappedTask.id, type: 'mapped' },
      });
    } else if (hasTaskDescription(mappedTask)) {
      bubbles.push({
        key: `mapped-task-${mappedTask.id}`,
        props: taskToBubbleProps(mappedTask, users, bubbleHandlers),
        navigateItem: mappedTask.messageId
          ? { messageId: mappedTask.messageId, channelId: null, replyToMessageId: null }
          : null,
        removePayload: { parentTaskId: task.id, taskId: mappedTask.id, type: 'mapped' },
      });
    }
  });

  const anyMappedMessageLoading = sortedMapped.some(
    (mt) => mt.messageId && loadingTaskMessageIds?.has(mt.id) && !taskMessagesByTaskId[mt.id],
  );

  const visibleBubbles = bubbles.filter((bubble) => !hiddenChatBubbleKeys.has(bubble.key));

  if (visibleBubbles.length === 0 && !loadingMappedTasks && !anyMappedMessageLoading) {
    return <p className="text-xs text-gray-500 dark:text-gray-400">No message content.</p>;
  }

  return (
    <div className="space-y-3">
      {visibleBubbles.map(({ key, props, navigateItem, removePayload }) => (
        <ChatMessageBubblePreview
          key={key}
          {...props}
          onClick={enableChatActions && navigateItem?.messageId
            ? (e) => {
              e.stopPropagation();
              onChatClick?.(navigateItem);
            }
            : undefined}
          onRemove={enableChatActions && removePayload && onRemoveChat
            ? (e) => {
              e.stopPropagation();
              onRemoveChat(removePayload, key);
            }
            : undefined}
          removing={removingChatKey === key}
        />
      ))}
      {(loadingMappedTasks || anyMappedMessageLoading) && (
        <p className="text-xs text-gray-500 dark:text-gray-400 py-0.5">Loading more messages…</p>
      )}
    </div>
  );
};

const REMINDERS_EXPANDED_STORAGE_KEY = 'dailyTasksPanel.remindersExpanded';
const TASKS_EXPANDED_STORAGE_KEY = 'dailyTasksPanel.tasksExpanded';
const WORKFLOWS_EXPANDED_STORAGE_KEY = 'dailyTasksPanel.workflowsExpanded';
const REMINDERS_VISIBLE_STORAGE_KEY = 'dailyTasksPanel.remindersVisible';
const TASKS_VISIBLE_STORAGE_KEY = 'dailyTasksPanel.tasksVisible';
const WORKFLOWS_VISIBLE_STORAGE_KEY = 'dailyTasksPanel.workflowsVisible';
const SHARED_PANELS_VISIBLE_STORAGE_KEY = 'dailyTasksPanel.sharedPanelsVisible';

const readExpandedPreference = (key, defaultValue = true) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved === null) return defaultValue;
    return saved === 'true';
  } catch (_) {
    return defaultValue;
  }
};

const getLocalTodayStr = () => new Date().toLocaleDateString('en-CA');

const notifyWorkflowsAssignedChanged = (detail) => {
  window.dispatchEvent(new CustomEvent('workloop:workflows-assigned-changed', { detail }));
};

const DailyTasksPanel = ({
  onClose,
  onNavigateToMessage,
  headerLeading = null,
  isExpanded = false,
  onToggleExpand,
  homeLayout = false,
  initialViewingUserId = null,
  onViewingUserChange,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { promptCompletionTime, completionTimeDialog } = useCompletionTimeDialog();
  const wideHome = homeLayout && (isExpanded || typeof onToggleExpand !== 'function');
  const currentUserId = useSelector((state) => state.auth.user?.id);
  const currentUserRole = useSelector((state) => state.auth.user?.role);
  const currentUserCanSeeSpaces = useSelector((state) => Boolean(state.auth.user?.canSeeSpaces));
  const todayScreenCache = useSelector((state) => state.tasks.todayScreen);
  const orgSpaces = useSelector((state) => state.spaces.list || []);
  const spacesFetchedOnce = useSelector((state) => Boolean(state.spaces.fetchedOnce));
  const canSeeSpacesNav = ['ADMIN', 'ORG_ADMIN', 'MANAGER'].includes(currentUserRole)
    || currentUserCanSeeSpaces;
  const { channels = [], directMessages = [] } = useSelector((state) => state.channels);
  const { users = [] } = useSelector((state) => state.users);
  const { messages = {} } = useSelector((state) => state.chat);
  const dailyAnalytics = useSelector((state) => state.tasks.dailyAnalytics);
  const chatMappingTarget = useSelector((state) => state.ui.chatMappingTarget);
  const lastChatMapping = useSelector((state) => state.ui.lastChatMapping);
  const pendingRemindersFocusAt = useSelector((state) => state.ui.pendingRemindersFocusAt);
  const reminderInvite = useSelector((state) => state.ui.reminderInvite);
  const sharedPanelsRestoreCount = useSelector((state) =>
    (state.panelAccess?.approvedAccess || []).filter((acc) => acc.status === 'APPROVED').length
  );
  // Fingerprint of workflow assignees in loaded chats — refreshes panel when anyone assigns/reassigns
  const workflowAssigneeFingerprint = useSelector((state) => {
    const byChannel = state.chat.messages || {};
    const parts = [];
    Object.keys(byChannel).forEach((channelId) => {
      (byChannel[channelId] || []).forEach((m) => {
        if (!m?.id) return;
        if (m.workflowAssignedToUserId) {
          parts.push(`${m.id}:${m.workflowAssignedToUserId}`);
        }
      });
    });
    return parts.sort().join('|');
  });
  const [viewingTargetUserId, setViewingTargetUserIdState] = useState(initialViewingUserId || null);
  const [selectedDay, setSelectedDay] = useState(() => getLocalTodayStr());
  const [workSessionDate, setWorkSessionDate] = useState(null);
  const [breakStatus, setBreakStatus] = useState(null);
  const [officeTick, setOfficeTick] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [dayOfficeMinutes, setDayOfficeMinutes] = useState(0);
  const [loginDays, setLoginDays] = useState(() => new Set());
  const calendarTodayStr = getLocalTodayStr();
  const todayStr = (!viewingTargetUserId && workSessionDate) ? workSessionDate : calendarTodayStr;
  const todayOwnerKey = todayScreenOwnerKey(currentUserId, viewingTargetUserId, todayStr);
  const analyticsOwnerKey = viewingTargetUserId || 'self';
  const spaceAnalyticsByKey = useSelector((state) => (
    state.spaces.analyticsByOwner[analyticsOwnerKey]?.byKey || {}
  ));
  const isSelectedToday = selectedDay === todayStr;
  const dateBarDays = useMemo(() => listRecentDates(calendarTodayStr, 14), [calendarTodayStr]);
  const dateBarRef = useRef(null);

  const setViewingTargetUserId = useCallback((userId) => {
    setViewingTargetUserIdState(userId || null);
    if (typeof onViewingUserChange === 'function') {
      onViewingUserChange(userId || null);
    }
  }, [onViewingUserChange]);

  useEffect(() => {
    if (initialViewingUserId) {
      setViewingTargetUserIdState(initialViewingUserId);
      setViewMode('today');
      setSelectedDay(getLocalTodayStr());
    }
  }, [initialViewingUserId]);

  useEffect(() => {
    if (viewingTargetUserId) {
      setWorkSessionDate(null);
      setBreakStatus(null);
      return undefined;
    }
    let cancelled = false;
    const applyStatus = (status) => {
      if (!status || cancelled) return;
      setBreakStatus(status);
      const sessionOpen = Boolean(status.checkedIn && !status.loggedOut && status.sessionDate);
      const nextDate = sessionOpen ? status.sessionDate : null;
      setWorkSessionDate(nextDate);
      if (sessionOpen && status.sessionDate) {
        setSelectedDay((prev) => {
          const calendarToday = new Date().toLocaleDateString('en-CA');
          if (!prev || prev === calendarToday || prev === status.sessionDate) {
            return status.sessionDate;
          }
          return prev;
        });
      }
    };
    const refresh = async () => {
      try {
        const status = await fetchBreakStatus();
        applyStatus(status);
      } catch {
        if (!cancelled) {
          setWorkSessionDate(null);
          setBreakStatus(null);
        }
      }
    };
    refresh();
    const unsubscribe = subscribeAttendanceSessionChanged((detail) => {
      if (detail?.status) applyStatus(detail.status);
      refresh();
    });
    const poll = window.setInterval(refresh, 30000);
    return () => {
      cancelled = true;
      unsubscribe();
      window.clearInterval(poll);
    };
  }, [viewingTargetUserId]);

  useEffect(() => {
    const running = Boolean(breakStatus?.workRunning) && !viewingTargetUserId && isSelectedToday;
    if (!running) return undefined;
    const id = window.setInterval(() => setOfficeTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [breakStatus?.workRunning, viewingTargetUserId, isSelectedToday]);

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 15000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    // Always load persisted working minutes for the selected day (needed after logout / 16h auto-close).
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchDayActivityTimeline(selectedDay, viewingTargetUserId || undefined);
        if (cancelled) return;
        const working = Number(data?.workingTimeMinutes);
        if (Number.isFinite(working) && working >= 0) {
          setDayOfficeMinutes(Math.floor(working));
          return;
        }
        const total = Number(data?.totalTimeMinutes) || 0;
        const breaks = Number(data?.totalBreakMinutes) || 0;
        setDayOfficeMinutes(Math.max(0, Math.floor(total - breaks)));
      } catch {
        if (!cancelled) setDayOfficeMinutes(0);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedDay, viewingTargetUserId, breakStatus?.loggedOut, breakStatus?.sessionDate]);

  useEffect(() => {
    let cancelled = false;
    const startDate = dateBarDays[0];
    const endDate = dateBarDays[dateBarDays.length - 1] || calendarTodayStr;
    if (!startDate) return undefined;
    const load = async () => {
      try {
        const dates = await fetchLoginDays(startDate, endDate, viewingTargetUserId || undefined);
        if (!cancelled) setLoginDays(new Set(dates));
      } catch {
        if (!cancelled) setLoginDays(new Set());
      }
    };
    load();
    const unsubscribe = subscribeAttendanceSessionChanged(() => {
      load();
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [dateBarDays, calendarTodayStr, viewingTargetUserId]);

  useEffect(() => {
    const node = dateBarRef.current;
    if (node) {
      node.scrollLeft = node.scrollWidth;
    }
  }, [dateBarDays]);
  const [reminders, setReminders] = useState([]);
  const [forMeReminders, setForMeReminders] = useState([]);
  const [viewMode, setViewMode] = useState('today'); // 'today' | 'all' | 'forMe' | 'spaces'
  const [selectedSpaceKey, setSelectedSpaceKey] = useState('all');
  const [spacesRailMinified, setSpacesRailMinified] = useState(() => {
    try {
      return localStorage.getItem('workloop.spacesRailMinified') !== 'false';
    } catch {
      return true;
    }
  });
  const [spaceBannerEditing, setSpaceBannerEditing] = useState(false);
  const [spaceBannerName, setSpaceBannerName] = useState('');
  const [spaceBannerIcon, setSpaceBannerIcon] = useState('');
  const [spaceBannerSaving, setSpaceBannerSaving] = useState(false);
  const [mySpaceIcon, setMySpaceIcon] = useState(() => {
    try {
      return localStorage.getItem('workloop.mySpaceIcon') || '';
    } catch {
      return '';
    }
  });
  const [mySpaceName, setMySpaceName] = useState(() => {
    try {
      return localStorage.getItem('workloop.mySpaceName') || '';
    } catch {
      return '';
    }
  });
  const isPastDayView = viewMode === 'today' && !isSelectedToday;
  const canUseWorkTimer = !viewingTargetUserId && !isPastDayView;
  const [loading, setLoading] = useState(true);
  const [forMeLoading, setForMeLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [cloneDraft, setCloneDraft] = useState(null);
  const [viewingReminder, setViewingReminder] = useState(null);
  const [showAllRemindersModal, setShowAllRemindersModal] = useState(false);
  const [allRemindersModalTab, setAllRemindersModalTab] = useState('all');
  const [allRemindersQuery, setAllRemindersQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [pickTodayTasks, setPickTodayTasks] = useState([]);
  const [optimisticCompletedTasks, setOptimisticCompletedTasks] = useState([]);
  const [optimisticProductivityEntries, setOptimisticProductivityEntries] = useState([]);
  const [optimisticCompletedWorkflows, setOptimisticCompletedWorkflows] = useState([]);
  const [pickTodayLoading, setPickTodayLoading] = useState(false);
  const [assignedWorkflows, setAssignedWorkflows] = useState([]);
  const [completedAssignedWorkflows, setCompletedAssignedWorkflows] = useState([]);
  const [assignedWorkflowsLoading, setAssignedWorkflowsLoading] = useState(false);

  useEffect(() => {
    if (todayScreenCache?.ownerKey !== todayOwnerKey) return;
    if (todayScreenCache.tasksHydrated) {
      setPickTodayTasks(Array.isArray(todayScreenCache.pickTodayTasks) ? todayScreenCache.pickTodayTasks : []);
    }
    if (todayScreenCache.workflowsHydrated) {
      setAssignedWorkflows(Array.isArray(todayScreenCache.assignedWorkflows) ? todayScreenCache.assignedWorkflows : []);
      setCompletedAssignedWorkflows(
        Array.isArray(todayScreenCache.completedAssignedWorkflows) ? todayScreenCache.completedAssignedWorkflows : []
      );
    }
  }, [todayOwnerKey]);
  const [viewingPickTodayTask, setViewingPickTodayTask] = useState(null);
  const [viewingWorkflow, setViewingWorkflow] = useState(null);
  const [magicPickOpen, setMagicPickOpen] = useState(false);
  const [workTimerTick, setWorkTimerTick] = useState(0);
  const [completingTaskId, setCompletingTaskId] = useState(null);
  const [completingWorkflowId, setCompletingWorkflowId] = useState(null);
  const [completingReminderId, setCompletingReminderId] = useState(null);
  const [skippingReminderId, setSkippingReminderId] = useState(null);
  const [togglingTaskKey, setTogglingTaskKey] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [showTaskCreateDialog, setShowTaskCreateDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [addingTask, setAddingTask] = useState(false);
  const [addingSubtaskForId, setAddingSubtaskForId] = useState(null);
  const [addingWorkflowTaskForId, setAddingWorkflowTaskForId] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [savingQuickTask, setSavingQuickTask] = useState(false);
  const [subtaskToDelete, setSubtaskToDelete] = useState(null);
  const [expandedTaskIds, setExpandedTaskIds] = useState(new Set());
  const [taskChatsVisibleIds, setTaskChatsVisibleIds] = useState(new Set());
  const [expandedWorkflowIds, setExpandedWorkflowIds] = useState(new Set());
  const [taskMessagesByTaskId, setTaskMessagesByTaskId] = useState({});
  const [loadingTaskMessageIds, setLoadingTaskMessageIds] = useState(new Set());
  const [mappedTasksByTaskId, setMappedTasksByTaskId] = useState({});
  const [loadingMappedTasksByTaskId, setLoadingMappedTasksByTaskId] = useState({});
  const [workflowMappedTasksByMessageId, setWorkflowMappedTasksByMessageId] = useState({});
  const [viewingWorkflowGallery, setViewingWorkflowGallery] = useState(null);
  const [removingChatKey, setRemovingChatKey] = useState(null);
  const [hiddenChatBubbleKeys, setHiddenChatBubbleKeys] = useState(() => new Set());
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'completed'
  const [showWorkflowTasks, setShowWorkflowTasks] = useState(true);
  const [showPersonalTasks, setShowPersonalTasks] = useState(true);
  const [taskSortOrder, setTaskSortOrder] = useState('newest');
  const [workflowSortOrder, setWorkflowSortOrder] = useState('newest');
  const [remindersExpanded, setRemindersExpanded] = useState(() =>
    readExpandedPreference(REMINDERS_EXPANDED_STORAGE_KEY, true),
  );
  const [tasksExpanded, setTasksExpanded] = useState(() =>
    readExpandedPreference(TASKS_EXPANDED_STORAGE_KEY, true),
  );
  const [workflowsExpanded, setWorkflowsExpanded] = useState(() =>
    readExpandedPreference(WORKFLOWS_EXPANDED_STORAGE_KEY, true),
  );
  const [remindersVisible, setRemindersVisible] = useState(() =>
    readExpandedPreference(REMINDERS_VISIBLE_STORAGE_KEY, true),
  );
  const [tasksVisible, setTasksVisible] = useState(() =>
    readExpandedPreference(TASKS_VISIBLE_STORAGE_KEY, true),
  );
  const [workflowsVisible, setWorkflowsVisible] = useState(() =>
    readExpandedPreference(WORKFLOWS_VISIBLE_STORAGE_KEY, true),
  );
  const [sharedPanelsVisible, setSharedPanelsVisible] = useState(() =>
    readExpandedPreference(SHARED_PANELS_VISIBLE_STORAGE_KEY, true),
  );
  /** null | { kind: 'type'|'status'|'source'|'track', value: string, label: string } */
  const [workflowChipFilter, setWorkflowChipFilter] = useState(null);
  const [workflowSourceTab, setWorkflowSourceTab] = useState('workloop');
  const [homeFocus, setHomeFocus] = useState(null);
  const [needToCallItems, setNeedToCallItems] = useState([]);
  const [needToCallDetail, setNeedToCallDetail] = useState(null);

  useEffect(() => {
    dispatch(markPanelEventsSeen());
  }, [dispatch]);

  useEffect(() => {
    const focusTodayTasks = () => {
      window.__workloopFocusTodayTasks = false;
      try {
        sessionStorage.removeItem('workloop.pendingTodayTasks');
      } catch {
        // ignore
      }
      setViewingTargetUserId(null);
      setSelectedDay(getLocalTodayStr());
      setViewMode('today');
      setActiveTab('pending');
      setHomeFocus('tasks');
      setTasksExpanded(true);
      setTasksVisible(true);
    };
    if (window.__workloopFocusTodayTasks) {
      focusTodayTasks();
    }
    window.addEventListener('workloop:focus-today-tasks', focusTodayTasks);
    return () => window.removeEventListener('workloop:focus-today-tasks', focusTodayTasks);
  }, []);

  useEffect(() => {
    if (!pendingRemindersFocusAt) return;
    if (Date.now() - pendingRemindersFocusAt > 8000) return;
    setViewingReminder(null);
    setShowModal(false);
    setViewingTargetUserId(null);
    setSelectedDay(getLocalTodayStr());
    setViewMode('today');
    setActiveTab('pending');
    setHomeFocus('reminders');
    setRemindersExpanded(true);
    setRemindersVisible(true);
  }, [pendingRemindersFocusAt]);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    const applyList = (list) => {
      if (!cancelled) setNeedToCallItems(Array.isArray(list) ? list : []);
    };
    fetchNeedToCall().then(applyList).catch(() => applyList([]));
    const refresh = (event) => {
      const immediate = event?.detail?.items;
      if (Array.isArray(immediate)) {
        applyList(immediate);
        return;
      }
      fetchNeedToCall().then(applyList).catch(() => {});
    };
    window.addEventListener('workloop:need-to-call-changed', refresh);
    return () => {
      cancelled = true;
      window.removeEventListener('workloop:need-to-call-changed', refresh);
    };
  }, []);

  useEffect(() => {
    if (!lastChatMapping?.parentTaskId) return;
    const parentId = lastChatMapping.parentTaskId;
    dispatch(fetchTasksByMapTaskId(parentId)).unwrap().then((result) => {
      const mapped = Array.isArray(result) ? result : [];
      setMappedTasksByTaskId((prev) => ({
        ...prev,
        [parentId]: mapped,
      }));
      if (lastChatMapping.type === 'workflow' && lastChatMapping.workflowMessageId) {
        setWorkflowMappedTasksByMessageId((prev) => {
          const existing = prev[lastChatMapping.workflowMessageId] || [];
          const linked = existing.filter(isWorkflowLinkedTask);
          return {
            ...prev,
            [lastChatMapping.workflowMessageId]: [...linked, ...mapped],
          };
        });
      }
    }).catch(() => {});
  }, [lastChatMapping, dispatch]);

  useEffect(() => {
    const loadWorkflowMapped = async () => {
      const messageIds = [
        ...Array.from(expandedWorkflowIds),
        viewingWorkflow?.messageId,
        ...(assignedWorkflows || []).map((item) => item.messageId),
      ].filter(Boolean);
      const uniqueIds = [...new Set(messageIds)].filter((id) => (
        id === viewingWorkflow?.messageId || !workflowMappedTasksByMessageId[id]
      ));
      if (uniqueIds.length === 0) return;

      for (const messageId of uniqueIds) {
        try {
          const existing = await dispatch(fetchTasks({ messageId })).unwrap();
          const list = Array.isArray(existing) ? existing : [];
          const linkedTasks = list.filter(isWorkflowLinkedTask);
          const parents = list.filter((task) => isWorkflowMappingParent(task, messageId) || isWorkflowShellTask(task));
          let mapped = [];
          for (const parent of parents) {
            if (!parent?.id) continue;
            try {
              const children = await dispatch(fetchTasksByMapTaskId(parent.id)).unwrap();
              mapped = mapped.concat(Array.isArray(children) ? children : []);
            } catch {
              // keep other parents
            }
          }
          setWorkflowMappedTasksByMessageId((prev) => ({
            ...prev,
            [messageId]: uniqueTasksById(prev[messageId], linkedTasks, mapped),
          }));
        } catch (err) {
          console.error(`Failed to load mapped chats for workflow ${messageId}:`, err);
          setWorkflowMappedTasksByMessageId((prev) => ({ ...prev, [messageId]: [] }));
        }
      }
    };

    loadWorkflowMapped();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedWorkflowIds, viewingWorkflow?.messageId, assignedWorkflows, dispatch, lastChatMapping]);

  const toggleTaskMapping = useCallback((task) => {
    if (chatMappingTarget?.type === 'task' && chatMappingTarget.id === task.id) {
      dispatch(clearChatMappingTarget());
      return;
    }
    dispatch(setChatMappingTarget({ type: 'task', id: task.id }));
  }, [chatMappingTarget, dispatch]);

  const toggleWorkflowMapping = useCallback((item) => {
    if (chatMappingTarget?.type === 'workflow' && chatMappingTarget.messageId === item.messageId) {
      dispatch(clearChatMappingTarget());
      return;
    }
    dispatch(setChatMappingTarget({
      type: 'workflow',
      messageId: item.messageId,
      contentHtml: item.contentHtml,
      content: item.content,
    }));
  }, [chatMappingTarget, dispatch]);

  const fetchReminders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reminderService.fetchReminders(viewingTargetUserId);
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => {
        const at = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bt = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bt - at;
      });
      setReminders(list);
    } catch (err) {
      if (!viewingTargetUserId) {
        try {
          const cached = await readRemindersFromRealm();
          if (cached?.length) {
            setReminders(cached);
            setError(null);
            return;
          }
        } catch {
          // ignore cache read
        }
      }
      setReminders((prev) => prev || []);
      setError(null);
    } finally {
      setLoading(false);
    }
  }, [dispatch, viewingTargetUserId]);

  const refreshProductivityAnalytics = useCallback(() => {
    dispatch(fetchDailyAnalytics({ date: selectedDay, targetUserId: viewingTargetUserId, force: true }));
  }, [dispatch, viewingTargetUserId, selectedDay]);

  const fetchForMeReminders = useCallback(async () => {
    try {
      setForMeLoading(true);
      const data = await reminderService.fetchRemindersForMe();
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => {
        const at = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bt = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bt - at;
      });
      setForMeReminders(list);
    } catch (err) {
      dispatch(addNotification({ type: 'error', message: err.response?.data?.error || err.message || 'Failed to load reminders for me' }));
    } finally {
      setForMeLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  useEffect(() => {
    const refreshReminders = () => {
      fetchReminders();
    };
    window.addEventListener('online', refreshReminders);
    window.addEventListener('workloop:reminders-flushed', refreshReminders);
    return () => {
      window.removeEventListener('online', refreshReminders);
      window.removeEventListener('workloop:reminders-flushed', refreshReminders);
    };
  }, [fetchReminders]);

  useEffect(() => {
    fetchForMeReminders();
  }, [fetchForMeReminders]);

  useEffect(() => {
    const refreshForMe = () => {
      fetchForMeReminders();
    };
    window.addEventListener('workloop:reminders-for-me-changed', refreshForMe);
    return () => window.removeEventListener('workloop:reminders-for-me-changed', refreshForMe);
  }, [fetchForMeReminders]);

  useEffect(() => {
    if (viewMode === 'forMe') {
      fetchForMeReminders();
    }
  }, [viewMode, fetchForMeReminders]);

  useEffect(() => {
    try {
      localStorage.setItem(REMINDERS_EXPANDED_STORAGE_KEY, String(remindersExpanded));
    } catch (_) {
      // Ignore storage write issues (e.g. privacy mode)
    }
  }, [remindersExpanded]);

  useEffect(() => {
    try {
      localStorage.setItem(TASKS_EXPANDED_STORAGE_KEY, String(tasksExpanded));
    } catch (_) {
      // Ignore storage write issues
    }
  }, [tasksExpanded]);

  useEffect(() => {
    try {
      localStorage.setItem(WORKFLOWS_EXPANDED_STORAGE_KEY, String(workflowsExpanded));
    } catch (_) {
      // Ignore storage write issues
    }
  }, [workflowsExpanded]);

  useEffect(() => {
    try {
      localStorage.setItem(REMINDERS_VISIBLE_STORAGE_KEY, String(remindersVisible));
    } catch (_) {
      // Ignore storage write issues
    }
  }, [remindersVisible]);

  useEffect(() => {
    try {
      localStorage.setItem(TASKS_VISIBLE_STORAGE_KEY, String(tasksVisible));
    } catch (_) {
      // Ignore storage write issues
    }
  }, [tasksVisible]);

  useEffect(() => {
    try {
      localStorage.setItem(WORKFLOWS_VISIBLE_STORAGE_KEY, String(workflowsVisible));
    } catch (_) {
      // Ignore storage write issues
    }
  }, [workflowsVisible]);

  useEffect(() => {
    try {
      localStorage.setItem(SHARED_PANELS_VISIBLE_STORAGE_KEY, String(sharedPanelsVisible));
    } catch (_) {
      // Ignore storage write issues
    }
  }, [sharedPanelsVisible]);

  const hideSection = useCallback((sectionId) => {
    if (sectionId === 'reminders') setRemindersVisible(false);
    if (sectionId === 'tasks') setTasksVisible(false);
    if (sectionId === 'workflows') setWorkflowsVisible(false);
    if (sectionId === 'sharedPanels') setSharedPanelsVisible(false);
  }, []);

  const showSection = useCallback((sectionId) => {
    if (sectionId === 'reminders') setRemindersVisible(true);
    if (sectionId === 'tasks') setTasksVisible(true);
    if (sectionId === 'workflows') setWorkflowsVisible(true);
    if (sectionId === 'sharedPanels') setSharedPanelsVisible(true);
  }, []);

  const fetchPickTodayTasks = useCallback(async (options = {}) => {
    const force = options?.force === true;
    try {
      if (selectedDay !== todayStr) {
        setPickTodayLoading(true);
        setPickTodayTasks([]);
        return;
      }
      if (!force) {
        const cache = store.getState().tasks.todayScreen;
        if (cache?.ownerKey === todayOwnerKey && cache.tasksHydrated) {
          setPickTodayTasks(Array.isArray(cache.pickTodayTasks) ? cache.pickTodayTasks : []);
          setPickTodayLoading(false);
          return;
        }
      }
      setPickTodayLoading(true);
      if (!force && !viewingTargetUserId && (await realmAvailable())) {
        const cached = await readTasksFromRealm();
        const merged = await overlayPendingTasks(cached || []);
        const pickToday = merged.filter(isTodayWorkTask);
        setPickTodayTasks(pickToday);
        dispatch(setTodayPickTasksCache({ ownerKey: todayOwnerKey, tasks: pickToday }));
        return;
      }
      const token = localStorage.getItem('token');
      const url = viewingTargetUserId 
        ? `${API_URL}/tasks?status=PICK_TODAY&targetUserId=${encodeURIComponent(viewingTargetUserId)}`
        : `${API_URL}/tasks?status=PICK_TODAY`;
      const response = await axios.get(url, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      const list = await overlayPendingTasks(Array.isArray(response.data) ? response.data : []);
      const pickToday = list.filter(isTodayWorkTask);
      setPickTodayTasks(pickToday);
      dispatch(setTodayPickTasksCache({ ownerKey: todayOwnerKey, tasks: pickToday }));
      if (!viewingTargetUserId) {
        upsertRealmDocs('Task', Array.isArray(response.data) ? response.data : []).catch(() => {});
      }
    } catch (err) {
      if (!viewingTargetUserId) {
        const cached = await readTasksFromRealm();
        const merged = await overlayPendingTasks(cached || []);
        const local = merged.filter(isTodayWorkTask);
        setPickTodayTasks(local);
        dispatch(setTodayPickTasksCache({ ownerKey: todayOwnerKey, tasks: local }));
        return;
      }
      setPickTodayTasks([]);
      const msg = err.response?.data?.error || err.message || 'Failed to load tasks';
      if (viewingTargetUserId && msg.includes('permission')) {
        setError(msg);
        dispatch(addNotification({ type: 'error', message: msg }));
      }
    } finally {
      setPickTodayLoading(false);
    }
  }, [viewingTargetUserId, dispatch, selectedDay, todayStr, todayOwnerKey]);

  const fetchAssignedWorkflows = useCallback(async (options = {}) => {
    const force = options?.force === true;
    const silent = options?.silent === true;
    const token = localStorage.getItem('token');
    const timezone = (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
      } catch {
        return 'Asia/Kolkata';
      }
    })();
    const completedParams = new URLSearchParams({
      completedToday: 'true',
      timezone,
      date: selectedDay,
    });
    if (viewingTargetUserId) completedParams.set('targetUserId', viewingTargetUserId);
    const fetchCompleted = () => axios.get(`${API_URL}/messages/workflows-assigned?${completedParams}`, {
      headers: { Authorization: token ? `Bearer ${token}` : '' },
    }).then((res) => (Array.isArray(res.data) ? res.data : [])).catch(() => []);

    const effectiveUserId = viewingTargetUserId || currentUserId;
    const filterOpen = (list) => (list || []).filter((item) => (
      item?.workflowByCategory?.['workflow-status'] !== WORKFLOW_CLOSED_STATUS_ID
    ));
    const withLoadedChats = (list) => mergeAssignedWorkflowItems(
      filterOpen(list),
      collectAssignedWorkflowsFromLoadedChats(effectiveUserId),
    );

    try {
      if (!silent) setAssignedWorkflowsLoading(true);
      if (selectedDay !== todayStr) {
        setAssignedWorkflows([]);
        setCompletedAssignedWorkflows(await fetchCompleted());
        return;
      }

      if (!force) {
        const cache = store.getState().tasks.todayScreen;
        if (cache?.ownerKey === todayOwnerKey && cache.workflowsHydrated) {
          const assigned = withLoadedChats(cache.assignedWorkflows);
          setAssignedWorkflows(assigned);
          setCompletedAssignedWorkflows(Array.isArray(cache.completedAssignedWorkflows)
            ? cache.completedAssignedWorkflows
            : []);
          setAssignedWorkflowsLoading(false);
          return;
        }
      }

      if (!viewingTargetUserId && (await realmAvailable())) {
        const cached = await readAssignedWorkflowsFromRealm();
        const local = withLoadedChats(cached);
        if (local.length) setAssignedWorkflows(local);
      } else {
        const local = collectAssignedWorkflowsFromLoadedChats(effectiveUserId);
        if (local.length) setAssignedWorkflows(local);
      }

      const pendingUrl = viewingTargetUserId
        ? `${API_URL}/messages/workflows-assigned?targetUserId=${encodeURIComponent(viewingTargetUserId)}`
        : `${API_URL}/messages/workflows-assigned`;
      const online = typeof navigator === 'undefined' || navigator.onLine !== false;
      if (online) {
        const response = await axios.get(pendingUrl, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        });
        const merged = withLoadedChats(Array.isArray(response.data) ? response.data : []);
        setAssignedWorkflows(merged);
        const completed = await fetchCompleted();
        setCompletedAssignedWorkflows(completed);
        dispatch(setTodayWorkflowsCache({
          ownerKey: todayOwnerKey,
          assignedWorkflows: merged,
          completedAssignedWorkflows: completed,
        }));
        if (!viewingTargetUserId) {
          saveAssignedWorkflowsToRealm(merged).catch(() => {});
        }
      } else if (!viewingTargetUserId) {
        const cached = await readAssignedWorkflowsFromRealm();
        setAssignedWorkflows(withLoadedChats(cached));
      }
    } catch (err) {
      if (!viewingTargetUserId) {
        const cached = await readAssignedWorkflowsFromRealm();
        const local = withLoadedChats(cached);
        if (local.length) {
          setAssignedWorkflows(local);
          setCompletedAssignedWorkflows(await fetchCompleted());
          setAssignedWorkflowsLoading(false);
          return;
        }
      }
      const local = collectAssignedWorkflowsFromLoadedChats(effectiveUserId);
      if (local.length) {
        setAssignedWorkflows(local);
        setCompletedAssignedWorkflows(await fetchCompleted());
        return;
      }
      setAssignedWorkflows([]);
      setCompletedAssignedWorkflows([]);
      const msg = err.response?.data?.error || err.message || 'Failed to load assigned workflows';
      if (viewingTargetUserId && String(msg).toLowerCase().includes('permission')) {
        setError(msg);
        dispatch(addNotification({ type: 'error', message: msg }));
      }
    } finally {
      setAssignedWorkflowsLoading(false);
    }
  }, [viewingTargetUserId, currentUserId, dispatch, selectedDay, todayStr, todayOwnerKey]);

  const toggleTaskExpansion = (taskId) => {
    setExpandedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
    setTaskChatsVisibleIds((prev) => {
      if (!prev.has(taskId)) return prev;
      const next = new Set(prev);
      next.delete(taskId);
      return next;
    });
  };

  const toggleTaskChats = (taskId) => {
    setTaskChatsVisibleIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const toggleWorkflowExpansion = (messageId) => {
    setExpandedWorkflowIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  const productivityEntriesToday = useMemo(() => {
    const fromApi = dailyAnalytics?.productivityEntries || [];
    const apiList = Array.isArray(fromApi) ? fromApi : [];
    const day = String(selectedDay || '').slice(0, 10);
    const byKey = new Map();
    const addEntry = (entry, { filterByDay = false } = {}) => {
      if (!entry) return;
      if (filterByDay) {
        const entryDay = String(entry.date || '').slice(0, 10);
        if (entryDay && day && entryDay !== day) return;
      }
      const key = entry.id
        || `${entry.taskId}-${entry.fromPercent}-${entry.toPercent}-${entry.createdAt}`;
      byKey.set(key, {
        ...entry,
        displayLabel: entry.displayLabel || formatProductivityEntryLabel(entry),
      });
    };
    optimisticProductivityEntries.forEach((entry) => addEntry(entry, { filterByDay: true }));
    apiList.forEach((entry) => addEntry(entry));
    return Array.from(byKey.values()).sort(
      (a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')),
    );
  }, [dailyAnalytics?.productivityEntries, optimisticProductivityEntries, selectedDay]);

  useEffect(() => {
    const onEntry = (event) => {
      const entry = event?.detail;
      if (!entry) return;
      setOptimisticProductivityEntries((prev) => [entry, ...prev.filter(
        (item) => item.id !== entry.id,
      )]);
    };
    const onEntryRemoved = (event) => {
      const taskId = event?.detail?.taskId;
      if (!taskId) return;
      setOptimisticProductivityEntries((prev) => prev.filter((item) => item.taskId !== taskId || Number(item.toPercent) < 100));
    };
    window.addEventListener('workloop:productivity-entry', onEntry);
    window.addEventListener('workloop:productivity-entry-removed', onEntryRemoved);
    return () => {
      window.removeEventListener('workloop:productivity-entry', onEntry);
      window.removeEventListener('workloop:productivity-entry-removed', onEntryRemoved);
    };
  }, []);

  useEffect(() => {
    if (!dailyAnalytics?.productivityEntries?.length) return;
    setOptimisticProductivityEntries((prev) => prev.filter((opt) => (
      !dailyAnalytics.productivityEntries.some((api) => (
        api.taskId === opt.taskId
        && Number(api.fromPercent) === Number(opt.fromPercent)
        && Number(api.toPercent) === Number(opt.toPercent)
      ))
    )));
  }, [dailyAnalytics?.productivityEntries]);

  const completedTodayTasks = useMemo(() => {
    const fromApi = dailyAnalytics?.completedTasks || [];
    const seen = new Set(fromApi.map((task) => task?.id).filter(Boolean));
    const overlayById = new Map(
      optimisticCompletedTasks.filter((task) => task?.id).map((task) => [task.id, task]),
    );
    const extras = optimisticCompletedTasks.filter((task) => task?.id && !seen.has(task.id));
    const mergedApi = fromApi.map((task) => {
      const overlay = overlayById.get(task.id);
      return overlay ? { ...task, ...overlay } : task;
    });
    return [...extras, ...mergedApi];
  }, [dailyAnalytics?.completedTasks, optimisticCompletedTasks]);

  const completedTodayReminders = useMemo(() => (
    reminders.filter((reminder) => reminderCompletedOnDate(reminder, selectedDay))
  ), [reminders, selectedDay]);

  const completedTodayWorkflows = useMemo(() => {
    const byId = new Map();
    (completedAssignedWorkflows || []).forEach((item) => {
      if (item?.messageId) byId.set(item.messageId, item);
    });
    (optimisticCompletedWorkflows || []).forEach((item) => {
      if (!item?.messageId) return;
      const existing = byId.get(item.messageId);
      if (!existing) {
        byId.set(item.messageId, item);
        return;
      }
      const existingMins = Number(existing.completionTimeInMinutes);
      const optimisticMins = Number(item.completionTimeInMinutes);
      byId.set(item.messageId, {
        ...existing,
        ...item,
        completionTimeInMinutes: (Number.isFinite(existingMins) && existingMins > 0)
          ? existingMins
          : (Number.isFinite(optimisticMins) && optimisticMins > 0 ? optimisticMins : existing.completionTimeInMinutes),
      });
    });
    return Array.from(byId.values());
  }, [completedAssignedWorkflows, optimisticCompletedWorkflows]);

  const completedWorkflowsCount = completedTodayWorkflows.length;

  const pendingAssignedWorkflows = useMemo(() => {
    const completedIds = new Set(
      completedTodayWorkflows.map((item) => item?.messageId).filter(Boolean)
    );
    return assignedWorkflows.filter((item) => !completedIds.has(item?.messageId));
  }, [assignedWorkflows, completedTodayWorkflows]);

  const pendingWorkflowsCount = pendingAssignedWorkflows.length;

  const knownWorkflowIds = useMemo(() => {
    const ids = new Set();
    const add = (item) => {
      const id = item?.messageId;
      if (id) ids.add(String(id));
    };
    (assignedWorkflows || []).forEach(add);
    (completedAssignedWorkflows || []).forEach(add);
    (pendingAssignedWorkflows || []).forEach(add);
    (completedTodayWorkflows || []).forEach(add);
    Object.keys(workflowMappedTasksByMessageId || {}).forEach((id) => ids.add(String(id)));
    return ids;
  }, [
    assignedWorkflows,
    completedAssignedWorkflows,
    pendingAssignedWorkflows,
    completedTodayWorkflows,
    workflowMappedTasksByMessageId,
  ]);

  const todayWorkflowSpaces = useMemo(() => workflowSpaceIndex([
    ...(assignedWorkflows || []),
    ...(completedAssignedWorkflows || []),
    ...(completedTodayWorkflows || []),
  ]), [assignedWorkflows, completedAssignedWorkflows, completedTodayWorkflows]);

  const tasksForSelectedDay = useMemo(() => {
    if (!isPastDayView) return pickTodayTasks;
    const picked = dailyAnalytics?.pickedTasks;
    return Array.isArray(picked) ? picked : [];
  }, [isPastDayView, pickTodayTasks, dailyAnalytics?.pickedTasks]);

  const completedTaskIdsForDay = useMemo(() => new Set(
    completedTodayTasks.map((task) => task?.id).filter(Boolean).map(String),
  ), [completedTodayTasks]);

  const pendingTasksForSelectedDay = useMemo(() => {
    if (!isPastDayView) return pickTodayTasks;
    return tasksForSelectedDay.filter((task) => {
      if (!task?.id) return false;
      if (completedTaskIdsForDay.has(String(task.id))) return false;
      const status = String(task?.status || task?.statusString || '').toUpperCase();
      if (status === 'COMPLETED') return false;
      return resolveProgressPercent(task) < 100;
    });
  }, [isPastDayView, pickTodayTasks, tasksForSelectedDay, completedTaskIdsForDay]);

  useEffect(() => {
    ensureWorkTimers();
    return subscribeWorkTimers(() => setWorkTimerTick((value) => value + 1));
  }, []);

  const displayedPickTodayTasks = useMemo(() => {
    const source = isPastDayView ? pendingTasksForSelectedDay : pickTodayTasks;
    const filtered = source.filter((task) => {
      const isWorkflow = taskShowsWorkflowTag(task, knownWorkflowIds);
      if (isWorkflow && !showWorkflowTasks) return false;
      if (!isWorkflow && !showPersonalTasks) return false;
      return true;
    });
    return [...filtered].sort((a, b) => {
      const urgentDiff = compareUrgentFirst(a, b, { isUrgent: isOpenUrgentTask });
      if (urgentDiff !== 0) return urgentDiff;
      const timerDiff = compareWorkTimerPriority('TASK', a.id, 'TASK', b.id);
      if (timerDiff !== 0) return timerDiff;
      const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return taskSortOrder === 'oldest' ? dateA - dateB : dateB - dateA;
    }).filter((task) => (
      viewMode !== 'spaces' || selectedSpaceKey === 'all'
      || resolveTaskSpace(task, todayWorkflowSpaces).key === selectedSpaceKey
    ));
  }, [isPastDayView, pendingTasksForSelectedDay, pickTodayTasks, knownWorkflowIds, showWorkflowTasks, showPersonalTasks, taskSortOrder, workTimerTick, viewMode, selectedSpaceKey, todayWorkflowSpaces]);

  const displayedCompletedTodayTasks = useMemo(() => {
    const filtered = completedTodayTasks.filter((task) => {
      const isWorkflow = taskShowsWorkflowTag(task, knownWorkflowIds);
      if (isWorkflow && !showWorkflowTasks) return false;
      if (!isWorkflow && !showPersonalTasks) return false;
      return true;
    });
    return [...filtered].sort((a, b) => {
      const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return taskSortOrder === 'oldest' ? dateA - dateB : dateB - dateA;
    }).filter((task) => (
      viewMode !== 'spaces' || selectedSpaceKey === 'all'
      || resolveTaskSpace(task, todayWorkflowSpaces).key === selectedSpaceKey
    ));
  }, [completedTodayTasks, knownWorkflowIds, showWorkflowTasks, showPersonalTasks, taskSortOrder, viewMode, selectedSpaceKey, todayWorkflowSpaces]);

  const magicPickItems = useMemo(() => {
    const taskItems = (displayedPickTodayTasks || []).map((task) => {
      const title = getTaskListLabel(task);
      const plainDesc = stripHtmlForTextExtraction(task?.description || '').trim();
      const subtitle = plainDesc && plainDesc !== title ? plainDesc.slice(0, 280) : '';
      const estimate = formatEstimateTime(task?.estimateTimeInMinutes || task?.completionTimeInMinutes);
      return {
        id: `task:${task.id}`,
        kind: 'task',
        title,
        subtitle,
        meta: estimate || undefined,
        raw: task,
      };
    });
    const workflowItems = (pendingAssignedWorkflows || []).filter((item) => (
      viewMode !== 'spaces' || selectedSpaceKey === 'all' || resolveItemSpace(item).key === selectedSpaceKey
    )).map((item) => {
      const title = item.workflowTitle || item.label || getWorkflowTicketText(item.content);
      const subtitle = stripHtmlForTextExtraction(
        item.workflowDescription || item.content || '',
      ).trim().slice(0, 280);
      const typeId = item.workflowByCategory?.['work-item-type'];
      const typeLabel = typeId ? getReactionDisplay(typeId)?.label : null;
      return {
        id: `workflow:${item.messageId}`,
        kind: 'workflow',
        title,
        subtitle: subtitle && subtitle !== title ? subtitle : '',
        meta: typeLabel || undefined,
        raw: item,
      };
    });
    return [...taskItems, ...workflowItems];
  }, [displayedPickTodayTasks, pendingAssignedWorkflows, viewMode, selectedSpaceKey]);

  const openMagicPickItem = useCallback((item) => {
    if (!item?.raw) return;
    if (item.kind === 'workflow') {
      setViewingWorkflow(item.raw);
      return;
    }
    setViewingPickTodayTask(item.raw);
  }, []);

  const refreshTodayLists = useCallback(async () => {
    await Promise.all([
      fetchPickTodayTasks({ force: true }),
      fetchAssignedWorkflows(),
    ]);
  }, [fetchPickTodayTasks, fetchAssignedWorkflows]);

  const todayListsRefreshing = pickTodayLoading || assignedWorkflowsLoading;

  const refreshTodayButton = () => (
    <span className="relative inline-flex flex-shrink-0">
      <button
        type="button"
        onClick={refreshTodayLists}
        disabled={todayListsRefreshing}
        className={`peer ${sectionFocusButtonClass} text-gray-600 dark:text-gray-300 disabled:opacity-50`}
        aria-label="Refresh tasks and workflows"
        title="Refresh tasks and workflows"
      >
        <RefreshIcon className={`w-3.5 h-3.5 ${todayListsRefreshing ? 'animate-spin' : ''}`} />
      </button>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-0 bottom-full z-[100] mb-1.5 whitespace-nowrap rounded-md border border-gray-800/20 bg-gray-900 px-2 py-1 text-[10px] font-medium text-white shadow-lg opacity-0 transition-opacity duration-150 peer-hover:opacity-100 peer-focus-visible:opacity-100 peer-disabled:opacity-0 dark:border-gray-600 dark:bg-gray-700"
      >
        Refresh tasks and workflows
      </span>
    </span>
  );

  const magicPickButton = (disabled = false) => (
    <span className="relative inline-flex flex-shrink-0">
      <button
        type="button"
        onClick={() => setMagicPickOpen(true)}
        disabled={disabled || magicPickItems.length === 0}
        className={`peer ${sectionFocusButtonClass} text-violet-600 dark:text-violet-400 disabled:opacity-40 disabled:pointer-events-none`}
        aria-label="Pick a task for me"
        title="Pick a task for me"
      >
        <MagicWandIcon className="w-3.5 h-3.5" />
      </button>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-0 bottom-full z-[100] mb-1.5 whitespace-nowrap rounded-md border border-gray-800/20 bg-gray-900 px-2 py-1 text-[10px] font-medium text-white shadow-lg opacity-0 transition-opacity duration-150 peer-hover:opacity-100 peer-focus-visible:opacity-100 peer-disabled:opacity-0 dark:border-gray-600 dark:bg-gray-700"
      >
        Pick a task for me
      </span>
    </span>
  );

  const displayedProductivityEntries = useMemo(() => (
    productivityEntriesToday
      .filter(isDisplayableProductivityEntry)
      .sort((a, b) => {
      const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return taskSortOrder === 'oldest' ? dateA - dateB : dateB - dateA;
    })
  ), [productivityEntriesToday, taskSortOrder]);

  const displayedCompletedProductivityEntries = useMemo(() => (
    filterProductivityEntriesForCompletedList(displayedProductivityEntries, displayedCompletedTodayTasks)
  ), [displayedProductivityEntries, displayedCompletedTodayTasks]);

  const showProductivityEntryList = displayedCompletedProductivityEntries.length > 0;

  const completedListItemCount = displayedCompletedProductivityEntries.length
    + displayedCompletedTodayTasks.length;

  const uniqueTasksDoneCount = useMemo(() => countUniqueTasksWithProgress(
    completedTodayTasks,
    productivityEntriesToday,
  ), [completedTodayTasks, productivityEntriesToday]);

  const completedTasksCount = completedListItemCount;

  const taskKindFilterEmptyMessage = !showWorkflowTasks && !showPersonalTasks
    ? 'Turn on Workflow task or Personal to see tasks.'
    : showWorkflowTasks && !showPersonalTasks
      ? 'No workflow tasks match these filters.'
      : !showWorkflowTasks && showPersonalTasks
        ? 'No personal tasks match these filters.'
        : null;

  const taskKindSortBar = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3.5 py-2 bg-white dark:bg-gray-800">
      <label className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
        <input
          type="checkbox"
          checked={showWorkflowTasks}
          onChange={(event) => setShowWorkflowTasks(event.target.checked)}
          className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
        />
        Workflow
      </label>
      <label className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
        <input
          type="checkbox"
          checked={showPersonalTasks}
          onChange={(event) => setShowPersonalTasks(event.target.checked)}
          className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
        />
        Personal
      </label>
      <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-700 dark:text-gray-300">
        <span className="whitespace-nowrap">Sort</span>
        <select
          value={taskSortOrder}
          onChange={(event) => setTaskSortOrder(event.target.value)}
          className="px-2 py-1 text-[11px] rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
      </label>
    </div>
  );

  const expandableTasks = useMemo(() => {
    const tasks = isPastDayView ? [...tasksForSelectedDay] : [...pickTodayTasks];
    if (completedTodayTasks.length) {
      completedTodayTasks.forEach((task) => {
        if (task?.id && !tasks.some((item) => item?.id === task.id)) {
          tasks.push(task);
        }
      });
    }
    return tasks;
  }, [isPastDayView, tasksForSelectedDay, pickTodayTasks, completedTodayTasks]);

  const fetchMessageById = useCallback(async (messageId) => {
    if (!messageId) return null;
    const local = await resolveLocalMessage(messageId, store.getState().chat?.messages);
    if (local) return local;
    if (await realmAvailable()) return null;

    const token = localStorage.getItem('token');
    let message = null;

    let allChannels = [...channels, ...directMessages];
    if (allChannels.length === 0) {
      const [channelsResult, directMessagesResult] = await Promise.all([
        dispatch(fetchChannels()),
        dispatch(fetchDirectMessages()),
      ]);
      const fetchedChannels = channelsResult.payload || [];
      const fetchedDirectMessages = directMessagesResult.payload || [];
      allChannels = [...fetchedChannels, ...fetchedDirectMessages];
      if (allChannels.length === 0) {
        allChannels = [...channels, ...directMessages];
      }
    }

    for (const channel of allChannels) {
      try {
        const response = await axios.get(
          `${API_URL}/messages/channel/${channel.id}`,
          {
            params: { page: 0, size: 100 },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const messages = Array.isArray(response.data) ? response.data : (response.data.content || []);
        const foundMessage = messages.find((m) => m.id === messageId);
        if (foundMessage) {
          message = foundMessage;
          break;
        }
      } catch (err) {
        console.warn(`Failed to fetch messages for channel ${channel.id}:`, err);
      }
    }

    return message;
  }, [channels, directMessages, dispatch]);

  useEffect(() => {
    const fetchMessagesForExpandedTasks = async () => {
      const tasksToFetch = [];

      expandableTasks.forEach((task) => {
        if (!expandedTaskIds.has(task.id)) return;

        const messageId = task.messageId?.trim?.() || task.messageId;
        if (messageId
          && !taskMessagesByTaskId[task.id]
          && !loadingTaskMessageIds.has(task.id)) {
          tasksToFetch.push(task);
        }

        const mapped = mappedTasksByTaskId[task.id] || [];
        mapped.forEach((mappedTask) => {
          const mappedMessageId = mappedTask.messageId?.trim?.() || mappedTask.messageId;
          if (mappedMessageId
            && !taskMessagesByTaskId[mappedTask.id]
            && !loadingTaskMessageIds.has(mappedTask.id)) {
            tasksToFetch.push(mappedTask);
          }
        });
      });

      if (tasksToFetch.length === 0) return;

      for (const task of tasksToFetch) {
        setLoadingTaskMessageIds((prev) => new Set(prev).add(task.id));
        try {
          const message = await fetchMessageById(task.messageId);
          if (message) {
            setTaskMessagesByTaskId((prev) => ({ ...prev, [task.id]: message }));
          }
        } catch (err) {
          console.error(`Failed to fetch message for task ${task.id}:`, err);
        } finally {
          setLoadingTaskMessageIds((prev) => {
            const next = new Set(prev);
            next.delete(task.id);
            return next;
          });
        }
      }
    };

    fetchMessagesForExpandedTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedTaskIds, expandableTasks, mappedTasksByTaskId, fetchMessageById]);

  useEffect(() => {
    const fetchMessagesForExpandedWorkflows = async () => {
      const tasksToFetch = [];

      assignedWorkflows.forEach((item) => {
        if (!expandedWorkflowIds.has(item.messageId)
          && viewingWorkflow?.messageId !== item.messageId) return;
        const mapped = workflowMappedTasksByMessageId[item.messageId] || [];
        mapped.forEach((mappedTask) => {
          const mappedMessageId = mappedTask.messageId?.trim?.() || mappedTask.messageId;
          if (mappedMessageId
            && !taskMessagesByTaskId[mappedTask.id]
            && !loadingTaskMessageIds.has(mappedTask.id)) {
            tasksToFetch.push(mappedTask);
          }
        });
      });

      if (tasksToFetch.length === 0) return;

      for (const task of tasksToFetch) {
        setLoadingTaskMessageIds((prev) => new Set(prev).add(task.id));
        try {
          const message = await fetchMessageById(task.messageId);
          if (message) {
            setTaskMessagesByTaskId((prev) => ({ ...prev, [task.id]: message }));
          }
        } catch (err) {
          console.error(`Failed to fetch message for workflow mapped task ${task.id}:`, err);
        } finally {
          setLoadingTaskMessageIds((prev) => {
            const next = new Set(prev);
            next.delete(task.id);
            return next;
          });
        }
      }
    };

    fetchMessagesForExpandedWorkflows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedWorkflowIds, viewingWorkflow?.messageId, assignedWorkflows, workflowMappedTasksByMessageId, fetchMessageById]);

  useEffect(() => {
    const fetchMappedTasksForExpanded = async () => {
      const parentTaskIds = Array.from(new Set([
        ...Array.from(expandedTaskIds),
        ...pickTodayTasks.map((task) => task.id),
        ...completedTodayTasks.map((task) => task.id),
      ])).filter(
        (taskId) => taskId && mappedTasksByTaskId[taskId] === undefined && !loadingMappedTasksByTaskId[taskId],
      );

      if (parentTaskIds.length === 0) return;

      for (const taskId of parentTaskIds) {
        setLoadingMappedTasksByTaskId((prev) => ({ ...prev, [taskId]: true }));
        try {
          const result = await dispatch(fetchTasksByMapTaskId(taskId)).unwrap();
          setMappedTasksByTaskId((prev) => ({
            ...prev,
            [taskId]: Array.isArray(result) ? result : [],
          }));
        } catch (err) {
          console.error(`Failed to fetch mapped tasks for ${taskId}:`, err);
          setMappedTasksByTaskId((prev) => ({ ...prev, [taskId]: [] }));
        } finally {
          setLoadingMappedTasksByTaskId((prev) => ({ ...prev, [taskId]: false }));
        }
      }
    };

    fetchMappedTasksForExpanded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedTaskIds, pickTodayTasks, completedTodayTasks, dispatch]);

  useEffect(() => {
    if (viewMode === 'today' || viewMode === 'spaces') {
      fetchPickTodayTasks();
      fetchAssignedWorkflows();
      dispatch(fetchDailyAnalytics({ date: selectedDay, targetUserId: viewingTargetUserId }));
    } else {
      setWorkflowChipFilter(null);
    }
  }, [viewMode, fetchPickTodayTasks, fetchAssignedWorkflows, dispatch, viewingTargetUserId, selectedDay]);

  // Instantly refresh "Workflows Assigned" when assignee changes in any loaded chat (self or peer via WS)
  useEffect(() => {
    if (viewMode !== 'today' && viewMode !== 'spaces') return undefined;
    const timer = setTimeout(() => {
      const effectiveUserId = viewingTargetUserId || currentUserId;
      const filterOpen = (list) => (list || []).filter((item) => (
        item?.workflowByCategory?.['workflow-status'] !== WORKFLOW_CLOSED_STATUS_ID
      ));
      setAssignedWorkflows((prev) => mergeAssignedWorkflowItems(
        filterOpen(prev),
        collectAssignedWorkflowsFromLoadedChats(effectiveUserId),
      ));
    }, 150);
    return () => clearTimeout(timer);
  }, [workflowAssigneeFingerprint, viewMode, viewingTargetUserId, currentUserId]);

  useEffect(() => {
    const onWorkflowsAssignedChanged = (event) => {
      if (viewMode !== 'today' && viewMode !== 'spaces') return;

      const messageId = event?.detail?.messageId;
      const action = event?.detail?.action;

      if (messageId && action === 'reopened') {
        setOptimisticCompletedWorkflows((prev) => prev.filter((workflow) => workflow.messageId !== messageId));
        setCompletedAssignedWorkflows((prev) => prev.filter((workflow) => workflow.messageId !== messageId));
        fetchAssignedWorkflows({ silent: true });
        refreshProductivityAnalytics();
        return;
      }

      if (messageId && action === 'completed') {
        const completionTimeInMinutes = Number(event?.detail?.completionTimeInMinutes);
        setAssignedWorkflows((prev) => {
          const item = prev.find((workflow) => workflow.messageId === messageId);
          const snapshot = item
            ? {
              ...item,
              messageId,
              label: item.workflowTitle || getWorkflowTicketText(item.content),
              content: item.content,
              completedAt: new Date().toISOString(),
              completionTimeInMinutes: Number.isFinite(completionTimeInMinutes) && completionTimeInMinutes > 0
                ? completionTimeInMinutes
                : item.completionTimeInMinutes,
              workflowByCategory: {
                ...(item.workflowByCategory || {}),
                'workflow-status': WORKFLOW_CLOSED_STATUS_ID,
              },
              reactions: applyOptimisticReaction(
                item.reactions || [],
                WORKFLOW_CLOSED_STATUS_ID,
                currentUserId,
              ),
            }
            : {
              messageId,
              completedAt: new Date().toISOString(),
              completionTimeInMinutes: Number.isFinite(completionTimeInMinutes) && completionTimeInMinutes > 0
                ? completionTimeInMinutes
                : undefined,
              workflowByCategory: { 'workflow-status': WORKFLOW_CLOSED_STATUS_ID },
              reactions: [{ emoji: WORKFLOW_CLOSED_STATUS_ID, userIds: currentUserId ? [currentUserId] : [] }],
            };
          queueMicrotask(() => {
            setOptimisticCompletedWorkflows((current) => {
              const existing = current.find((workflow) => workflow.messageId === messageId);
              if (existing) {
                return current.map((workflow) => (
                  workflow.messageId === messageId
                    ? { ...workflow, ...snapshot, completionTimeInMinutes: snapshot.completionTimeInMinutes || workflow.completionTimeInMinutes }
                    : workflow
                ));
              }
              return [snapshot, ...current];
            });
          });
          return prev.filter((workflow) => workflow.messageId !== messageId);
        });
        setExpandedWorkflowIds((prev) => {
          const next = new Set(prev);
          next.delete(messageId);
          return next;
        });
        window.setTimeout(() => {
          refreshProductivityAnalytics();
          fetchAssignedWorkflows({ silent: true });
        }, 400);
        return;
      }

      const effectiveUserId = viewingTargetUserId || currentUserId;
      setAssignedWorkflows((prev) => mergeAssignedWorkflowItems(
        (prev || []).filter((item) => item?.workflowByCategory?.['workflow-status'] !== WORKFLOW_CLOSED_STATUS_ID),
        collectAssignedWorkflowsFromLoadedChats(effectiveUserId),
      ));
      refreshProductivityAnalytics();
    };
    window.addEventListener('workloop:workflows-assigned-changed', onWorkflowsAssignedChanged);
    return () => window.removeEventListener('workloop:workflows-assigned-changed', onWorkflowsAssignedChanged);
  }, [viewMode, fetchAssignedWorkflows, refreshProductivityAnalytics]);

  const resolveParentCompletionMinutes = async (task) => {
    const subtasks = (mappedTasksByTaskId[task.id] || []).filter(isNamedSubtask);
    if (subtasks.length > 0) {
      const error = unfinishedSubtasksError(subtasks);
      if (error) {
        dispatch(addNotification({
          type: 'error',
          message: error,
        }));
        return null;
      }
    }
    const subtaskSum = subtasks.length > 0 ? sumSubtaskActualMinutes(subtasks) : 0;
    return promptCompletionTime({
      kind: 'task',
      targetType: 'TASK',
      targetId: task.id,
      initialMinutes: subtaskSum > 0 ? subtaskSum : undefined,
    });
  };

  const handleCompletePickTodayTask = async (e, task) => {
    e.stopPropagation();
    const minutes = await resolveParentCompletionMinutes(task);
    if (minutes == null) return;
    let succeeded = false;
    try {
      setCompletingTaskId(task.id);
      await dispatch(markTaskAsCompleted({
        ...task,
        completionTimeInMinutes: minutes,
        productivityDate: selectedDay,
      })).unwrap();
      const completedTask = {
        ...task,
        status: TaskStatus.COMPLETED,
        completionPercentage: 100,
        completionTimeInMinutes: minutes,
        completed: task.completed || new Date().toISOString(),
      };
      setPickTodayTasks((prev) => prev.filter((item) => item.id !== task.id));
      setOptimisticCompletedTasks((prev) => [completedTask, ...prev.filter((item) => item.id !== task.id)]);
      if (task.messageId) {
        setWorkflowMappedTasksByMessageId((prev) => ({
          ...prev,
          [task.messageId]: (prev[task.messageId] || []).map((item) => (
            item.id === task.id ? { ...item, ...completedTask } : item
          )),
        }));
      }
      setViewingPickTodayTask((prev) => (prev?.id === task.id ? { ...prev, ...completedTask } : prev));
      setActiveTab('completed');
      dispatch(addNotification({ type: 'success', message: 'Task completed' }));
      succeeded = true;
      await new Promise((resolve) => window.setTimeout(resolve, 1100));
    } catch (err) {
      const msg = apiErrorMessage(err, 'Failed to complete task');
      dispatch(addNotification({ type: 'error', message: msg }));
    } finally {
      setCompletingTaskId(null);
    }
    if (succeeded) {
      await new Promise((resolve) => window.setTimeout(resolve, 520));
      setViewingPickTodayTask((prev) => (prev?.id === task.id ? null : prev));
      await fetchPickTodayTasks();
      await dispatch(fetchDailyAnalytics({ date: selectedDay, targetUserId: viewingTargetUserId }));
    }
  };

  const handleUndoCompleteTask = async (e, task) => {
    e?.stopPropagation?.();
    if (!task?.id || viewingTargetUserId) return;
    try {
      setCompletingTaskId(task.id);
      const updated = await dispatch(undoTaskCompletion(task.id)).unwrap();
      const restored = {
        ...task,
        ...updated,
        status: updated?.status || TaskStatus.PICK_TODAY,
        completionPercentage: updated?.completionPercentage ?? 0,
        completionTimeInMinutes: null,
        completed: null,
      };
      setOptimisticCompletedTasks((prev) => prev.filter((item) => item.id !== task.id));
      setPickTodayTasks((prev) => [restored, ...prev.filter((item) => item.id !== task.id)]);
      if (viewingPickTodayTask?.id === task.id) {
        setViewingPickTodayTask(null);
      }
      dispatch(addNotification({ type: 'success', message: 'Task completion undone' }));
      await fetchPickTodayTasks();
      await dispatch(fetchDailyAnalytics({ date: selectedDay, targetUserId: viewingTargetUserId }));
    } catch (err) {
      const msg = apiErrorMessage(err, 'Failed to undo task completion');
      dispatch(addNotification({ type: 'error', message: msg }));
    } finally {
      setCompletingTaskId(null);
    }
  };

  const handleProgressPickTodayTask = async (task, completionPercentage) => {
    try {
      const completingNow = completionPercentage >= 100
        && (task?.status || task?.statusString) !== TaskStatus.COMPLETED;
      let completionTimeInMinutes;
      if (completingNow) {
        const minutes = await resolveParentCompletionMinutes(task);
        if (minutes == null) return;
        completionTimeInMinutes = minutes;
      }
      setCompletingTaskId(task.id);
      const updated = await dispatch(
        updateTaskProgress({
          taskId: task.id,
          completionPercentage,
          task,
          completionTimeInMinutes,
          productivityDate: selectedDay,
        })
      ).unwrap();
      if (viewingPickTodayTask?.id === task.id) {
        setViewingPickTodayTask(updated);
      }
      if ((updated?.status || updated?.statusString) === TaskStatus.COMPLETED || completionPercentage >= 100) {
        const completedTask = {
          ...(updated || task),
          status: TaskStatus.COMPLETED,
          completionPercentage: 100,
          completionTimeInMinutes: completionTimeInMinutes
            || (updated || task).completionTimeInMinutes,
          completed: (updated || task).completed || new Date().toISOString(),
        };
        setPickTodayTasks((prev) => prev.filter((item) => item.id !== task.id));
        setOptimisticCompletedTasks((prev) => [completedTask, ...prev.filter((item) => item.id !== task.id)]);
      } else {
        setPickTodayTasks((prev) => prev.map((item) => (item.id === task.id ? { ...item, ...updated } : item)));
      }
      if (completionPercentage > 0) {
        setActiveTab('completed');
      }
      dispatch(
        addNotification({
          type: 'success',
          message:
            completionPercentage >= 100
              ? 'Task completed'
              : `Progress updated to ${completionPercentage}%`,
        })
      );
      await fetchPickTodayTasks();
      await dispatch(fetchDailyAnalytics({ date: selectedDay, targetUserId: viewingTargetUserId }));
    } catch (err) {
      const msg = err?.error || err?.message || 'Failed to update task progress';
      dispatch(addNotification({ type: 'error', message: msg }));
    } finally {
      setCompletingTaskId(null);
    }
  };

  const handleUndoCompleteWorkflow = async (e, item) => {
    e?.stopPropagation?.();
    if (!item?.messageId || !item?.channelId || !currentUserId || viewingTargetUserId) return;
    const { messageId } = item;
    try {
      setCompletingWorkflowId(messageId);
      setOptimisticCompletedWorkflows((prev) => prev.filter((workflow) => workflow.messageId !== messageId));
      await dispatch(addReaction({
        messageId,
        emoji: WORKFLOW_CLOSED_STATUS_ID,
        channelId: item.channelId,
        userId: currentUserId,
      })).unwrap();
      notifyWorkflowsAssignedChanged({
        messageId,
        action: 'reopened',
      });
      setViewingWorkflow((prev) => (prev?.messageId === messageId ? null : prev));
      dispatch(addNotification({ type: 'success', message: 'Workflow completion undone' }));
      refreshProductivityAnalytics();
      fetchAssignedWorkflows({ silent: true });
    } catch (err) {
      fetchAssignedWorkflows();
      const msg = apiErrorMessage(err, 'Failed to undo workflow completion');
      dispatch(addNotification({ type: 'error', message: msg }));
    } finally {
      setCompletingWorkflowId(null);
    }
  };

  const handleCompleteSubtask = async (parent, subtask) => {
    if (!parent?.id || !subtask?.id) return;
    const minutes = await promptCompletionTime({
      kind: 'subtask',
      targetType: 'TASK',
      targetId: subtask.id,
    });
    if (minutes == null) return;
    setSavingQuickTask(true);
    try {
      const updated = await dispatch(updateTask({
        taskId: subtask.id,
        taskData: { completionTimeInMinutes: minutes },
      })).unwrap();
      setMappedTasksByTaskId((prev) => ({
        ...prev,
        [parent.id]: (prev[parent.id] || []).map((item) => (
          item.id === subtask.id
            ? { ...item, ...(updated || {}), completionTimeInMinutes: minutes }
            : item
        )),
      }));
      dispatch(addNotification({ type: 'success', message: 'Subtask finished' }));
      refreshProductivityAnalytics();
    } catch (err) {
      const msg = err?.error || err?.message || err || 'Failed to finish subtask';
      dispatch(addNotification({
        type: 'error',
        message: typeof msg === 'string' ? msg : 'Failed to finish subtask',
      }));
    } finally {
      setSavingQuickTask(false);
    }
  };

  const applyCompletedTaskMinutes = (task, minutes) => {
    const next = { ...task, completionTimeInMinutes: minutes };
    setOptimisticCompletedTasks((prev) => {
      if (prev.some((item) => item.id === task.id)) {
        return prev.map((item) => (item.id === task.id ? { ...item, ...next } : item));
      }
      return [next, ...prev];
    });
    setViewingPickTodayTask((prev) => (prev?.id === task.id ? { ...prev, ...next } : prev));
    return next;
  };

  const handleEditCompletedTaskTime = async (e, task) => {
    e?.stopPropagation?.();
    if (!task?.id || viewingTargetUserId) return;
    const minutes = await promptCompletionTime({
      kind: 'task',
      initialMinutes: Number(task.completionTimeInMinutes) > 0
        ? Number(task.completionTimeInMinutes)
        : undefined,
      confirmLabel: 'Save time',
    });
    if (minutes == null) return;
    try {
      setCompletingTaskId(task.id);
      const updated = await dispatch(updateTask({
        taskId: task.id,
        taskData: { completionTimeInMinutes: minutes },
      })).unwrap();
      applyCompletedTaskMinutes({ ...task, ...(updated || {}) }, minutes);
      dispatch(addNotification({ type: 'success', message: 'Time spent updated' }));
      refreshProductivityAnalytics();
      await dispatch(fetchDailyAnalytics({ date: selectedDay, targetUserId: viewingTargetUserId }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err, 'Failed to update time spent'),
      }));
    } finally {
      setCompletingTaskId(null);
    }
  };

  const handleEditSubtaskTime = async (parent, subtask) => {
    if (!parent?.id || !subtask?.id || viewingTargetUserId) return;
    const minutes = await promptCompletionTime({
      kind: 'subtask',
      initialMinutes: Number(subtask.completionTimeInMinutes) > 0
        ? Number(subtask.completionTimeInMinutes)
        : undefined,
      confirmLabel: 'Save time',
    });
    if (minutes == null) return;
    try {
      setSavingQuickTask(true);
      const updated = await dispatch(updateTask({
        taskId: subtask.id,
        taskData: { completionTimeInMinutes: minutes },
      })).unwrap();
      setMappedTasksByTaskId((prev) => ({
        ...prev,
        [parent.id]: (prev[parent.id] || []).map((item) => (
          item.id === subtask.id
            ? { ...item, ...(updated || {}), completionTimeInMinutes: minutes }
            : item
        )),
      }));
      dispatch(addNotification({ type: 'success', message: 'Time spent updated' }));
      refreshProductivityAnalytics();
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err, 'Failed to update time spent'),
      }));
    } finally {
      setSavingQuickTask(false);
    }
  };

  const handleCompleteWorkflow = async (e, item) => {
    e.stopPropagation();
    if (!item?.messageId || !item?.channelId || !currentUserId) return;

    const mappedItems = workflowMappedTasksByMessageId[item.messageId] || [];
    const linkedTasks = workflowLinkedTasksFrom(mappedItems);
    const { minutes, error } = await resolveWorkflowCloseMinutes({
      linkedTasks,
      mappedByTaskId: mappedTasksByTaskId,
      workflowTitle: item?.workflowTitle,
    });
    if (error) {
      dispatch(addNotification({ type: 'error', message: error }));
      return;
    }
    if (minutes == null) return;

    const { messageId } = item;
    let succeeded = false;
    try {
      setCompletingWorkflowId(messageId);
      setOptimisticCompletedWorkflows((prev) => [
        {
          ...item,
          messageId,
          label: item.workflowTitle || getWorkflowTicketText(item.content),
          content: item.content,
          completedAt: new Date().toISOString(),
          completionTimeInMinutes: Number(minutes) > 0 ? minutes : item.completionTimeInMinutes,
          workflowByCategory: {
            ...(item.workflowByCategory || {}),
            'workflow-status': WORKFLOW_CLOSED_STATUS_ID,
          },
          reactions: applyOptimisticReaction(
            item.reactions || [],
            WORKFLOW_CLOSED_STATUS_ID,
            currentUserId,
          ),
        },
        ...prev.filter((workflow) => workflow.messageId !== messageId),
      ]);

      await dispatch(addReaction({
        messageId,
        emoji: WORKFLOW_CLOSED_STATUS_ID,
        channelId: item.channelId,
        userId: currentUserId,
        ...(Number(minutes) > 0 ? { completionTimeInMinutes: minutes } : {}),
      })).unwrap();

      notifyWorkflowsAssignedChanged({
        messageId,
        action: 'completed',
        completionTimeInMinutes: minutes,
      });
      dispatch(addNotification({ type: 'success', message: 'Workflow marked complete' }));
      setActiveTab('completed');
      refreshProductivityAnalytics();
      fetchAssignedWorkflows({ silent: true });
      succeeded = true;
      await new Promise((resolve) => window.setTimeout(resolve, 1100));
    } catch (err) {
      setOptimisticCompletedWorkflows((prev) => prev.filter((workflow) => workflow.messageId !== messageId));
      fetchAssignedWorkflows();
      const msg = apiErrorMessage(err, 'Failed to complete workflow');
      dispatch(addNotification({ type: 'error', message: msg }));
    } finally {
      setCompletingWorkflowId(null);
    }
    if (succeeded) {
      await new Promise((resolve) => window.setTimeout(resolve, 520));
      setViewingWorkflow((prev) => (prev?.messageId === messageId ? null : prev));
    }
  };

  const handleOpenCreateTask = () => {
    if (!isSelectedToday) return;
    setEditingTask(null);
    setAddingSubtaskForId(null);
    setEditingTaskId(null);
    setActiveTab('pending');
    setAddingTask(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowTaskCreateDialog(true);
  };

  const beginInlineEditTask = (task) => {
    setAddingTask(false);
    setAddingSubtaskForId(null);
    setEditingTaskId(task.id);
  };

  const handleCloseCreateTask = () => {
    setShowTaskCreateDialog(false);
    setEditingTask(null);
  };

  const handleQuickCreateTask = async (payload = {}) => {
    const { title, estimateTimeInMinutes } = payload;
    setSavingQuickTask(true);
    try {
      const created = await dispatch(createTask({
        title,
        estimateTimeInMinutes,
        status: TaskStatus.PICK_TODAY,
        spaceId: payload?.spaceId || '',
        spaceName: payload?.spaceName || '',
      })).unwrap();
      if (created?.id) {
        setPickTodayTasks((prev) => (
          prev.some((task) => task.id === created.id) ? prev : [...prev, created]
        ));
      } else {
        fetchPickTodayTasks();
      }
      setAddingTask(false);
      refreshProductivityAnalytics();
    } catch (err) {
      const msg = err?.error || err?.message || err || 'Failed to add task';
      dispatch(addNotification({
        type: 'error',
        message: typeof msg === 'string' ? msg : 'Failed to add task',
      }));
    } finally {
      setSavingQuickTask(false);
    }
  };

  const handleQuickCreateSubtask = async (parent, { title, estimateTimeInMinutes }) => {
    if (!parent?.id) return;
    setSavingQuickTask(true);
    try {
      const created = await dispatch(createTask({
        title,
        estimateTimeInMinutes,
        mapTaskId: parent.id,
        spaceId: parent.spaceId || '',
        spaceName: parent.spaceName || '',
      })).unwrap();
      setMappedTasksByTaskId((prev) => ({
        ...prev,
        [parent.id]: [...(prev[parent.id] || []), created].filter(Boolean),
      }));
      setAddingSubtaskForId(null);
      refreshProductivityAnalytics();
    } catch (err) {
      const msg = err?.error || err?.message || err || 'Failed to add subtask';
      dispatch(addNotification({
        type: 'error',
        message: typeof msg === 'string' ? msg : 'Failed to add subtask',
      }));
      throw err;
    } finally {
      setSavingQuickTask(false);
    }
  };

  const handleQuickCreateWorkflowTask = async (item, { title, estimateTimeInMinutes, assigneeUserId }) => {
    if (!item?.messageId) return;
    setSavingQuickTask(true);
    try {
      const ownerId = assigneeUserId || item.senderId || currentUserId;
      const delegateId = ownerId && String(ownerId) !== String(currentUserId)
        ? [ownerId]
        : [];
      const created = await dispatch(createTask({
        title,
        estimateTimeInMinutes,
        status: TaskStatus.PICK_TODAY,
        messageId: item.messageId,
        delegateId,
        spaceId: item.workflowSpaceId || '',
        spaceName: item.workflowSpaceName || '',
      })).unwrap();
      if (created?.id) {
        const assignedToMe = !delegateId.length
          || delegateId.some((id) => String(id) === String(currentUserId));
        if (assignedToMe) {
          setPickTodayTasks((prev) => (
            prev.some((task) => task.id === created.id) ? prev : [...prev, created]
          ));
        }
        setWorkflowMappedTasksByMessageId((prev) => ({
          ...prev,
          [item.messageId]: [...(prev[item.messageId] || []), created],
        }));
      }
      setAddingWorkflowTaskForId(null);
      setActiveTab('pending');
      refreshProductivityAnalytics();
    } catch (err) {
      const msg = err?.error || err?.message || err || 'Failed to add workflow task';
      dispatch(addNotification({
        type: 'error',
        message: typeof msg === 'string' ? msg : 'Failed to add workflow task',
      }));
      throw err;
    } finally {
      setSavingQuickTask(false);
    }
  };

  const handleCloneWorkflowTask = async (item, task) => {
    if (!item?.messageId || !task?.id) return;
    const existingTags = Array.isArray(task.tags)
      ? task.tags.map((tag) => String(tag).trim()).filter(Boolean)
      : [];
    const tags = existingTags.some((tag) => tag.toLowerCase() === 'cloned')
      ? existingTags
      : [...existingTags, 'cloned'];
    const sourceDelegates = Array.isArray(task.delegateId)
      ? task.delegateId.filter(Boolean)
      : (task.delegateId ? [task.delegateId] : []);
    setSavingQuickTask(true);
    try {
      const created = await dispatch(createTask({
        title: (task.title || '').trim() || 'Task',
        description: task.description || '',
        estimateTimeInMinutes: task.estimateTimeInMinutes,
        status: TaskStatus.PICK_TODAY,
        messageId: task.messageId || item.messageId,
        workflowMessageIds: Array.isArray(task.workflowMessageIds) ? task.workflowMessageIds : undefined,
        delegateId: sourceDelegates,
        tags,
        spaceId: item.workflowSpaceId || task.spaceId || '',
        spaceName: item.workflowSpaceName || task.spaceName || '',
      })).unwrap();
      if (created?.id) {
        const assignedToMe = !sourceDelegates.length
          || sourceDelegates.some((id) => String(id) === String(currentUserId));
        if (assignedToMe) {
          setPickTodayTasks((prev) => (
            prev.some((row) => row.id === created.id) ? prev : [...prev, created]
          ));
        }
        setWorkflowMappedTasksByMessageId((prev) => ({
          ...prev,
          [item.messageId]: [...(prev[item.messageId] || []).filter((row) => row.id !== created.id), created],
        }));
        setViewingPickTodayTask(created);
      }
      setActiveTab('pending');
      refreshProductivityAnalytics();
    } catch (err) {
      const msg = err?.error || err?.message || err || 'Failed to clone task';
      dispatch(addNotification({
        type: 'error',
        message: typeof msg === 'string' ? msg : 'Failed to clone task',
      }));
    } finally {
      setSavingQuickTask(false);
    }
  };

  const handleQuickEditTask = async (task, { title, estimateTimeInMinutes }) => {
    if (!task?.id) return;
    setSavingQuickTask(true);
    try {
      const updated = await dispatch(updateTask({
        taskId: task.id,
        taskData: { title, estimateTimeInMinutes },
      })).unwrap();
      setPickTodayTasks((prev) => prev.map((item) => (
        item.id === task.id
          ? { ...item, ...(updated || {}), title, estimateTimeInMinutes }
          : item
      )));
      setEditingTaskId(null);
      refreshProductivityAnalytics();
    } catch (err) {
      const msg = err?.error || err?.message || err || 'Failed to update task';
      dispatch(addNotification({
        type: 'error',
        message: typeof msg === 'string' ? msg : 'Failed to update task',
      }));
      throw err;
    } finally {
      setSavingQuickTask(false);
    }
  };

  const handleQuickEditSubtask = async (parent, subtask, { title, estimateTimeInMinutes }) => {
    if (!parent?.id || !subtask?.id) return;
    setSavingQuickTask(true);
    try {
      const newMins = Number(estimateTimeInMinutes) || 0;
      const updated = await dispatch(updateTask({
        taskId: subtask.id,
        taskData: { title, estimateTimeInMinutes: newMins },
      })).unwrap();
      setMappedTasksByTaskId((prev) => ({
        ...prev,
        [parent.id]: (prev[parent.id] || []).map((item) => (
          item.id === subtask.id
            ? { ...item, ...(updated || {}), title, estimateTimeInMinutes: newMins }
            : item
        )),
      }));
      refreshProductivityAnalytics();
    } catch (err) {
      const msg = err?.error || err?.message || err || 'Failed to update subtask';
      dispatch(addNotification({
        type: 'error',
        message: typeof msg === 'string' ? msg : 'Failed to update subtask',
      }));
      throw err;
    } finally {
      setSavingQuickTask(false);
    }
  };

  const handleDeleteSubtask = (parent, subtask) => {
    if (!parent?.id || !subtask?.id) return;
    setSubtaskToDelete({ parent, subtask });
  };

  const confirmDeleteSubtask = async () => {
    const parent = subtaskToDelete?.parent;
    const subtask = subtaskToDelete?.subtask;
    if (!parent?.id || !subtask?.id) return;
    setSavingQuickTask(true);
    try {
      await dispatch(deleteTask(subtask)).unwrap();
      setMappedTasksByTaskId((prev) => ({
        ...prev,
        [parent.id]: (prev[parent.id] || []).filter((item) => item.id !== subtask.id),
      }));
      setSubtaskToDelete(null);
      refreshProductivityAnalytics();
    } catch (err) {
      const msg = err?.error || err?.message || err || 'Failed to delete subtask';
      dispatch(addNotification({
        type: 'error',
        message: typeof msg === 'string' ? msg : 'Failed to delete subtask',
      }));
    } finally {
      setSavingQuickTask(false);
    }
  };

  const handleDeletePendingTask = async (task, subtasks = []) => {
    if (!task?.id) return;
    if (!window.confirm(subtasks.length ? 'Delete this task and its subtasks?' : 'Delete this task?')) return;
    setSavingQuickTask(true);
    try {
      for (const subtask of subtasks) {
        if (subtask?.id) {
          await dispatch(deleteTask(subtask)).unwrap();
        }
      }
      await dispatch(deleteTask(task)).unwrap();
      setPickTodayTasks((prev) => prev.filter((item) => item.id !== task.id));
      setWorkflowMappedTasksByMessageId((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((messageId) => {
          next[messageId] = (next[messageId] || []).filter((item) => item.id !== task.id);
        });
        return next;
      });
      setMappedTasksByTaskId((prev) => {
        const next = { ...prev };
        delete next[task.id];
        return next;
      });
      if (viewingPickTodayTask?.id === task.id) {
        setViewingPickTodayTask(null);
      }
      refreshProductivityAnalytics();
    } catch (err) {
      const msg = err?.error || err?.message || err || 'Failed to delete task';
      dispatch(addNotification({
        type: 'error',
        message: typeof msg === 'string' ? msg : 'Failed to delete task',
      }));
      fetchPickTodayTasks();
    } finally {
      setSavingQuickTask(false);
    }
  };

  const handleTaskCreated = () => {
    if (viewMode === 'today' || viewMode === 'spaces') {
      refreshProductivityAnalytics();
    }
  };

  useEffect(() => {
    const onOnline = () => {
      if (viewMode === 'today' || viewMode === 'spaces') fetchPickTodayTasks({ force: true });
    };
    const onFlushed = () => {
      if (viewMode === 'today' || viewMode === 'spaces') fetchPickTodayTasks();
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('workloop:tasks-flushed', onFlushed);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('workloop:tasks-flushed', onFlushed);
    };
  }, [viewMode, fetchPickTodayTasks]);

  useEffect(() => {
    const ownerId = viewingTargetUserId || currentUserId;
    const onTaskUpdated = (event) => {
      const task = event?.detail?.task;
      if (!task?.id) return;
      const belongs = taskAssignedToUser(task, ownerId);
      const showOnToday = belongs && isTodayWorkTask(task) && selectedDay === todayStr;

      setPickTodayTasks((prev) => {
        const exists = prev.some((item) => item.id === task.id);
        if (showOnToday) {
          return exists
            ? prev.map((item) => (item.id === task.id ? { ...item, ...task } : item))
            : [...prev, task];
        }
        return exists ? prev.filter((item) => item.id !== task.id) : prev;
      });
      dispatch(applyLiveTaskToTodayCache({
        task,
        eventType: event?.detail?.action === 'deleted' ? 'TASK_DELETED' : 'TASK_UPDATED',
      }));
      setViewingPickTodayTask((prev) => (
        prev?.id === task.id ? { ...prev, ...task } : prev
      ));
      setOptimisticCompletedTasks((prev) => prev.map((item) => (
        item.id === task.id ? { ...item, ...task } : item
      )));
      setMappedTasksByTaskId((prev) => {
        let changed = false;
        const next = {};
        Object.entries(prev || {}).forEach(([parentId, list]) => {
          const mapped = (list || []).map((item) => {
            if (item.id !== task.id) return item;
            changed = true;
            return { ...item, ...task };
          });
          next[parentId] = mapped;
        });
        return changed ? next : prev;
      });
      setWorkflowMappedTasksByMessageId((prev) => {
        let changed = false;
        const next = {};
        Object.entries(prev || {}).forEach(([messageId, list]) => {
          const mapped = (list || []).map((item) => {
            if (item.id !== task.id) return item;
            changed = true;
            return { ...item, ...task };
          });
          next[messageId] = mapped;
        });
        return changed ? next : prev;
      });
    };
    window.addEventListener('workloop:task-updated', onTaskUpdated);
    return () => window.removeEventListener('workloop:task-updated', onTaskUpdated);
  }, [currentUserId, viewingTargetUserId, selectedDay, todayStr, dispatch]);

  const handleWorkflowAttachmentDownload = async (attachment) => {
    try {
      await downloadAttachment(attachment);
    } catch (error) {
      console.error('Download error:', error);
      openAttachmentFallback(attachment);
    }
  };

  const handleWorkflowTicketClick = (item) => {
    if (!item?.messageId) return;
    setViewingWorkflow(item);
  };

  const openWorkflowInChat = (item) => {
    if (!item?.messageId || !item?.channelId) return;
    setViewingWorkflow(null);
    if (onNavigateToMessage) {
      onNavigateToMessage(item);
      return;
    }
    const allChannels = [...channels, ...directMessages];
    const channel = allChannels.find((ch) => ch.id === item.channelId);
    if (!channel) {
      dispatch(addNotification({ type: 'error', message: 'Channel not found.' }));
      return;
    }
    dispatch(setCurrentChannel(channel));
    setTimeout(() => {
      navigate(`/chat?messageId=${item.messageId}`);
    }, 50);
  };

  const handleTaskChatClick = useCallback(async (navItem) => {
    if (!navItem?.messageId) return;

    let item = navItem;
    if (!item.channelId) {
      const message = await fetchMessageById(item.messageId);
      if (!message?.channelId) {
        dispatch(addNotification({ type: 'error', message: 'Could not find the linked chat message.' }));
        return;
      }
      item = {
        messageId: message.id,
        channelId: message.channelId,
        replyToMessageId: message.replyToMessageId || null,
      };
    }

    if (onNavigateToMessage) {
      onNavigateToMessage(item);
      return;
    }

    const allChannels = [...channels, ...directMessages];
    const channel = allChannels.find((ch) => ch.id === item.channelId);
    if (!channel) {
      dispatch(addNotification({ type: 'error', message: 'Channel not found.' }));
      return;
    }

    dispatch(setCurrentChannel(channel));
    setTimeout(() => {
      navigate(`/chat?messageId=${item.messageId}`);
    }, 50);
  }, [channels, directMessages, dispatch, fetchMessageById, navigate, onNavigateToMessage]);

  const handleRemoveTaskChat = useCallback(async (payload, bubbleKey) => {
    if (!payload?.taskId || !payload?.parentTaskId) return;

    const hiddenKeys = new Set([bubbleKey]);
    if (payload.type === 'primary-link') {
      hiddenKeys.add(`primary-msg-${payload.taskId}`);
      hiddenKeys.add(`primary-task-${payload.taskId}`);
    } else if (payload.type === 'mapped') {
      hiddenKeys.add(`mapped-msg-${payload.taskId}`);
      hiddenKeys.add(`mapped-task-${payload.taskId}`);
    }

    setHiddenChatBubbleKeys((prev) => new Set([...prev, ...hiddenKeys]));

    try {
      setRemovingChatKey(bubbleKey);
      if (payload.type === 'mapped') {
        await dispatch(deleteTask(payload.taskId)).unwrap();
        const refreshed = await dispatch(fetchTasksByMapTaskId(payload.parentTaskId)).unwrap();
        setMappedTasksByTaskId((prev) => ({
          ...prev,
          [payload.parentTaskId]: Array.isArray(refreshed) ? refreshed : [],
        }));
      } else if (payload.type === 'primary-link') {
        const updatedTask = await dispatch(updateTask({
          taskId: payload.taskId,
          taskData: { clearMessageId: true },
        })).unwrap();
        setPickTodayTasks((prev) => prev.map((t) => (
          t.id === payload.taskId ? { ...t, ...updatedTask, messageId: null } : t
        )));
        await fetchPickTodayTasks();
      }

      setTaskMessagesByTaskId((prev) => {
        const next = { ...prev };
        delete next[payload.taskId];
        return next;
      });
      dispatch(addNotification({ type: 'success', message: 'Chat removed from task' }));
    } catch (err) {
      setHiddenChatBubbleKeys((prev) => {
        const next = new Set(prev);
        hiddenKeys.forEach((key) => next.delete(key));
        return next;
      });
      const msg = err?.error || err?.message || 'Failed to remove chat from task';
      dispatch(addNotification({ type: 'error', message: msg }));
    } finally {
      setRemovingChatKey(null);
    }
  }, [dispatch, fetchPickTodayTasks]);

  const handleAddReminder = () => {
    if (!isSelectedToday) return;
    setEditingReminder(null);
    setCloneDraft(null);
    setShowModal(true);
  };

  const handleEditReminder = (reminder) => {
    setCloneDraft(null);
    setEditingReminder(reminder);
    setShowModal(true);
  };

  const handleCloneReminder = (reminder) => {
    if (!reminder) return;
    const title = String(reminder.title || 'Reminder').trim() || 'Reminder';
    setEditingReminder(null);
    setCloneDraft({
      title: / \(copy\)$/i.test(title) ? title : `${title} (copy)`,
      description: reminder.description || '',
      estimateTimeInMinutes: reminder.estimateTimeInMinutes,
      enabled: reminder.enabled !== false,
      timezone: reminder.timezone,
      recurrence: reminder.recurrence ? { ...reminder.recurrence } : undefined,
      tasks: Array.isArray(reminder.tasks)
        ? reminder.tasks.map((task) => ({ title: task?.title || '' })).filter((task) => task.title)
        : [],
    });
    setShowModal(true);
  };

  const handleFormSubmit = async (payload) => {
    try {
      setSaving(true);
      if (editingReminder) {
        await reminderService.updateReminder(editingReminder.id, payload);
        dispatch(addNotification({ type: 'success', message: 'Reminder updated' }));
      } else {
        await reminderService.createReminder(payload);
        dispatch(addNotification({
          type: 'success',
          message: cloneDraft ? 'Reminder cloned' : 'Reminder created',
        }));
      }
      setShowModal(false);
      setEditingReminder(null);
      setCloneDraft(null);
      await fetchReminders();
      refreshProductivityAnalytics();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to save reminder';
      dispatch(addNotification({ type: 'error', message: msg }));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReminder = async (reminder) => {
    try {
      await reminderService.deleteReminder(reminder);
      dispatch(addNotification({ type: 'success', message: 'Reminder deleted' }));
      await fetchReminders();
      refreshProductivityAnalytics();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to delete reminder';
      dispatch(addNotification({ type: 'error', message: msg }));
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingReminder(null);
    setCloneDraft(null);
  };

  const handleViewReminder = (reminder) => {
    setViewingReminder(reminder);
  };

  const handleCompleteReminderForToday = async (reminder) => {
    try {
      setCompletingReminderId(reminder.id);
      await reminderService.markReminderCompleteForToday(reminder, selectedDay);
      dispatch(addNotification({
        type: 'success',
        message: selectedDay === todayStr ? 'Reminder completed for today' : `Reminder completed for ${formatSelectedDayLabel(selectedDay, todayStr)}`,
      }));
      await fetchReminders();
      refreshProductivityAnalytics();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to complete reminder';
      dispatch(addNotification({ type: 'error', message: msg }));
    } finally {
      setCompletingReminderId(null);
    }
  };

  const handleSkipReminderForToday = async (reminder) => {
    try {
      setSkippingReminderId(reminder.id);
      await reminderService.skipReminderForToday(reminder, selectedDay);
      setViewingReminder(null);
      dispatch(addNotification({
        type: 'success',
        message: selectedDay === todayStr ? 'Reminder skipped for today (missed)' : `Reminder skipped for ${formatSelectedDayLabel(selectedDay, todayStr)}`,
      }));
      await fetchReminders();
      refreshProductivityAnalytics();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to skip reminder for today';
      dispatch(addNotification({ type: 'error', message: msg }));
    } finally {
      setSkippingReminderId(null);
    }
  };

  const handleToggleReminderTask = async (reminder, task) => {
    if (!reminder || !task?.id) return;
    const key = `${reminder.id}:${task.id}`;
    try {
      setTogglingTaskKey(key);
      const updated = await reminderService.toggleReminderTask(reminder, task.id, selectedDay);
      if (viewingReminder && viewingReminder.id === reminder.id && updated) {
        setViewingReminder(updated);
      }
      await fetchReminders();
      refreshProductivityAnalytics();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to update task';
      dispatch(addNotification({ type: 'error', message: msg }));
    } finally {
      setTogglingTaskKey(null);
    }
  };

  const handleSnoozeReminder = async (minutes, reminder) => {
    try {
      await reminderService.snoozeReminder(reminder, minutes);
      setViewingReminder(null);
      dispatch(addNotification({
        type: 'success',
        message: `Reminder snoozed for ${formatSnoozeDuration(minutes)}`,
      }));
      await fetchReminders();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to snooze reminder';
      dispatch(addNotification({ type: 'error', message: msg }));
    }
  };

  const handleAcceptReminder = async (reminder) => {
    try {
      setActionLoadingId(reminder.id);
      await reminderService.acceptReminder(reminder.id);
      dispatch(addNotification({ type: 'success', message: 'Reminder accepted' }));
      if (String(reminderInvite?.id || reminderInvite?.reminderId || '') === String(reminder.id)) {
        dispatch(clearReminderInvite());
      }
      await fetchForMeReminders();
      await fetchReminders();
      refreshProductivityAnalytics();
    } catch (err) {
      dispatch(addNotification({ type: 'error', message: err.response?.data?.error || err.message || 'Failed to accept' }));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRejectReminder = async (reminder) => {
    try {
      setActionLoadingId(reminder.id);
      await reminderService.rejectReminder(reminder.id);
      dispatch(addNotification({ type: 'success', message: 'Reminder rejected' }));
      if (String(reminderInvite?.id || reminderInvite?.reminderId || '') === String(reminder.id)) {
        dispatch(clearReminderInvite());
      }
      await fetchForMeReminders();
      await fetchReminders();
      refreshProductivityAnalytics();
    } catch (err) {
      dispatch(addNotification({ type: 'error', message: err.response?.data?.error || err.message || 'Failed to reject' }));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRevokeReminder = async (reminder) => {
    try {
      setActionLoadingId(reminder.id);
      await reminderService.revokeReminder(reminder.id);
      dispatch(addNotification({ type: 'success', message: 'Reminder revoked' }));
      await fetchForMeReminders();
      await fetchReminders();
      refreshProductivityAnalytics();
    } catch (err) {
      dispatch(addNotification({ type: 'error', message: err.response?.data?.error || err.message || 'Failed to revoke' }));
    } finally {
      setActionLoadingId(null);
    }
  };

  const allRemindersSorted = reminders.slice().sort(
    (a, b) => getSortableTriggerMinutes(a, nowMs) - getSortableTriggerMinutes(b, nowMs)
  );
  const pendingApprovalReminders = forMeReminders.filter(
    (reminder) => String(reminder?.status || '').toUpperCase() === 'PENDING'
  );
  const reminderMatchesQuery = (reminder) => {
    const q = allRemindersQuery.trim().toLowerCase();
    if (!q) return true;
    const hay = [
      reminder?.title,
      reminder?.description,
      reminder?.targetUsername,
      reminder?.creatorUsername,
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  };
  const filteredAllReminders = allRemindersSorted.filter(reminderMatchesQuery);
  const filteredPendingApprovals = pendingApprovalReminders.filter(reminderMatchesQuery);

  const displayedReminders = (viewMode === 'today'
    ? reminders.filter((r) => {
        if (isPastDayView) {
          return reminderPendingOnDate(r, selectedDay, todayStr)
            || reminderCompletedOnDate(r, selectedDay);
        }
        if (activeTab === 'pending') {
          return reminderPendingOnDate(r, selectedDay, todayStr);
        }
        return reminderCompletedOnDate(r, selectedDay);
      })
    : viewMode === 'forMe'
      ? forMeReminders
      : reminders
  ).slice().sort((a, b) => {
    if (viewMode === 'today' && isPastDayView) {
      const aDone = reminderCompletedOnDate(a, selectedDay) ? 0 : 1;
      const bDone = reminderCompletedOnDate(b, selectedDay) ? 0 : 1;
      if (aDone !== bDone) return aDone - bDone;
    }
    return getSortableTriggerMinutes(a, nowMs) - getSortableTriggerMinutes(b, nowMs);
  });

  const pendingRemindersCount = reminders.filter((r) => reminderPendingOnDate(r, selectedDay, todayStr)).length;
  const overdueRemindersCount = reminders.filter((r) => (
    reminderPendingOnDate(r, selectedDay, todayStr)
    && isReminderTriggerOverdue(r, selectedDay, todayStr, nowMs)
  )).length;
  const completedRemindersCount = reminders.filter((r) => reminderCompletedOnDate(r, selectedDay)).length;

  const isEmptyToday = viewMode === 'today' && displayedReminders.length === 0;
  const isEmptyAll = viewMode === 'all' && reminders.length === 0;
  const isEmptyForMe = viewMode === 'forMe' && forMeReminders.length === 0;
  const isEmpty = viewMode === 'today' ? isEmptyToday : viewMode === 'forMe' ? isEmptyForMe : isEmptyAll;
  const listLoading = viewMode === 'forMe' ? forMeLoading : loading;

  const todayCount = pendingRemindersCount;
  const allCount = reminders.length;

  const doneMins = useMemo(() => {
    const entryMins = sumCreditedMinutes(productivityEntriesToday);
    const reminderMins = dailyAnalytics?.totalCompletedReminderMinutes || 0;
    if (entryMins > 0 || productivityEntriesToday.length > 0) {
      return entryMins + reminderMins;
    }
    if (dailyAnalytics) {
      return dailyAnalytics.totalCompletedMinutesCombined
        ?? ((dailyAnalytics.totalCompletedMinutes || 0)
          + (dailyAnalytics.totalCompletedReminderMinutes || 0));
    }
    return 0;
  }, [dailyAnalytics, productivityEntriesToday]);
  // Live timer only while checked in; after logout use day timeline office minutes.
  const preferLiveOfficeTimer = isSelectedToday
    && !viewingTargetUserId
    && isActiveWorkSession(breakStatus);
  const officeMins = resolveOfficeMinutesForProductivity({
    breakStatus,
    dayOfficeMinutes,
    preferLiveTimer: preferLiveOfficeTimer,
  }) + (officeTick * 0);
  const productivityPct = calcOfficeProductivityPercent(doneMins, officeMins);
  const forMeCount = forMeReminders.length;

  const tabBadgeClass = 'ml-1 text-[10px] tabular-nums text-gray-400 dark:text-gray-500';
  const remindersHeaderCount =
    viewMode === 'today' ? todayCount : viewMode === 'forMe' ? forMeCount : allCount;

  const showRemindersBlock = remindersVisible;
  const showTasksBlock = tasksVisible && (viewMode === 'today' || viewMode === 'spaces');
  const showWorkflowsBlock = workflowsVisible && (viewMode === 'today' || viewMode === 'spaces');
  const remindersListOpen = (homeLayout && isPastDayView)
    ? remindersExpanded
    : (homeLayout && homeFocus === 'reminders') || remindersExpanded;
  const tasksListOpen = (homeLayout && isPastDayView)
    ? tasksExpanded
    : (homeLayout && homeFocus === 'tasks') || tasksExpanded;
  const workflowsListOpen = (homeLayout && isPastDayView)
    ? workflowsExpanded
    : (homeLayout && homeFocus === 'workflows') || workflowsExpanded;

  const isNeedToCallClosed = (item) => Boolean(item?.done) || item?.status === 'skipped' || item?.status === 'called';
  const pendingNeedToCall = needToCallItems.filter((item) => !isNeedToCallClosed(item));
  const closedNeedToCall = needToCallItems.filter(isNeedToCallClosed);
  const showOwnNeedToCall = !viewingTargetUserId && viewMode === 'today' && !isPastDayView;
  const persistNeedToCall = (next) => {
    setNeedToCallItems(next);
    saveNeedToCall(next)
      .then((saved) => {
        const list = Array.isArray(saved) ? saved : next;
        setNeedToCallItems(list);
        window.dispatchEvent(new CustomEvent('workloop:need-to-call-changed', { detail: { items: list } }));
      })
      .catch(() => {});
  };
  const patchNeedToCall = (itemId, patch) => {
    persistNeedToCall(needToCallItems.map((entry) => (
      entry.id === itemId ? { ...entry, ...patch } : entry
    )));
  };

  const showHomeReminders = homeLayout && (isPastDayView || homeFocus === 'reminders');
  const showHomeTasks = homeLayout && (isPastDayView || homeFocus === 'tasks' || viewMode === 'spaces');
  const showHomeWorkflows = homeLayout && (isPastDayView || homeFocus === 'workflows' || viewMode === 'spaces');
  const showHomeCalls = homeLayout && showOwnNeedToCall && homeFocus === 'calls';
  const showHomeChart = homeLayout && (isPastDayView || !homeFocus);
  const chartShowsTimeline = Boolean(showHomeChart && !isPastDayView);
  const showDayTimelineLog = viewMode === 'today' && (isPastDayView || !chartShowsTimeline);
  const showPanelReminders = !homeLayout && showRemindersBlock;
  const showPanelTasks = !homeLayout && showTasksBlock;
  const showPanelWorkflows = !homeLayout && showWorkflowsBlock;

  const tabScope = homeLayout
    ? homeFocus
    : (showPanelWorkflows && !showPanelReminders && !showPanelTasks
      ? 'workflows'
      : showPanelTasks && !showPanelReminders && !showPanelWorkflows
        ? 'tasks'
        : 'reminders');
  const pendingTabCount = tabScope === 'workflows'
    ? pendingWorkflowsCount
    : tabScope === 'tasks'
      ? displayedPickTodayTasks.length
      : tabScope === 'calls'
        ? pendingNeedToCall.length
        : pendingRemindersCount;
  const completedTabCount = tabScope === 'workflows'
    ? completedWorkflowsCount
    : tabScope === 'tasks'
      ? completedListItemCount
      : tabScope === 'calls'
        ? closedNeedToCall.length
        : completedRemindersCount;

  const canShowTasksSection = viewMode === 'today' || viewMode === 'spaces';
  const canShowWorkflowsSection = viewMode === 'today' || viewMode === 'spaces';
  const remindersRestoreCount = viewMode === 'today'
    ? (activeTab === 'pending' ? pendingRemindersCount : completedRemindersCount)
    : viewMode === 'forMe'
      ? forMeCount
      : allCount;
  const tasksRestoreCount = activeTab === 'pending'
    ? pickTodayTasks.length
    : completedTasksCount;
  const workflowsRestoreCount = activeTab === 'pending'
    ? pendingWorkflowsCount
    : completedWorkflowsCount;
  const hiddenSectionRestores = [
    !sharedPanelsVisible && {
      id: 'sharedPanels',
      label: 'Shared Panels',
      shortLabel: 'Panels',
      count: sharedPanelsRestoreCount,
    },
    !remindersVisible && {
      id: 'reminders',
      label: 'Reminders',
      shortLabel: 'Reminders',
      count: remindersRestoreCount,
    },
    !tasksVisible && canShowTasksSection && {
      id: 'tasks',
      label: 'Tasks',
      shortLabel: 'Tasks',
      count: tasksRestoreCount,
    },
    !workflowsVisible && canShowWorkflowsSection && {
      id: 'workflows',
      label: 'Workflows',
      shortLabel: 'Workflows',
      count: workflowsRestoreCount,
    },
  ].filter(Boolean);

  const toggleWorkflowChipFilter = useCallback((kind, value, label) => {
    setWorkflowChipFilter((prev) => {
      if (prev && prev.kind === kind && prev.value === value) return null;
      return { kind, value, label };
    });
  }, []);

  const filteredAssignedWorkflows = useMemo(() => {
    if (!workflowChipFilter) return pendingAssignedWorkflows;
    const { kind, value } = workflowChipFilter;
    const selfIdForSource = viewingTargetUserId || currentUserId;
    return pendingAssignedWorkflows.filter((item) => {
      if (kind === 'type') {
        const typeId = item.workflowByCategory?.['work-item-type'] || '__workflow__';
        return typeId === value;
      }
      if (kind === 'status') {
        return item.workflowByCategory?.['workflow-status'] === value;
      }
      if (kind === 'source') {
        const source = resolveWorkflowSourceLabel(
          item,
          [...channels, ...directMessages],
          users,
          selfIdForSource,
        );
        return source.label === value;
      }
      if (kind === 'track') {
        return item.workflowTrackId === value;
      }
      return true;
    });
  }, [
    pendingAssignedWorkflows,
    workflowChipFilter,
    viewingTargetUserId,
    currentUserId,
    channels,
    directMessages,
    users,
  ]);

  const displayedPendingWorkflows = useMemo(() => (
    [...filteredAssignedWorkflows].sort((a, b) => {
      const urgentDiff = compareUrgentFirst(a, b, { isUrgent: isOpenUrgentWorkflow });
      if (urgentDiff !== 0) return urgentDiff;
      const timerDiff = compareWorkTimerPriority('WORKFLOW', a.messageId, 'WORKFLOW', b.messageId);
      if (timerDiff !== 0) return timerDiff;
      const dateA = new Date(a?.createdAt || a?.timestamp || 0).getTime() || 0;
      const dateB = new Date(b?.createdAt || b?.timestamp || 0).getTime() || 0;
      return workflowSortOrder === 'oldest' ? dateA - dateB : dateB - dateA;
    }).filter((item) => (
      viewMode !== 'spaces' || selectedSpaceKey === 'all' || resolveItemSpace(item).key === selectedSpaceKey
    ))
  ), [filteredAssignedWorkflows, workflowSortOrder, workTimerTick, viewMode, selectedSpaceKey]);

  const todaySpaceGroups = useMemo(() => collectTodaySpaceGroups(
    isPastDayView ? pendingTasksForSelectedDay : pickTodayTasks,
    [...(assignedWorkflows || []), ...(completedAssignedWorkflows || [])],
  ), [isPastDayView, pendingTasksForSelectedDay, pickTodayTasks, assignedWorkflows, completedAssignedWorkflows]);
  const todaySpaceGroupsWithIcons = useMemo(() => {
    const byId = new Map((orgSpaces || []).map((space) => [String(space.id), space]));
    return todaySpaceGroups.map((group) => {
      const catalog = byId.get(String(group.key));
      return {
        ...group,
        name: group.key === OTHER_SPACE_KEY
          ? (mySpaceName || catalog?.name || group.name)
          : (catalog?.name || group.name),
        icon: group.key === OTHER_SPACE_KEY ? mySpaceIcon : (catalog?.icon || ''),
        createdByUserId: catalog?.createdByUserId || '',
      };
    });
  }, [todaySpaceGroups, orgSpaces, mySpaceIcon, mySpaceName]);
  const spacePendingPercents = useMemo(
    () => pendingSharePercents(todaySpaceGroupsWithIcons),
    [todaySpaceGroupsWithIcons],
  );
  const showSpacesTab = canSeeSpacesNav && isSelectedToday && shouldShowSpacesTab(todaySpaceGroups);
  const showSpacesSideMenu = showSpacesTab
    && (viewMode === 'today' || viewMode === 'spaces')
    && (isExpanded || wideHome);

  useEffect(() => {
    if (!showSpacesTab) return undefined;
    if (spacesFetchedOnce) return undefined;
    dispatch(loadOrgSpaces());
    return undefined;
  }, [showSpacesTab, spacesFetchedOnce, dispatch]);

  useEffect(() => {
    if (!showSpacesSideMenu || spacesRailMinified) return undefined;
    dispatch(loadSpaceAnalytics({ targetUserId: viewingTargetUserId || undefined }));
    return undefined;
  }, [showSpacesSideMenu, spacesRailMinified, viewingTargetUserId, dispatch]);

  useEffect(() => {
    try {
      localStorage.setItem('workloop.spacesRailMinified', String(spacesRailMinified));
    } catch (_) {
      // ignore
    }
  }, [spacesRailMinified]);

  useEffect(() => {
    if (viewMode === 'spaces' && !showSpacesTab) {
      setViewMode('today');
    }
  }, [viewMode, showSpacesTab]);

  const displayedCompletedTodayWorkflows = useMemo(() => (
    [...(completedTodayWorkflows || [])]
      .filter((item) => (
        viewMode !== 'spaces' || selectedSpaceKey === 'all' || resolveItemSpace(item).key === selectedSpaceKey
      ))
      .sort((a, b) => {
        const dateA = new Date(a?.createdAt || a?.timestamp || 0).getTime() || 0;
        const dateB = new Date(b?.createdAt || b?.timestamp || 0).getTime() || 0;
        return workflowSortOrder === 'oldest' ? dateA - dateB : dateB - dateA;
      })
  ), [completedTodayWorkflows, viewMode, selectedSpaceKey, workflowSortOrder]);

  const selectHomeFocus = (id) => {
    if (homeFocus === id) {
      setHomeFocus(null);
      return;
    }
    setHomeFocus(id);
    if (viewMode !== 'spaces') setViewMode('today');
    setActiveTab('pending');
  };

  const openAllRemindersModal = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setAllRemindersModalTab('all');
    setAllRemindersQuery('');
    setShowAllRemindersModal(true);
    fetchForMeReminders();
  };

  const openPendingApprovalsModal = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setAllRemindersModalTab('pendingApprovals');
    setAllRemindersQuery('');
    setShowAllRemindersModal(true);
    fetchForMeReminders();
  };

  const viewAllRemindersButton = (
    <span className="relative inline-flex flex-shrink-0">
      <button
        type="button"
        onClick={openAllRemindersModal}
        className={`peer ${sectionFocusButtonClass} text-primary-600 dark:text-primary-400`}
        aria-label="View all reminders"
      >
        <ViewAllRemindersIcon className="w-3.5 h-3.5" />
      </button>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-0 bottom-full z-[100] mb-1.5 whitespace-nowrap rounded-md border border-gray-800/20 bg-gray-900 px-2 py-1 text-[10px] font-medium text-white shadow-lg opacity-0 transition-opacity duration-150 peer-hover:opacity-100 peer-focus-visible:opacity-100 dark:border-gray-600 dark:bg-gray-700"
      >
        View all reminders
      </span>
    </span>
  );

  const pendingApprovalsCount = pendingApprovalReminders.length;
  const pendingApprovalsButton = (
    <span className="relative inline-flex flex-shrink-0">
      <button
        type="button"
        onClick={openPendingApprovalsModal}
        className={`peer ${sectionFocusButtonClass} text-amber-600 dark:text-amber-400`}
        aria-label={`Pending approvals${pendingApprovalsCount ? `, ${pendingApprovalsCount}` : ''}`}
      >
        <PendingApprovalsIcon className="w-3.5 h-3.5" />
        {pendingApprovalsCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 rounded-full bg-amber-500 text-white text-[9px] font-bold leading-[14px] text-center tabular-nums">
            {pendingApprovalsCount > 99 ? '99+' : pendingApprovalsCount}
          </span>
        )}
      </button>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-0 bottom-full z-[100] mb-1.5 whitespace-nowrap rounded-md border border-gray-800/20 bg-gray-900 px-2 py-1 text-[10px] font-medium text-white shadow-lg opacity-0 transition-opacity duration-150 peer-hover:opacity-100 peer-focus-visible:opacity-100 dark:border-gray-600 dark:bg-gray-700"
      >
        Pending approvals
      </span>
    </span>
  );

  const renderReminderRow = (reminder, {
    allowComplete = true,
    compactActions = false,
    forMe = false,
    hideScheduleActions = false,
    variant = 'row',
  } = {}) => {
    const isCompleted = reminderCompletedOnDate(reminder, selectedDay);
    const forMeMode = forMe || viewMode === 'forMe';
    const canActOnDay = allowComplete
      && !forMeMode
      && selectedDay <= todayStr
      && reminderPendingOnDate(reminder, selectedDay, todayStr);
    return (
      <ReminderItem
        key={reminder.id}
        reminder={reminder}
        currentUserId={currentUserId}
        onEdit={handleEditReminder}
        onClone={handleCloneReminder}
        onDelete={handleDeleteReminder}
        onView={handleViewReminder}
        onCompleteForToday={canActOnDay ? handleCompleteReminderForToday : undefined}
        completingReminderId={completingReminderId}
        onSkipForToday={!hideScheduleActions && canActOnDay ? handleSkipReminderForToday : undefined}
        skippingReminderId={skippingReminderId}
        forMeMode={forMeMode}
        onAccept={handleAcceptReminder}
        onReject={handleRejectReminder}
        onRevoke={handleRevokeReminder}
        actionLoadingId={actionLoadingId}
        isCompleted={isCompleted}
        selectedDay={selectedDay}
        todayStr={todayStr}
        nowMs={nowMs}
        compactActions={compactActions}
        onSnooze={hideScheduleActions || forMeMode ? undefined : handleSnoozeReminder}
        variant={variant}
        onToggleTask={
          !forMeMode && variant !== 'card' && selectedDay <= todayStr
            ? (task) => handleToggleReminderTask(reminder, task)
            : undefined
        }
        togglingTaskId={togglingTaskKey}
      />
    );
  };

  const showSpacesStrip = showSpacesTab && (viewMode === 'today' || viewMode === 'spaces');
  const selectedSpaceGroup = useMemo(() => {
    if (viewMode !== 'spaces' || !selectedSpaceKey || selectedSpaceKey === 'all') return null;
    return todaySpaceGroupsWithIcons.find((group) => group.key === selectedSpaceKey) || null;
  }, [viewMode, selectedSpaceKey, todaySpaceGroupsWithIcons]);
  const canEditSelectedSpace = Boolean(
    selectedSpaceGroup
    && currentUserId
    && (
      selectedSpaceGroup.key === OTHER_SPACE_KEY
        ? !viewingTargetUserId
        : String(selectedSpaceGroup.createdByUserId || '') === String(currentUserId)
    ),
  );

  useEffect(() => {
    setSpaceBannerEditing(false);
  }, [selectedSpaceKey, viewMode]);

  const openSpaceBannerEditor = () => {
    if (!selectedSpaceGroup || !canEditSelectedSpace) return;
    setSpaceBannerName(selectedSpaceGroup.name || '');
    setSpaceBannerIcon(selectedSpaceGroup.icon || '');
    setSpaceBannerEditing(true);
  };

  const saveSelectedSpace = async () => {
    if (!selectedSpaceGroup || !canEditSelectedSpace) return;
    const nextName = String(spaceBannerName || '').trim();
    if (!nextName) {
      dispatch(addNotification({ type: 'error', message: 'Space name is required' }));
      return;
    }
    try {
      setSpaceBannerSaving(true);
      if (selectedSpaceGroup.key === OTHER_SPACE_KEY) {
        setMySpaceIcon(spaceBannerIcon || '');
        setMySpaceName(nextName);
        localStorage.setItem('workloop.mySpaceIcon', spaceBannerIcon || '');
        localStorage.setItem('workloop.mySpaceName', nextName);
      } else {
        const updated = await updateSpace(selectedSpaceGroup.key, {
          name: nextName,
          icon: spaceBannerIcon || '',
        });
        dispatch(upsertSpace(updated));
      }
      setSpaceBannerEditing(false);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Failed to update space',
      }));
    } finally {
      setSpaceBannerSaving(false);
    }
  };

  const selectedSpaceTone = selectedSpaceGroup ? spaceTone(selectedSpaceGroup.key) : null;
  const selectedSpaceBanner = selectedSpaceGroup ? (
    <div className="mb-3 px-0.5">
      <div className="flex items-center gap-2.5 min-w-0">
        <span className={`flex-shrink-0 h-11 w-11 rounded-full flex items-center justify-center text-lg font-bold ${selectedSpaceTone.chip}`}>
          {selectedSpaceGroup.icon || spaceInitials(selectedSpaceGroup.name)}
        </span>
        <div className="min-w-0 flex-1">
          {spaceBannerEditing ? (
            <input
              type="text"
              value={spaceBannerName}
              onChange={(event) => setSpaceBannerName(event.target.value)}
              maxLength={80}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-2 py-1 font-bold text-gray-900 dark:text-white"
              style={{ fontSize: 22 }}
              aria-label="Space name"
            />
          ) : (
            <h3
              className={`font-bold leading-tight truncate ${selectedSpaceTone.title}`}
              style={{ fontSize: 22 }}
            >
              {selectedSpaceGroup.name}
            </h3>
          )}
        </div>
        {canEditSelectedSpace && !spaceBannerEditing && (
          <button
            type="button"
            onClick={openSpaceBannerEditor}
            className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
            title="Edit space"
            aria-label="Edit space"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M4 20h4.586a1 1 0 00.707-.293l9.414-9.414a2 2 0 000-2.828l-2.172-2.172a2 2 0 00-2.828 0L4.293 14.707A1 1 0 004 15.414V20z" />
            </svg>
          </button>
        )}
      </div>
      {spaceBannerEditing && (
        <div className="mt-2">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Icon</p>
          <div className="grid grid-cols-8 gap-1">
            {SPACE_ICON_PRESETS.map((icon) => (
              <button
                key={icon}
                type="button"
                onClick={() => setSpaceBannerIcon(icon)}
                className={`h-8 w-8 text-base ${
                  spaceBannerIcon === icon ? 'bg-primary-100 dark:bg-primary-900/40' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {icon}
              </button>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSpaceBannerIcon('')}
              className="text-[11px] font-semibold text-gray-500 hover:text-gray-800"
            >
              Remove icon
            </button>
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => setSpaceBannerEditing(false)}
              className="h-7 px-2 text-[11px] font-semibold text-gray-500"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={spaceBannerSaving}
              onClick={saveSelectedSpace}
              className="h-7 px-2.5 rounded-md bg-primary-600 text-white text-[11px] font-semibold disabled:opacity-60"
            >
              {spaceBannerSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  ) : null;
  const activeSpaceAnalytics = selectedSpaceGroup
    ? spaceAnalyticsByKey[selectedSpaceGroup.key]
    : ((viewMode === 'today' || viewMode === 'spaces') ? spaceAnalyticsByKey.__all__ : null);
  const spaceActivityChartEl = (
    !spaceBannerEditing
    && (viewMode === 'today' || viewMode === 'spaces')
    && activeSpaceAnalytics?.activityDays?.length
  ) ? (
    <div className="mb-3">
      <SpaceActivityChart days={activeSpaceAnalytics.activityDays} />
    </div>
  ) : null;
  const spaceMenuItems = (
    <>
      <button
        type="button"
        onClick={() => {
          setSelectedSpaceKey('all');
          setViewMode('today');
        }}
        className={`relative overflow-hidden border px-3 py-2 text-left transition-shadow ${
          isExpanded ? 'w-full' : 'rounded-xl'
        } ${
          (viewMode !== 'spaces' || selectedSpaceKey === 'all')
            ? 'border-primary-300 bg-primary-50 text-primary-800 shadow-sm dark:border-primary-700 dark:bg-primary-900/40 dark:text-primary-100'
            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300'
        }`}
      >
        <p className="text-sm font-bold truncate">All spaces</p>
        <p className="mt-0.5 text-[12px] font-medium opacity-80 tabular-nums">
          {spaceWorkLabel({
            workflowCount: todaySpaceGroupsWithIcons.reduce((sum, group) => sum + (group.workflowCount || 0), 0),
            taskCount: todaySpaceGroupsWithIcons.reduce((sum, group) => sum + (group.taskCount || 0), 0),
          })}
        </p>
      </button>
      {todaySpaceGroupsWithIcons.map((group) => {
        const tone = spaceTone(group.key);
        const selected = viewMode === 'spaces' && selectedSpaceKey === group.key;
        return (
          <button
            key={group.key}
            type="button"
            onClick={() => {
              setSelectedSpaceKey(group.key);
              setViewMode('spaces');
            }}
            className={`relative overflow-hidden border px-3 py-2 pl-3.5 text-left transition-shadow ${
              isExpanded ? 'w-full' : 'rounded-xl'
            } ${selected ? tone.chipSelected : tone.chip} ${
              selected ? 'ring-2 ring-offset-1 ring-white/40 shadow-sm' : 'hover:brightness-[1.03]'
            }`}
            title={group.name}
          >
            <span className={`absolute inset-y-0 left-0 w-1.5 ${tone.dot}`} aria-hidden="true" />
            <p className="text-sm font-bold truncate">{group.icon ? `${group.icon} ${group.name}` : group.name}</p>
            <p className="mt-0.5 text-[12px] font-medium opacity-80 tabular-nums">
              {spaceWorkLabel(group)}
            </p>
          </button>
        );
      })}
    </>
  );

  return (
    <div className="flex flex-col h-full min-w-0 overflow-x-hidden bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 flex-wrap">
        <div className="flex items-center space-x-2 min-w-0">
          {headerLeading}
          <ClockIcon className="w-5 h-5 text-primary-500 flex-shrink-0" />
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {isSelectedToday ? "Today's Tasks" : 'Day workload'}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatSelectedDayLabel(selectedDay, todayStr)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 p-0.5 min-w-0">
            <button
              type="button"
              onClick={() => setViewMode('today')}
              className={`px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs font-medium rounded-md transition-colors flex items-center ${
                viewMode === 'today'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Today
              <span className={tabBadgeClass}>{todayCount}</span>
            </button>
            {showSpacesTab && (
              <button
                type="button"
                onClick={() => setViewMode('spaces')}
                className={`px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs font-medium rounded-md transition-colors flex items-center ${
                  viewMode === 'spaces'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Spaces
                <span className={tabBadgeClass}>{todaySpaceGroups.filter((group) => group.key !== '__other__').length}</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setViewMode('all')}
              className={`px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs font-medium rounded-md transition-colors flex items-center ${
                viewMode === 'all'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              All
              <span className={tabBadgeClass}>{allCount}</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('forMe')}
              className={`px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs font-medium rounded-md transition-colors flex items-center ${
                viewMode === 'forMe'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              For me
              <span className={tabBadgeClass}>{forMeCount}</span>
            </button>
          </div>
          {typeof onToggleExpand === 'function' && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
              title={isExpanded ? 'Exit full screen' : 'Full screen'}
              aria-label={isExpanded ? 'Exit full screen' : "Show Today's Tasks full screen"}
              aria-pressed={isExpanded}
            >
              {isExpanded
                ? <MinimizeSectionIcon className="w-4 h-4" />
                : <MaximizeSectionIcon className="w-4 h-4" />}
            </button>
          )}
          {typeof onClose === 'function' && (
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
            title="Close panel"
          >
            <CloseIcon className="w-4 h-4" />
          </button>
          )}
        </div>
      </div>

      {showSpacesTab && (viewMode === 'today' || viewMode === 'spaces') && !showSpacesSideMenu && (
        <div className="flex-shrink-0 px-3 pt-2.5">
          <div className="rounded-2xl border border-gray-200/90 dark:border-gray-700 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900/70 dark:to-gray-800/80 px-3 py-2.5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
                Spaces
              </span>
              <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {spaceMenuItems}
            </div>
          </div>
        </div>
      )}

      <div className={showSpacesSideMenu
        ? 'flex flex-1 min-h-0 overflow-hidden'
        : 'contents'}
      >
        {showSpacesSideMenu && (
          <nav
            aria-label="Spaces"
              className={`relative flex-shrink-0 min-h-0 self-stretch flex flex-col border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/70 overflow-hidden transition-[width] duration-200 ease-out ${
              spacesRailMinified ? 'w-[80px]' : 'w-[240px]'
            }`}
          >
            <button
              type="button"
              onClick={() => setSpacesRailMinified((prev) => !prev)}
              className={`h-[50px] w-full flex items-center border-b border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                spacesRailMinified ? 'justify-center' : 'px-3 gap-2'
              }`}
              title={spacesRailMinified ? 'Expand spaces menu' : 'Collapse spaces menu'}
              aria-label={spacesRailMinified ? 'Expand spaces menu' : 'Collapse spaces menu'}
              aria-expanded={!spacesRailMinified}
            >
              {spacesRailMinified ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              ) : (
                <>
                  <span className="text-[12px] font-bold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                    Spaces
                  </span>
                  <span className="flex-1" />
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </>
              )}
            </button>
            <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar flex flex-col">
              <button
                type="button"
                onClick={() => {
                  setSelectedSpaceKey('all');
                  setViewMode('today');
                }}
                className={`min-h-[64px] w-full flex items-center border-b border-gray-200 dark:border-gray-700 text-[14px] font-bold ${
                  spacesRailMinified ? 'justify-center' : 'px-2.5 gap-2 py-2'
                } ${
                  (viewMode !== 'spaces' || selectedSpaceKey === 'all')
                    ? 'bg-gradient-to-br from-primary-700 via-primary-600 to-sky-500 text-white'
                    : 'bg-gradient-to-br from-gray-50 via-white to-sky-50 text-gray-700 dark:from-gray-800 dark:via-gray-800 dark:to-sky-950/40 dark:text-gray-200 hover:from-gray-100 hover:to-sky-100 dark:hover:from-gray-700'
                }`}
                title="All spaces"
              >
                <span className="w-8 flex-shrink-0 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </span>
                {!spacesRailMinified && (
                  <>
                  <span className="min-w-0 flex-1 text-left">
                    <span className="block truncate">All spaces</span>
                    <span className={`block text-[12px] font-semibold tabular-nums ${
                      (viewMode !== 'spaces' || selectedSpaceKey === 'all') ? 'text-white/85' : 'opacity-75'
                    }`}>
                      {spaceWorkLabel({
                        workflowCount: todaySpaceGroupsWithIcons.reduce((sum, group) => sum + (group.workflowCount || 0), 0),
                        taskCount: todaySpaceGroupsWithIcons.reduce((sum, group) => sum + (group.taskCount || 0), 0),
                      })}
                    </span>
                  </span>
                  </>
                )}
              </button>
              {todaySpaceGroupsWithIcons.map((group) => {
                const tone = spaceTone(group.key);
                const selected = viewMode === 'spaces' && selectedSpaceKey === group.key;
                const mark = group.icon || spaceInitials(group.name);
                const taskLabel = spaceWorkLabel(group);
                return (
                  <button
                    key={group.key}
                    type="button"
                    onClick={() => {
                      setSelectedSpaceKey(group.key);
                      setViewMode('spaces');
                    }}
                    className={`min-h-[64px] w-full flex items-center border-b border-gray-200 dark:border-gray-700 text-[14px] font-bold ${
                      spacesRailMinified ? 'justify-center py-1.5' : 'px-2.5 gap-2 py-2'
                    } ${
                      selected ? `${tone.chipSelected} shadow-inner` : `${tone.chip} hover:brightness-[1.03]`
                    }`}
                    title={`${group.name} · ${taskLabel}`}
                  >
                    <span className="w-8 flex-shrink-0 flex flex-col items-center justify-center leading-none text-lg gap-0.5">
                      <span>{mark}</span>
                      {spacesRailMinified && (
                        <span className={`text-[11px] font-semibold tabular-nums ${selected ? 'text-white/85' : 'opacity-75'}`}>
                          {spaceWorkCompact(group)}
                        </span>
                      )}
                    </span>
                    {!spacesRailMinified && (
                      <>
                      <span className="min-w-0 flex-1 text-left">
                        <span className="block truncate">{group.name}</span>
                        <span className={`block text-[12px] font-semibold tabular-nums ${
                          selected ? 'text-white/85' : 'opacity-75'
                        }`}>
                          {taskLabel}
                        </span>
                      </span>
                      <SpacePercentBadge percent={spacePendingPercents[group.key]} selected={selected} />
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </nav>
        )}
        <div className={showSpacesSideMenu
          ? 'flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden'
          : 'contents'}
        >
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-3 py-2">
        <div
          ref={dateBarRef}
          className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5"
          role="tablist"
          aria-label="Workload date"
        >
          {dateBarDays.map((dateStr) => {
            const parts = formatDateBarParts(dateStr);
            const selected = dateStr === selectedDay && (viewMode === 'today' || viewMode === 'spaces');
            const isTodayChip = dateStr === todayStr;
            const noLogin = dateStr !== todayStr && !loginDays.has(dateStr);
            return (
              <button
                key={dateStr}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-disabled={noLogin}
                disabled={noLogin}
                onClick={() => {
                  if (noLogin) return;
                  setSelectedDay(dateStr);
                  if (viewMode !== 'spaces') setViewMode('today');
                }}
                className={`flex flex-col items-center justify-center min-w-[3.15rem] px-2 py-1.5 rounded-xl text-center flex-shrink-0 transition-colors ${
                  noLogin
                    ? 'bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/60 cursor-not-allowed opacity-80'
                    : selected
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-gray-50 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title={noLogin
                  ? `${formatSelectedDayLabel(dateStr, todayStr)} · No login`
                  : formatSelectedDayLabel(dateStr, todayStr)}
              >
                <span className={`text-[10px] font-semibold uppercase tracking-wide ${
                  noLogin
                    ? 'text-red-500 dark:text-red-400'
                    : selected
                    ? 'text-white/80'
                    : 'text-gray-400 dark:text-gray-500'
                }`}>
                  {isTodayChip ? 'Today' : parts.weekday}
                </span>
                <span className="text-sm font-bold tabular-nums leading-tight">{parts.dayNum}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className={`flex-1 min-h-0 overflow-hidden ${homeLayout ? `flex flex-col ${wideHome ? 'lg:flex-row' : ''}` : 'flex flex-col'}`}>
        {homeLayout && (
          <aside className={`w-full flex-shrink-0 border-orange-200/80 dark:border-gray-700 bg-orange-50/40 dark:bg-gray-900/40 ${
            wideHome
              ? 'p-2 sm:p-3 gap-1.5 sm:gap-3 lg:w-[min(22rem,40%)] border-b lg:border-b-0 lg:border-r grid grid-cols-2 lg:flex lg:flex-col overflow-y-auto'
              : 'p-2 gap-1.5 border-b grid grid-cols-2'
          }`}
          >
            <div className={`w-full min-w-0 col-span-2 ${wideHome ? 'lg:basis-auto' : ''}`}>
              <SharedPanelsWidget
                variant="inline"
                onSelectUser={setViewingTargetUserId}
                currentViewingUserId={viewingTargetUserId}
                onShowMetrics={() => setHomeFocus(null)}
                metricsActive={!homeFocus}
              />
            </div>
            {(selectedSpaceBanner || spaceActivityChartEl) ? (
              <div className="w-full min-w-0 col-span-2">
                {selectedSpaceBanner}
                {spaceActivityChartEl}
              </div>
            ) : null}
            {!isPastDayView && (
              <>
            <HomePendingCard
              compact={!wideHome}
              title="Reminders"
              count={pendingRemindersCount}
              hint={
                overdueRemindersCount > 0
                  ? `${overdueRemindersCount} overdue.`
                  : (pendingRemindersCount === 1 ? '1 due today.' : `${pendingRemindersCount} due today.`)
              }
              completedCount={completedRemindersCount}
              selected={homeFocus === 'reminders'}
              onClick={() => selectHomeFocus('reminders')}
            />
            <HomePendingCard
              compact={!wideHome}
              title="Tasks"
              count={displayedPickTodayTasks.length}
              hint={pickTodayLoading
                ? 'Loading…'
                : (displayedPickTodayTasks.length === 1 ? '1 picked for today.' : `${displayedPickTodayTasks.length} picked for today.`)}
              completedCount={displayedCompletedTodayTasks.length}
              selected={homeFocus === 'tasks'}
              onClick={() => selectHomeFocus('tasks')}
            />
            <HomePendingCard
              compact={!wideHome}
              title="Workflows"
              pendingLabel="Pending approval"
              count={displayedPendingWorkflows.length}
              hint={assignedWorkflowsLoading
                ? 'Loading…'
                : (displayedPendingWorkflows.length === 1 ? '1 assigned to you.' : `${displayedPendingWorkflows.length} assigned to you.`)}
              completedCount={displayedCompletedTodayWorkflows.length}
              selected={homeFocus === 'workflows'}
              onClick={() => selectHomeFocus('workflows')}
            />
            {showOwnNeedToCall ? (
            <HomePendingCard
              compact={!wideHome}
              title="Calls"
              pendingLabel="Pending call"
              count={pendingNeedToCall.length}
              hint={pendingNeedToCall.length === 1 ? '1 person to call.' : `${pendingNeedToCall.length} people to call.`}
              completedCount={closedNeedToCall.length}
              selected={homeFocus === 'calls'}
              onClick={() => selectHomeFocus('calls')}
            />
            ) : null}
              </>
            )}
          </aside>
        )}
        <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden px-2 sm:px-3 py-2 sm:py-3 bg-slate-50/70 dark:bg-gray-900/25">

        {!homeLayout && hiddenSectionRestores.length > 0 && (
          <div className="mb-2 flex flex-nowrap items-center gap-1 sticky top-0 z-20 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm py-1">
            {hiddenSectionRestores.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => showSection(section.id)}
                className="inline-flex flex-1 min-w-0 items-center justify-center gap-0.5 rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-700/40 px-1 py-1 text-[10px] font-medium leading-tight text-gray-600 dark:text-gray-300 hover:border-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50/70 dark:hover:bg-primary-900/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/35 transition-colors"
                aria-label={`Show ${section.label} section (${section.count})`}
                title={`Show ${section.label} (${section.count})`}
              >
                <ShowSectionIcon className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{section.shortLabel}</span>
                <span className="tabular-nums text-gray-500 dark:text-gray-400 flex-shrink-0">
                  ({section.count})
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Shared Panels Widget */}
        {!homeLayout && sharedPanelsVisible && (
        <SharedPanelsWidget 
          onSelectUser={setViewingTargetUserId}
          currentViewingUserId={viewingTargetUserId}
            onHide={() => hideSection('sharedPanels')}
          />
        )}

        {!homeLayout ? (
          <>
            {selectedSpaceBanner}
            {spaceActivityChartEl}
          </>
        ) : null}

        {showHomeChart && (
          <div className={isPastDayView ? 'mb-3' : ''}>
          <TodayProductivityChart
            productivityPct={productivityPct}
            plannedMins={officeMins}
            doneMins={doneMins}
            denominatorLabel="office minutes"
            tasksPicked={dailyAnalytics?.totalPickedTasks || pickTodayTasks.length}
            tasksDone={Math.max(
              dailyAnalytics?.totalCompletedTasks || 0,
              uniqueTasksDoneCount,
            )}
            remindersPlanned={dailyAnalytics?.totalPlannedReminders ?? pendingRemindersCount}
            remindersDone={dailyAnalytics?.totalCompletedReminders ?? completedRemindersCount}
            workflowsPending={pendingWorkflowsCount}
            workflowsDone={completedWorkflowsCount}
            completedTasks={completedTodayTasks}
            completedReminders={completedTodayReminders}
            completedWorkflows={completedTodayWorkflows}
            productivityEntries={productivityEntriesToday}
            ownerUserId={viewingTargetUserId}
            date={selectedDay}
            showActivityLog={chartShowsTimeline}
            heading={isPastDayView ? 'Productivity' : 'Productivity today'}
            showWorkWatch={isSelectedToday && !viewingTargetUserId}
            headerActions={!isPastDayView ? magicPickButton() : null}
            compact={!wideHome}
          />
          </div>
        )}

        {/* Productivity Summary Widget */}
        {!homeLayout && dailyAnalytics && viewMode === 'today' && (
          <div className="mb-1 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2.5 sm:p-3 border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                {isPastDayView ? 'Productivity' : 'Productivity Today'}
              </span>
              <div className="flex items-center gap-2 min-w-0">
                {!isPastDayView && magicPickButton()}
                <WorkSessionWatch enabled={isSelectedToday && !viewingTargetUserId} />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-semibold text-emerald-600 tabular-nums">{doneMins}</span>
                  {' / '}{officeMins} office mins
                {officeMins > 0 && ` (${productivityPct}%)`}
              </span>
              </div>
            </div>
            {isSelectedToday && !viewingTargetUserId && (
              <CurrentPresenceStatus />
            )}
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full transition-all duration-500"
                style={{
                  width: `${officeMins > 0 ? Math.min(100, productivityPct) : 0}%`,
                }}
              ></div>
            </div>
            <div className="mt-2 space-y-1 text-[10px] text-gray-500 dark:text-gray-400">
              <div className="flex justify-between gap-2">
                <span>
                  Tasks: {dailyAnalytics.totalPickedTasks} picked ·{' '}
                  <span className="font-semibold text-emerald-600">{dailyAnalytics.totalCompletedTasks} done</span>
                </span>
                <span className="tabular-nums flex-shrink-0">
                  <span className="text-emerald-600 font-semibold">{dailyAnalytics.totalCompletedMinutes}</span>
                  /{dailyAnalytics.totalPickedMinutes} min
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span>
                  Reminders: {dailyAnalytics.totalPlannedReminders ?? 0} planned ·{' '}
                  <span className="font-semibold text-emerald-600">
                  {dailyAnalytics.totalCompletedReminders ?? 0} done
                  </span>
                </span>
                <span className="tabular-nums flex-shrink-0">
                  <span className="text-emerald-600 font-semibold">
                    {dailyAnalytics.totalCompletedReminderMinutes ?? 0}
                  </span>
                  /{dailyAnalytics.totalPlannedReminderMinutes ?? 0} min
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span>
                  Workflows:{' '}
                  <span className="font-semibold text-emerald-600">
                    {dailyAnalytics.totalCompletedWorkflows ?? completedWorkflowsCount} done
                  </span>
                </span>
              </div>
            </div>
          </div>
        )}

        {/* List title + Pending/Completed tabs */}
        {!isPastDayView && (!homeLayout || homeFocus === 'reminders' || homeFocus === 'tasks' || homeFocus === 'workflows' || homeFocus === 'calls') && (viewMode === 'today' || viewMode === 'spaces') && (
          <div className="mb-3 py-0.5">
            {homeLayout && homeFocus && (
              <div className="mb-2.5 flex items-center gap-2 px-0.5 min-w-0">
                <h3 className={`${sectionHeadingClass(activeTab === 'completed')} min-w-0`}>
                  {homeFocus === 'reminders'
                    ? (activeTab === 'completed' ? 'Completed Reminders' : 'Pending Reminders')
                    : homeFocus === 'calls'
                      ? (activeTab === 'completed' ? 'Completed Calls' : 'Pending Calls')
                    : homeFocus === 'workflows'
                      ? (activeTab === 'completed' ? 'Completed Workflows' : 'Pending approval Workflows')
                      : (activeTab === 'completed' ? 'Completed Tasks' : 'Pending Tasks')}
                </h3>
                <div className="ml-auto flex items-center gap-0.5">
                  {!isPastDayView && (homeFocus === 'tasks' || homeFocus === 'workflows') && (
                    <>
                      {refreshTodayButton()}
                      {magicPickButton()}
                    </>
                  )}
                  {homeFocus === 'reminders' && (
                    <>
                      {viewAllRemindersButton}
                      {pendingApprovalsButton}
                      <span className="relative inline-flex flex-shrink-0">
                      <button
                        type="button"
                        onClick={handleAddReminder}
                        disabled={loading || !isSelectedToday}
                        className={`peer ${sectionFocusButtonClass} text-primary-600 dark:text-primary-400 disabled:opacity-40 disabled:pointer-events-none`}
                        aria-label="Add reminder"
                      >
                        <PlusIcon className="w-3.5 h-3.5" />
                      </button>
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute right-0 bottom-full z-[100] mb-1.5 whitespace-nowrap rounded-md border border-gray-800/20 bg-gray-900 px-2 py-1 text-[10px] font-medium text-white shadow-lg opacity-0 transition-opacity duration-150 peer-hover:opacity-100 peer-focus-visible:opacity-100 peer-disabled:opacity-0 dark:border-gray-600 dark:bg-gray-700"
                      >
                        Add reminder
                      </span>
                    </span>
                    </>
                  )}
                  {homeFocus === 'tasks' && (
                    <span className="relative inline-flex flex-shrink-0">
                      <button
                        type="button"
                        onClick={handleOpenCreateTask}
                        disabled={!isSelectedToday}
                        className={`peer ${sectionFocusButtonClass} text-primary-600 dark:text-primary-400 disabled:opacity-40 disabled:pointer-events-none`}
                        aria-label="Add task"
                      >
                        <PlusIcon className="w-3.5 h-3.5" />
                      </button>
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute right-0 bottom-full z-[100] mb-1.5 whitespace-nowrap rounded-md border border-gray-800/20 bg-gray-900 px-2 py-1 text-[10px] font-medium text-white shadow-lg opacity-0 transition-opacity duration-150 peer-hover:opacity-100 peer-focus-visible:opacity-100 dark:border-gray-600 dark:bg-gray-700"
                      >
                        Add task
                      </span>
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setHomeFocus(null)}
                    className={sectionFocusButtonClass}
                    title="Back to chart"
                    aria-label="Back to chart"
                  >
                    <HideSectionIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
            <div className="flex justify-center">
            <div className="inline-flex rounded-full bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700 p-1 shadow-sm">
              <button
                type="button"
                onClick={() => setActiveTab('pending')}
                className={`px-3.5 py-1.5 text-[11px] font-semibold tracking-wide uppercase rounded-full transition-all flex items-center gap-1.5 ${
                  activeTab === 'pending'
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                {homeFocus === 'workflows' ? 'Pending approval' : 'Pending'}
                <span className={`tabular-nums ${activeTab === 'pending' ? 'text-white/90' : 'opacity-70'}`}>
                  {pendingTabCount}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('completed')}
                className={`px-3.5 py-1.5 text-[11px] font-semibold tracking-wide uppercase rounded-full transition-all flex items-center gap-1.5 ${
                  activeTab === 'completed'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300'
                }`}
              >
                Completed
                <span className={`tabular-nums ${activeTab === 'completed' ? 'text-white/90' : 'opacity-80'}`}>
                  {completedTabCount}
                </span>
              </button>
            </div>
            </div>
          </div>
        )}

        {(showPanelReminders || showHomeReminders) && (
        <>
          <div className={`${sectionCardClass}${isPastDayView ? ' mb-3' : ''}`}>
            {homeLayout && isPastDayView && (
              <div className={sectionCardHeaderClass}>
          <button
            type="button"
            onClick={() => setRemindersExpanded((v) => !v)}
                  aria-label={remindersListOpen ? 'Collapse reminders' : 'Expand reminders'}
                  aria-expanded={remindersListOpen}
                  className="min-w-0 flex-1 flex items-center gap-2 text-left rounded-md px-1 py-0.5 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
                >
                  {remindersListOpen ? (
                    <ChevronUpIcon className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                  )}
                  <span className="min-w-0 flex-1 flex items-center gap-2">
                    <span className={sectionHeadingClass(false)}>Reminders</span>
                    <span className={sectionCountPillClass(false)}>
                      {listLoading ? '…' : pendingRemindersCount + completedRemindersCount}
                    </span>
                  </span>
                </button>
              </div>
            )}
            {!homeLayout && (
            <div className={sectionCardHeaderClass}>
              <button
                type="button"
                onClick={() => setRemindersExpanded((v) => !v)}
                aria-label={remindersListOpen ? 'Collapse reminders list' : 'Expand reminders list'}
                aria-expanded={remindersListOpen}
                className="min-w-0 flex-1 flex items-center gap-2 text-left rounded-md px-1 py-0.5 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
              >
              <ChevronDownIcon
                className={`w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                    remindersListOpen ? '' : '-rotate-90'
                }`}
              />
                <span className="min-w-0 flex-1 flex items-center gap-2">
                  <span className={sectionHeadingClass(!isPastDayView && viewMode === 'today' && activeTab === 'completed')}>
                    {isPastDayView || viewMode !== 'today'
                      ? 'Reminders'
                      : `${activeTab === 'pending' ? 'Pending' : 'Completed'} Reminders`}
              </span>
                  <span className={sectionCountPillClass(!isPastDayView && viewMode === 'today' && activeTab === 'completed')}>
                    {listLoading
                      ? '…'
                      : (isPastDayView
                        ? pendingRemindersCount + completedRemindersCount
                        : (viewMode === 'today'
                        ? (activeTab === 'pending' ? pendingRemindersCount : completedRemindersCount)
                        : remindersHeaderCount))}
                  </span>
            </span>
          </button>
              <button
                type="button"
                onClick={() => hideSection('reminders')}
                className={sectionFocusButtonClass}
                title="Hide reminders"
                aria-label="Hide reminders section"
              >
                <HideSectionIcon />
              </button>
              {viewAllRemindersButton}
              {pendingApprovalsButton}
              <span className="relative inline-flex flex-shrink-0">
                <button
                  type="button"
                  onClick={handleAddReminder}
                  disabled={loading || !isSelectedToday}
                  className={`peer ${sectionFocusButtonClass} text-primary-600 dark:text-primary-400 disabled:opacity-40 disabled:pointer-events-none`}
                  aria-label="Add reminder"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                </button>
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute right-0 bottom-full z-[100] mb-1.5 whitespace-nowrap rounded-md border border-gray-800/20 bg-gray-900 px-2 py-1 text-[10px] font-medium text-white shadow-lg opacity-0 transition-opacity duration-150 peer-hover:opacity-100 peer-focus-visible:opacity-100 peer-disabled:opacity-0 dark:border-gray-600 dark:bg-gray-700"
                >
                  Add reminder
                </span>
              </span>
        </div>
            )}

            {remindersListOpen && (
            <div className={sectionCardBodyClass}>
              {listLoading ? (
                <div className={emptyStateClass}>
                  <ClockIcon className="w-8 h-8 mb-2 opacity-30 animate-pulse" />
                  <p className={emptyTitleClass}>{viewMode === 'forMe' ? 'Loading reminders for you...' : 'Loading reminders...'}</p>
            </div>
          ) : error && viewMode !== 'forMe' ? (
                <div className={`${emptyStateClass} text-red-500 dark:text-red-400`}>
                  <p className="text-sm font-medium">{error}</p>
              <button
                type="button"
                onClick={fetchReminders}
                    className="mt-2 text-xs font-semibold text-primary-500 hover:underline"
              >
                Retry
              </button>
            </div>
          ) : reminders.length === 0 && viewMode !== 'forMe' ? (
                <div className={emptyStateClass}>
                  <ClockIcon className="w-8 h-8 mb-2 opacity-30" />
                  <p className={emptyTitleClass}>No reminders yet</p>
                  <p className={emptyHintClass}>Add one to get started</p>
            </div>
          ) : viewMode === 'forMe' && forMeReminders.length === 0 ? (
                <div className={emptyStateClass}>
                  <ClockIcon className="w-8 h-8 mb-2 opacity-30" />
                  <p className={emptyTitleClass}>No reminders for you</p>
                  <p className={emptyHintClass}>When someone creates a reminder for you, it will appear here</p>
            </div>
          ) : isEmptyToday ? (
                <div className={emptyStateClass}>
                  <ClockIcon className="w-8 h-8 mb-2 opacity-30" />
                  <p className={emptyTitleClass}>
                    {isPastDayView
                      ? 'No reminders for this day'
                      : (activeTab === 'pending'
                        ? `No pending reminders for ${isSelectedToday ? 'today' : 'this day'}`
                        : `No reminders completed ${isSelectedToday ? 'today' : 'this day'}`)}
                  </p>
                  {!isPastDayView && activeTab === 'pending' && <p className={emptyHintClass}>Switch to All to see your reminders or add one</p>}
            </div>
          ) : (
                displayedReminders.map((reminder) => renderReminderRow(reminder))
              )}
            </div>
            )}
          </div>
        </>
        )}

        {showOwnNeedToCall && (
          showHomeCalls
          || (homeLayout && homeFocus === 'reminders' && activeTab === 'pending')
          || (!homeLayout && activeTab === 'pending')
        ) && (
          <div className={`${sectionCardClass}${isPastDayView ? ' mb-3' : ''} ${homeLayout && homeFocus === 'reminders' ? 'mt-3' : ''}`}>
            <div className={sectionCardHeaderClass}>
              <span className="min-w-0 flex-1 flex items-center gap-2">
                <span className={sectionHeadingClass(showHomeCalls && activeTab === 'completed')}>
                  {showHomeCalls && activeTab === 'completed' ? 'Completed Calls' : 'Pending Calls'}
                </span>
                <span className={sectionCountPillClass(showHomeCalls && activeTab === 'completed')}>
                  {(showHomeCalls && activeTab === 'completed' ? closedNeedToCall : pendingNeedToCall).length}
                </span>
              </span>
            </div>
            <div className={sectionCardBodyClass}>
              {((showHomeCalls && activeTab === 'completed') ? closedNeedToCall : pendingNeedToCall).length === 0 ? (
                <div className={emptyStateClass}>
                  <p className={emptyTitleClass}>
                    {showHomeCalls && activeTab === 'completed' ? 'No completed calls' : 'No pending calls'}
                  </p>
                  <p className={emptyHintClass}>People you still need to call will show up here</p>
                </div>
              ) : (
                ((showHomeCalls && activeTab === 'completed') ? closedNeedToCall : pendingNeedToCall).map((item) => {
                  const label = item.name || 'Someone';
                  return (
                    <button
                      type="button"
                      key={item.id}
                      onClick={() => setNeedToCallDetail(item)}
                      className={`${sectionListItemClass} text-left`}
                    >
                      <div className={sectionRowPadClass}>
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-600 text-white text-[11px] font-bold flex-shrink-0">
                          {String(label).trim().split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || '?'}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className={itemTitleClass}>{label}</p>
                          {item.phone ? (
                            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 tabular-nums">{item.phone}</p>
                          ) : null}
                          {item.reason ? (
                            <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 truncate">{item.reason}</p>
                          ) : null}
                        </div>
                        {item.status === 'followup' ? (
                          <span className="flex-shrink-0 text-[10px] font-semibold text-amber-700 dark:text-amber-300">Follow-up</span>
                        ) : item.status === 'skipped' ? (
                          <span className="flex-shrink-0 text-[10px] font-semibold text-gray-500">Skipped</span>
                        ) : item.done ? (
                          <span className="flex-shrink-0 text-[10px] font-semibold text-gray-500">Called</span>
                        ) : null}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Pick today tasks: only when Today view and not loading/error */}
        {(showPanelTasks || showHomeTasks) && (
          <>
            {(!homeLayout || isPastDayView) && <div className="border-t border-transparent my-2.5" aria-hidden="true" />}
            <div className={`${sectionCardClass}${isPastDayView ? ' mb-3' : ''}`}>
              {homeLayout && isPastDayView && (
                <div className={sectionCardHeaderClass}>
                  <button
                    type="button"
                    onClick={() => setTasksExpanded((v) => !v)}
                    aria-label={tasksListOpen ? 'Collapse tasks' : 'Expand tasks'}
                    aria-expanded={tasksListOpen}
                    className="min-w-0 flex-1 flex items-center gap-2 text-left rounded-md px-1 py-0.5 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
                  >
                    {tasksListOpen ? (
                      <ChevronUpIcon className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                    )}
                    <span className="min-w-0 flex-1 flex items-center gap-2">
                      <span className={sectionHeadingClass(false)}>Tasks</span>
                      <span className={sectionCountPillClass(false)}>
                        {pickTodayLoading ? '…' : displayedPickTodayTasks.length + completedListItemCount}
                      </span>
                    </span>
                  </button>
                </div>
              )}
              {!homeLayout && (
              <div className={sectionCardHeaderClass}>
                <button
                  type="button"
                  onClick={() => setTasksExpanded((v) => !v)}
                  aria-label={tasksListOpen ? 'Collapse tasks list' : 'Expand tasks list'}
                  aria-expanded={tasksListOpen}
                  className="min-w-0 flex-1 flex items-center gap-2 text-left rounded-md px-1 py-0.5 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
                >
                  <ChevronDownIcon
                    className={`w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                      tasksListOpen ? '' : '-rotate-90'
                    }`}
                  />
                  <span className="min-w-0 flex-1 flex items-center gap-2">
                    <span className={sectionHeadingClass(!isPastDayView && activeTab === 'completed')}>
                      {isPastDayView ? 'Tasks' : (activeTab === 'pending' ? 'Pending Tasks' : 'Completed Tasks')}
                    </span>
                    <span className={sectionCountPillClass(!isPastDayView && activeTab === 'completed')}>
                      {isPastDayView
                        ? (pickTodayLoading ? '…' : displayedPickTodayTasks.length + completedListItemCount)
                        : (activeTab === 'pending'
                        ? (pickTodayLoading ? '…' : displayedPickTodayTasks.length)
                        : completedListItemCount)}
                    </span>
                  </span>
                </button>
                <div className="ml-auto flex items-center gap-0.5">
                {!isPastDayView && refreshTodayButton()}
                {!isPastDayView && magicPickButton()}
                <button
                  type="button"
                  onClick={() => hideSection('tasks')}
                  className={sectionFocusButtonClass}
                  title="Hide tasks"
                  aria-label="Hide tasks section"
                >
                  <HideSectionIcon />
                </button>
                <span className="relative inline-flex flex-shrink-0">
                  <button
                    type="button"
                    onClick={handleOpenCreateTask}
                    disabled={!isSelectedToday}
                    className={`peer ${sectionFocusButtonClass} text-primary-600 dark:text-primary-400 disabled:opacity-40 disabled:pointer-events-none`}
                    aria-label="Add task"
                  >
                    <PlusIcon className="w-3.5 h-3.5" />
                  </button>
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute right-0 bottom-full z-[100] mb-1.5 whitespace-nowrap rounded-md border border-gray-800/20 bg-gray-900 px-2 py-1 text-[10px] font-medium text-white shadow-lg opacity-0 transition-opacity duration-150 peer-hover:opacity-100 peer-focus-visible:opacity-100 dark:border-gray-600 dark:bg-gray-700"
                  >
                    Add task
                  </span>
                </span>
              </div>
              </div>
              )}
              
              {tasksListOpen && (
              <div className={sectionCardBodyClass}>
              {taskKindSortBar}
              {(isPastDayView || activeTab === 'pending') && (
                <>
                  {pickTodayLoading ? (
                    <p className={`${emptyTitleClass} px-4 py-8 text-center`}>Loading tasks...</p>
                  ) : displayedPickTodayTasks.length === 0 && !addingTask ? (
                    isPastDayView ? null : (
                    <p className={`${emptyTitleClass} px-4 py-8 text-center`}>
                      {pickTodayTasks.length > 0 && taskKindFilterEmptyMessage
                        ? taskKindFilterEmptyMessage
                        : (isSelectedToday ? 'No tasks picked for today' : 'No incomplete tasks stored for this day')}
                    </p>
                    )
                  ) : (
                      displayedPickTodayTasks.map((task) => {
                        const isExpanded = expandedTaskIds.has(task.id);
                        const chatsVisible = taskChatsVisibleIds.has(task.id);
                        const label = getTaskListLabel(task);
                        const isMappingTarget = chatMappingTarget?.type === 'task' && chatMappingTarget.id === task.id;
                        const progressPct = normalizeTaskProgress(task.completionPercentage, task.status);
                        const mappedItems = mappedTasksByTaskId[task.id] || [];
                        const subtasks = mappedItems.filter(isNamedSubtask);
                        const mappedChats = mappedItems.filter((item) => !isNamedSubtask(item));
                        const totalMinutes = taskActualMinutes(task, mappedItems);
                        const unfinishedSubtasks = subtasks.filter((item) => !isSubtaskFinished(item)).length;
                        const canCompleteParent = unfinishedSubtasks === 0;
                        const showExpandBubble = Boolean(task.messageId) || hasTaskDescription(task) || mappedChats.length > 0;
                        const canDeleteTask = sameUserId(task.createdByUserId, currentUserId);
                        const isUrgentTask = isOpenUrgentTask(task);
                        const urgentEstimateMinutes = Number(task.estimateTimeInMinutes) > 0
                          ? Number(task.estimateTimeInMinutes)
                          : undefined;
                        const rowClass = isUrgentTask
                          ? ''
                          : sectionListItemClass;
                        const Row = isUrgentTask ? UrgentRowShell : 'div';
                        const rowProps = isUrgentTask
                          ? {
                              deadlineAt: task.urgentDeadlineAt,
                              estimateMinutes: urgentEstimateMinutes,
                              className: `group ${isMappingTarget ? 'map-target-blink' : ''}`,
                            }
                          : {
                              className: `group ${rowClass} ${isMappingTarget ? 'map-target-blink' : ''}`,
                            };
                        return (
                          <Row key={task.id} {...rowProps}>
                            <div
                              className={`${
                                wideHome
                                  ? sectionRowPadClass
                                  : 'flex flex-col items-stretch gap-1 px-2.5 py-2 min-w-0'
                              } cursor-pointer`}
                              onClick={() => {
                                if (editingTaskId) {
                                  setEditingTaskId(null);
                                  return;
                                }
                                setViewingPickTodayTask(task);
                              }}
                            >
                              <div className={wideHome ? 'contents' : 'flex items-start gap-2.5 min-w-0 w-full'}>
                              <div className="flex-1 min-w-0">
                                {editingTaskId === task.id ? (
                                  <div onClick={(e) => e.stopPropagation()}>
                                    <QuickTaskComposer
                                      placeholder="Task title"
                                      submitLabel="Save"
                                      saving={savingQuickTask}
                                      initialTitle={getTaskListLabel(task)}
                                      initialMinutes={Number(task.estimateTimeInMinutes) || 15}
                                      onSubmit={(payload) => handleQuickEditTask(task, payload)}
                                      onCancel={() => setEditingTaskId(null)}
                                    />
                                  </div>
                                ) : (
                                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 w-full min-w-0">
                                    <div className="flex items-center gap-1.5 min-w-0 flex-1 basis-[12rem]">
                                    <span className={itemIconWrapClass(false)}>
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                      </svg>
                                    </span>
                                    <span
                                      className={`min-w-0 flex-1 text-left text-[15px] font-bold tracking-tight text-gray-900 dark:text-white leading-snug ${
                                        isExpanded ? 'whitespace-normal break-words' : 'truncate'
                                      }`}
                                      title={label}
                                    >
                                      {label}
                                    </span>
                                    {!viewingTargetUserId && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          beginInlineEditTask(task);
                                        }}
                                        className="flex-shrink-0 p-1 rounded-md text-gray-400 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 focus:pointer-events-auto hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                                        title="Rename task"
                                        aria-label="Rename task"
                                      >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 11l6.232-6.232a2.5 2.5 0 113.536 3.536L12.536 14.536a2 2 0 01-.878.513l-3.09.883a.5.5 0 01-.62-.62l.883-3.09a2 2 0 01.513-.878z" />
                                        </svg>
                                      </button>
                                    )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                                    {taskShowsWorkflowTag(task, knownWorkflowIds) ? (
                                      <>
                                      <span
                                        className="flex-shrink-0 px-1.5 py-0.5 rounded-md text-[10px] font-semibold border border-indigo-200/90 bg-indigo-50 text-indigo-800 dark:border-indigo-700/60 dark:bg-indigo-900/25 dark:text-indigo-200"
                                        title="Connected to a workflow"
                                      >
                                        Workflow
                                      </span>
                                      {(() => {
                                        const source = resolveTaskWorkflowChannelLabel(task, {
                                          channels,
                                          directMessages,
                                          users,
                                          currentUserId,
                                          messagesByChannel: messages,
                                          workflowItems: assignedWorkflows,
                                        });
                                        if (!source?.label) return null;
                                        return (
                                          <span
                                            className={`flex-shrink-0 px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${
                                              source.kind === 'dm'
                                                ? 'border border-blue-200/90 bg-blue-50 text-blue-800 dark:border-blue-700/60 dark:bg-blue-900/25 dark:text-blue-200'
                                                : 'border border-emerald-200/90 bg-emerald-50 text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-900/25 dark:text-emerald-200'
                                            }`}
                                            title={source.label}
                                          >
                                            {source.label}
                                          </span>
                                        );
                                      })()}
                                      </>
                                    ) : null}
                                    {isUrgentTask ? (
                                      <span className="inline-flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                        <UrgentTag
                                          deadlineAt={task.urgentDeadlineAt}
                                          estimateMinutes={urgentEstimateMinutes}
                                        />
                                        <UrgentCountdownBadge
                                          deadlineAt={task.urgentDeadlineAt}
                                          estimateMinutes={urgentEstimateMinutes}
                                          compact
                                        />
                                      </span>
                                    ) : null}
                                    {assigneeNameForTask(task, users, currentUserId) ? (
                                      <span
                                        className="flex-shrink-0 px-1.5 py-0.5 rounded-md text-[10px] font-semibold border border-gray-200/90 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700/40 dark:text-gray-300"
                                        title={`Assigned to ${assigneeNameForTask(task, users, currentUserId)}`}
                                      >
                                        {assigneeNameForTask(task, users, currentUserId)}
                                      </span>
                                    ) : null}
                                    {wideHome && <TaskEstimateBadge minutes={totalMinutes} />}
                                    {wideHome && editingTaskId !== task.id && subtasks.length === 0 && (
                                      <WorkItemTimer
                                        targetType="TASK"
                                        targetId={task.id}
                                        noun="task"
                                        disabled={!canUseWorkTimer}
                                      />
                                    )}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAddingTask(false);
                                  setEditingTaskId(null);
                                  setAddingSubtaskForId(task.id);
                                  setExpandedTaskIds((prev) => {
                                    const next = new Set(prev);
                                    next.add(task.id);
                                    return next;
                                  });
                                }}
                                className="flex-shrink-0 p-1.5 rounded-lg text-primary-600 dark:text-primary-400 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 focus:pointer-events-auto hover:bg-primary-50 dark:hover:bg-primary-900/30"
                                title="Add subtask"
                                aria-label="Add subtask"
                              >
                                <PlusIcon className="w-3.5 h-3.5" />
                              </button>
                              <span className="inline-flex items-center gap-1.5 flex-shrink-0 self-start h-7">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTaskMapping(task);
                                }}
                                className={`flex-shrink-0 p-1.5 rounded-lg transition-all duration-150 ${
                                  isMappingTarget
                                    ? 'opacity-100 pointer-events-auto bg-yellow-400 text-yellow-900 ring-2 ring-yellow-500'
                                    : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 focus:pointer-events-auto text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200'
                                }`}
                                title={isMappingTarget ? 'Cancel mapping' : 'Map chat to this task'}
                                aria-label={isMappingTarget ? 'Cancel mapping' : 'Map chat to this task'}
                                aria-pressed={isMappingTarget}
                              >
                                <MapLinkIcon />
                              </button>
                              {formatTimeBack(task.createdAt) ? (
                                <span
                                  className="inline-flex items-center text-[10px] leading-none font-medium text-gray-400 dark:text-gray-500 whitespace-nowrap"
                                  title={task.createdAt ? new Date(task.createdAt).toLocaleString() : ''}
                                >
                                  {formatTimeBack(task.createdAt)}
                                </span>
                              ) : null}
                              </span>
                              {wideHome && <TaskMiniProgress percent={progressPct} />}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTaskExpansion(task.id);
                                }}
                                className={sectionActionIconClass}
                                title={isExpanded ? 'Hide task details' : 'Show task details'}
                                aria-expanded={isExpanded}
                              >
                                <ChevronDownIcon
                                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                                    isExpanded ? '' : '-rotate-90'
                                  }`}
                                />
                              </button>
                              </div>
                              {!wideHome && editingTaskId !== task.id && (
                                <div className="flex items-center gap-2 pl-10 flex-wrap">
                                  <TaskEstimateBadge minutes={totalMinutes} />
                                  <TaskMiniProgress percent={progressPct} />
                                  {subtasks.length === 0 && (
                                    <WorkItemTimer
                                      targetType="TASK"
                                      targetId={task.id}
                                      noun="task"
                                      disabled={!canUseWorkTimer}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                            {isExpanded && (subtasks.length > 0 || addingSubtaskForId === task.id) && (
                              <div className="w-full min-w-0 border-t border-gray-100/80 dark:border-gray-700/50">
                                {subtasks.map((subtask) => (
                                  <TaskSubtaskItem
                                    key={subtask.id}
                                    subtask={subtask}
                                    busy={savingQuickTask}
                                    onOpen={() => setViewingPickTodayTask(subtask)}
                                    onEdit={viewingTargetUserId ? undefined : (payload) => handleQuickEditSubtask(task, subtask, payload)}
                                    onEditTime={viewingTargetUserId ? undefined : () => handleEditSubtaskTime(task, subtask)}
                                    onDelete={canDeleteTask ? () => handleDeleteSubtask(task, subtask) : undefined}
                                    onComplete={() => handleCompleteSubtask(task, subtask)}
                                    timerDisabled={!canUseWorkTimer}
                                  />
                                ))}
                                {addingSubtaskForId === task.id && (
                                  <QuickTaskComposer
                                    compact
                                    placeholder="Subtask title"
                                    submitLabel="Add"
                                    saving={savingQuickTask}
                                    onSubmit={(payload) => handleQuickCreateSubtask(task, payload)}
                                    onCancel={() => setAddingSubtaskForId(null)}
                                  />
                                )}
                              </div>
                            )}
                            {isExpanded && chatsVisible && showExpandBubble && (
                              <div
                                className="border-t border-gray-100 dark:border-gray-700/80 px-2.5 py-2 min-w-0 max-w-full overflow-x-hidden overflow-y-visible animate-fadeIn"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <TaskExpandBubble
                                  task={task}
                                  linkedMessage={taskMessagesByTaskId[task.id]}
                                  mappedTasks={mappedChats}
                                  taskMessagesByTaskId={taskMessagesByTaskId}
                                  loadingMessage={loadingTaskMessageIds.has(task.id)}
                                  loadingMappedTasks={loadingMappedTasksByTaskId[task.id]}
                                  loadingTaskMessageIds={loadingTaskMessageIds}
                                  users={users}
                                  onViewImage={(images, index) => setViewingWorkflowGallery({ images, index })}
                                  onDownload={handleWorkflowAttachmentDownload}
                                  enableChatActions
                                  onChatClick={handleTaskChatClick}
                                  onRemoveChat={handleRemoveTaskChat}
                                  removingChatKey={removingChatKey}
                                  hiddenChatBubbleKeys={hiddenChatBubbleKeys}
                                />
                              </div>
                            )}
                            {isExpanded && (
                              <div
                                className="flex items-center justify-end gap-3 w-full min-w-0 px-3.5 py-2 border-t border-gray-100/80 dark:border-gray-700/50"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {showExpandBubble && (
                              <button
                                type="button"
                                    onClick={() => toggleTaskChats(task.id)}
                                    className="mr-auto text-[11px] font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                                  >
                                    {chatsVisible ? 'Hide chats' : 'Show all chats'}
                              </button>
                                )}
                              <button
                                type="button"
                                onClick={(e) => handleCompletePickTodayTask(e, task)}
                                disabled={completingTaskId === task.id || !canCompleteParent}
                                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 disabled:opacity-50"
                                  title={!canCompleteParent ? unfinishedSubtasksMessage(unfinishedSubtasks) : undefined}
                              >
                                  <CompleteTaskIcon className="w-3.5 h-3.5" />
                                  {completingTaskId === task.id ? 'Completing…' : 'Complete task'}
                              </button>
                                {canDeleteTask && (
                                  <>
                                    <span className="text-[11px] font-semibold text-gray-300 dark:text-gray-600" aria-hidden="true">|</span>
                                    <button
                                      type="button"
                                      onClick={() => handleDeletePendingTask(task, subtasks)}
                                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300"
                                    >
                                      <DeleteTaskIcon className="w-3.5 h-3.5" />
                                      Delete Task
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </Row>
                        );
                      })
                  )}
                  {addingTask && (
                    <QuickTaskComposer
                      placeholder="Task title"
                      submitLabel="Add"
                      saving={savingQuickTask}
                      showSpace
                      onSubmit={handleQuickCreateTask}
                      onCancel={() => setAddingTask(false)}
                    />
                  )}
                </>
              )}
              {(isPastDayView || activeTab === 'completed') && (
                <>
                  {completedListItemCount === 0 ? (
                    !isPastDayView ? (
                    <p className={`${emptyTitleClass} px-4 py-8 text-center`}>
                      {completedTodayTasks.length > 0 && taskKindFilterEmptyMessage
                        ? taskKindFilterEmptyMessage
                        : `No tasks completed ${isSelectedToday ? 'today' : 'this day'}`}
                    </p>
                    ) : null
                  ) : (
                    <>
                  {displayedCompletedProductivityEntries.map((entry) => {
                      const label = formatProductivityEntryLabel(entry);
                      const mins = Number(entry.creditedMinutes) || 0;
                      return (
                        <div
                          key={entry.id || `${entry.taskId}-${entry.fromPercent}-${entry.toPercent}-${entry.createdAt}`}
                          className={`${sectionListItemClass} opacity-90`}
                        >
                          <div className={wideHome ? 'flex items-center gap-2.5 px-3.5 py-3 min-w-0' : 'flex flex-col items-stretch gap-1 px-2.5 py-2 min-w-0'}>
                            <div className={wideHome ? 'contents' : 'flex items-center gap-2.5 min-w-0 w-full'}>
                              <div className="flex items-center gap-2.5 flex-1 min-w-0 overflow-hidden text-left">
                                <span className={itemIconWrapClass(true)}>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </span>
                                <span className={`${itemTitleMutedClass} truncate`} title={label}>
                                  {label}
                                </span>
                                {wideHome && <TaskEstimateBadge minutes={mins} />}
                              </div>
                              {wideHome && <TaskMiniProgress percent={Number(entry.toPercent) || 0} />}
                            </div>
                            {!wideHome && (
                              <div className="flex items-center gap-2 pl-10">
                                <TaskEstimateBadge minutes={mins} />
                                <TaskMiniProgress percent={Number(entry.toPercent) || 0} />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  {displayedCompletedTodayTasks.map((task) => {
                        const isExpanded = expandedTaskIds.has(task.id);
                        const chatsVisible = taskChatsVisibleIds.has(task.id);
                        const label = getTaskListLabel(task);
                        const mappedItems = mappedTasksByTaskId[task.id] || [];
                        const subtasks = mappedItems.filter(isNamedSubtask);
                        const mappedChats = mappedItems.filter((item) => !isNamedSubtask(item));
                        const totalMinutes = taskActualMinutes(task, mappedItems);
                        const showExpandBubble = Boolean(task.messageId) || hasTaskDescription(task) || mappedChats.length > 0;
                        return (
                          <div
                            key={task.id}
                            className={`${sectionListItemClass} opacity-80`}
                          >
                            <div className={wideHome ? 'flex items-center gap-2.5 px-3.5 py-3 min-w-0' : 'flex flex-col items-stretch gap-1 px-2.5 py-2 min-w-0'}>
                              <div className={wideHome ? 'contents' : 'flex items-center gap-2.5 min-w-0 w-full'}>
                              <button
                                type="button"
                                onClick={() => setViewingPickTodayTask(task)}
                                className="flex items-center gap-2.5 flex-1 min-w-0 overflow-hidden text-left rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/25"
                              >
                                <span className={itemIconWrapClass(true)}>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                </span>
                                <span className={`${itemTitleMutedClass} ${isExpanded ? 'whitespace-normal break-words' : 'truncate'}`} title={label}>
                                  {label}
                                </span>
                                {taskShowsWorkflowTag(task, knownWorkflowIds) ? (
                                  <span
                                    className="flex-shrink-0 px-1.5 py-0.5 rounded-md text-[10px] font-semibold border border-indigo-200/90 bg-indigo-50 text-indigo-800 dark:border-indigo-700/60 dark:bg-indigo-900/25 dark:text-indigo-200"
                                    title="Connected to a workflow"
                                  >
                                    Workflow
                                  </span>
                                ) : null}
                                {taskShowsWorkflowTag(task, knownWorkflowIds) ? (() => {
                                  const source = resolveTaskWorkflowChannelLabel(task, {
                                    channels,
                                    directMessages,
                                    users,
                                    currentUserId,
                                    messagesByChannel: messages,
                                    workflowItems: assignedWorkflows,
                                  });
                                  if (!source?.label) return null;
                                  return (
                                    <span
                                      className={`flex-shrink-0 px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${
                                        source.kind === 'dm'
                                          ? 'border border-blue-200/90 bg-blue-50 text-blue-800 dark:border-blue-700/60 dark:bg-blue-900/25 dark:text-blue-200'
                                          : 'border border-emerald-200/90 bg-emerald-50 text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-900/25 dark:text-emerald-200'
                                      }`}
                                      title={source.label}
                                    >
                                      {source.label}
                                    </span>
                                  );
                                })() : null}
                                {wideHome && <TaskEstimateBadge minutes={totalMinutes} />}
                              </button>
                              {wideHome && <TaskMiniProgress percent={100} />}
                              {!viewingTargetUserId && (
                                <>
                                <button
                                  type="button"
                                  onClick={(e) => handleEditCompletedTaskTime(e, task)}
                                  disabled={completingTaskId === task.id}
                                  className="flex-shrink-0 text-[11px] font-semibold px-2 py-1 rounded-md text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-950/30 disabled:opacity-50"
                                  title="Edit time spent"
                                >
                                  Edit time
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => handleUndoCompleteTask(e, task)}
                                  disabled={completingTaskId === task.id}
                                  className="flex-shrink-0 text-[11px] font-semibold px-2 py-1 rounded-md text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30 disabled:opacity-50"
                                  title="Undo completion and revert time spent"
                                >
                                  {completingTaskId === task.id ? 'Undoing…' : 'Undo'}
                                </button>
                                </>
                              )}
                              {subtasks.length > 0 && (
                              <button
                                type="button"
                                onClick={() => toggleTaskExpansion(task.id)}
                                  className={sectionActionIconClass}
                                  title={isExpanded ? 'Hide subtasks' : 'Show subtasks'}
                                  aria-expanded={isExpanded}
                              >
                                <ChevronDownIcon
                                    className={`w-3.5 h-3.5 transition-transform duration-200 ${
                                    isExpanded ? '' : '-rotate-90'
                                  }`}
                                />
                              </button>
                              )}
                            </div>
                              {!wideHome && (
                                <div className="flex items-center gap-2 pl-10">
                                  <TaskEstimateBadge minutes={totalMinutes} />
                                  <TaskMiniProgress percent={100} />
                                </div>
                                    )}
                                 </div>
                            {isExpanded && subtasks.length > 0 && (
                              <div className="w-full min-w-0 border-t border-gray-100/80 dark:border-gray-700/50">
                                {subtasks.map((subtask) => (
                                  <TaskSubtaskItem
                                    key={subtask.id}
                                    subtask={subtask}
                                    readOnly
                                    onEditTime={
                                      viewingTargetUserId
                                        ? undefined
                                        : () => handleEditSubtaskTime(task, subtask)
                                    }
                                  />
                                ))}
                              </div>
                            )}
                            {isExpanded && chatsVisible && showExpandBubble && (
                              <div
                                className="border-t border-gray-100 dark:border-gray-700/80 px-2.5 py-2 min-w-0 max-w-full overflow-x-hidden overflow-y-visible animate-fadeIn"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <TaskExpandBubble
                                  task={task}
                                  linkedMessage={taskMessagesByTaskId[task.id]}
                                  mappedTasks={mappedChats}
                                  taskMessagesByTaskId={taskMessagesByTaskId}
                                  loadingMessage={loadingTaskMessageIds.has(task.id)}
                                  loadingMappedTasks={loadingMappedTasksByTaskId[task.id]}
                                  loadingTaskMessageIds={loadingTaskMessageIds}
                                  users={users}
                                  onViewImage={(images, index) => setViewingWorkflowGallery({ images, index })}
                                  onDownload={handleWorkflowAttachmentDownload}
                                />
                              </div>
                            )}
                            {isExpanded && showExpandBubble && (
                              <div
                                className="flex items-center px-3.5 py-2 border-t border-gray-100/80 dark:border-gray-700/50"
                                onClick={(e) => e.stopPropagation()}
                              >
                              <button
                                type="button"
                                  onClick={() => toggleTaskChats(task.id)}
                                  className="text-[11px] font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                                >
                                  {chatsVisible ? 'Hide chats' : 'Show all chats'}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}
              {isPastDayView && !pickTodayLoading && displayedPickTodayTasks.length === 0 && completedListItemCount === 0 && !addingTask && (
                <p className={`${emptyTitleClass} px-4 py-8 text-center`}>
                  {tasksForSelectedDay.length + completedTodayTasks.length > 0 && taskKindFilterEmptyMessage
                    ? taskKindFilterEmptyMessage
                    : 'No tasks for this day'}
                </p>
              )}
                    </div>
                  )}
            </div>
          </>
        )}

              {(showPanelWorkflows || showHomeWorkflows) && (
              <>
                  {(!homeLayout || isPastDayView) && <div className="border-t border-transparent my-2.5" aria-hidden="true" />}
                  <div className={`${sectionCardClass}${isPastDayView ? ' mb-3' : ''}`}>
                    {homeLayout && isPastDayView && (
                    <div className={sectionCardHeaderClass}>
                      <button
                        type="button"
                        onClick={() => setWorkflowsExpanded((v) => !v)}
                        aria-label={workflowsListOpen ? 'Collapse workflows' : 'Expand workflows'}
                        aria-expanded={workflowsListOpen}
                        className="min-w-0 flex-1 flex items-center gap-2 text-left rounded-md px-1 py-0.5 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
                      >
                        {workflowsListOpen ? (
                          <ChevronUpIcon className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                        ) : (
                          <ChevronDownIcon className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                        )}
                        <span className="min-w-0 flex-1 flex items-center gap-2">
                          <span className={sectionHeadingClass(false)}>Workflows</span>
                          <span className={sectionCountPillClass(false)}>
                            {assignedWorkflowsLoading
                              ? '…'
                              : pendingWorkflowsCount + completedWorkflowsCount}
                          </span>
                        </span>
                      </button>
                    </div>
                    )}
                    {!homeLayout && (
                    <div className={sectionCardHeaderClass}>
                              <button
                                type="button"
                      onClick={() => setWorkflowsExpanded((v) => !v)}
                      aria-label={workflowsListOpen ? 'Collapse workflows list' : 'Expand workflows list'}
                      aria-expanded={workflowsListOpen}
                      className="min-w-0 flex-1 flex items-center gap-2 text-left rounded-md px-1 py-0.5 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
                              >
                                <ChevronDownIcon
                          className={`w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                            workflowsListOpen ? '' : '-rotate-90'
                                  }`}
                                />
                        <span className="min-w-0 flex-1 flex items-center gap-2">
                          <span className={sectionHeadingClass(!isPastDayView && activeTab === 'completed')}>
                            {isPastDayView ? 'Workflows' : (activeTab === 'completed' ? 'Completed Workflows' : 'Pending approval Workflows')}
                          </span>
                          <span className={sectionCountPillClass(!isPastDayView && activeTab === 'completed')}>
                            {assignedWorkflowsLoading
                              ? '…'
                              : isPastDayView
                                ? pendingWorkflowsCount + completedWorkflowsCount
                              : activeTab === 'completed'
                                ? completedWorkflowsCount
                                : displayedPendingWorkflows.length}
                          </span>
                        </span>
                              </button>
                    {!isPastDayView && magicPickButton()}
                              <button
                                type="button"
                      onClick={() => hideSection('workflows')}
                      className={sectionFocusButtonClass}
                      title="Hide workflows"
                      aria-label="Hide workflows section"
                    >
                      <HideSectionIcon />
                    </button>
                    </div>
                    )}
                    <div className="flex items-center gap-1 px-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setWorkflowSourceTab('workloop')}
                        className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg ${
                          workflowSourceTab === 'workloop'
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        Workflows
                      </button>
                      <button
                        type="button"
                        onClick={() => setWorkflowSourceTab('jira')}
                        className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg ${
                          workflowSourceTab === 'jira'
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        JIRA
                      </button>
                      {workflowSourceTab === 'workloop' ? (
                        <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-700 dark:text-gray-300">
                          <span className="whitespace-nowrap">Sort</span>
                          <select
                            value={workflowSortOrder}
                            onChange={(event) => setWorkflowSortOrder(event.target.value)}
                            className="px-2 py-1 text-[11px] rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="newest">Newest first</option>
                            <option value="oldest">Oldest first</option>
                          </select>
                        </label>
                      ) : null}
                    </div>
                    {workflowsListOpen && (
                    <div className={sectionCardBodyClass}>
                    {workflowSourceTab === 'jira' ? (
                      <JiraAssignedTicketsList compact />
                    ) : (
                    <>
                    {(isPastDayView || activeTab === 'completed') && (
                      !displayedCompletedTodayWorkflows.length ? (
                        isPastDayView ? null : (
                        <p className={`${emptyTitleClass} px-4 py-8 text-center`}>
                          No workflows completed {isSelectedToday ? 'today' : 'this day'}
                        </p>
                        )
                      ) : (
                        displayedCompletedTodayWorkflows.map((item) => {
                          const label = item.workflowTitle || item.label || getWorkflowTicketText(item.content);
                          const timeLabel = formatEstimateTime(item.completionTimeInMinutes);
                          return (
                            <div
                              key={item.messageId}
                              className={`${sectionListItemClass} opacity-80 cursor-pointer`}
                              onClick={() => handleWorkflowTicketClick(item)}
                            >
                              <div className={sectionRowPadClass}>
                                <span className={itemIconWrapClass(true)}>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                </span>
                                <span className={`${itemTitleMutedClass} truncate`} title={label}>
                                  {label}
                                </span>
                                {timeLabel ? (
                                  <TaskEstimateBadge minutes={item.completionTimeInMinutes} title="Time spent" />
                                ) : null}
                                {!viewingTargetUserId && (
                                  <button
                                    type="button"
                                    onClick={(e) => handleUndoCompleteWorkflow(e, item)}
                                    disabled={completingWorkflowId === item.messageId}
                                    className="flex-shrink-0 text-[11px] font-semibold px-2 py-1 rounded-md text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30 disabled:opacity-50"
                                    title="Undo completion and revert time spent"
                                  >
                                    {completingWorkflowId === item.messageId ? 'Undoing…' : 'Undo'}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )
                    )}
                    {(isPastDayView || activeTab !== 'completed') && (
                    <>
                    {workflowChipFilter && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-primary-50/70 dark:bg-primary-900/20">
                        <span className="text-[11px] text-gray-600 dark:text-gray-300 min-w-0 truncate">
                          Filtered by <span className="font-semibold text-gray-800 dark:text-gray-100">{workflowChipFilter.label}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => setWorkflowChipFilter(null)}
                          className="ml-auto flex-shrink-0 text-[11px] font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          Clear
                              </button>
                            </div>
                    )}
                    {assignedWorkflowsLoading ? (
                      <p className={`${emptyTitleClass} px-4 py-8 text-center`}>Loading workflows...</p>
                    ) : pendingAssignedWorkflows.length === 0 ? (
                      isPastDayView ? null : (
                      <p className={`${emptyTitleClass} px-4 py-8 text-center`}>
                        No workflows assigned to you
                      </p>
                      )
                    ) : filteredAssignedWorkflows.length === 0 ? (
                      <p className={`${emptyTitleClass} px-4 py-8 text-center`}>
                        No workflows match this filter
                      </p>
                    ) : (
                        displayedPendingWorkflows.map((item) => {
                          const isClosed = Object.values(item.workflowByCategory || {}).includes(WORKFLOW_CLOSED_STATUS_ID);
                          const isUrgent = isOpenUrgentWorkflow(item);
                          const isExpanded = expandedWorkflowIds.has(item.messageId);
                          const ticketText = item.workflowTitle || getWorkflowTicketText(item.content);
                          const fullTicketText = item.content?.trim() || 'Workflow item';
                          const isWorkflowMappingTarget = chatMappingTarget?.type === 'workflow'
                            && chatMappingTarget.messageId === item.messageId;
                          const mappedWorkflowItems = workflowMappedTasksByMessageId[item.messageId] || [];
                          const linkedTasks = workflowLinkedTasksFrom(mappedWorkflowItems);
                          const unfinishedLinked = unfinishedWorkflowLinkedCount(
                            linkedTasks,
                            mappedTasksByTaskId,
                            item.workflowTitle,
                          );
                          const canCompleteWorkflow = unfinishedLinked === 0;
                          const displayLinkedTasks = visibleWorkflowLinkedTasks(
                            mappedWorkflowItems,
                            item.messageId,
                            ticketText,
                          );
                          const workflowTaskTotal = displayLinkedTasks.length;
                          const workflowTaskDone = displayLinkedTasks.filter((task) => (
                            String(task?.status || task?.statusString || '').toUpperCase() === 'COMPLETED'
                            || isTaskTimeRecorded(task, mappedTasksByTaskId[task.id] || [])
                          )).length;
                          const workflowProgressPct = isClosed || (workflowTaskTotal > 0 && workflowTaskDone === workflowTaskTotal)
                            ? 100
                            : (workflowTaskTotal === 0 ? 0 : Math.round((workflowTaskDone / workflowTaskTotal) * 100));
                          const workflowAge = formatTimeBack(item.createdAt || item.timestamp);
                          const urgentEstimateMinutes = (() => {
                            const mins = linkedTasks
                              .map((task) => Number(task?.estimateTimeInMinutes))
                              .filter((n) => Number.isFinite(n) && n > 0);
                            if (!mins.length) return undefined;
                            return Math.max(...mins);
                          })();
                          const typeId = item.workflowByCategory?.['work-item-type'];
                          const statusId = item.workflowByCategory?.['workflow-status'];
                          const typeDisplay = typeId ? getReactionDisplay(typeId) : null;
                          const statusDisplay = statusId ? getReactionDisplay(statusId) : null;
                          const selfIdForSource = viewingTargetUserId || currentUserId;
                          const source = resolveWorkflowSourceLabel(
                            item,
                            [...channels, ...directMessages],
                            users,
                            selfIdForSource,
                          );
                          const typeFilterValue = typeId || '__workflow__';
                          const typeFilterLabel = typeDisplay?.label || 'Workflow';
                          const typeActive = workflowChipFilter?.kind === 'type' && workflowChipFilter.value === typeFilterValue;
                          const statusActive = workflowChipFilter?.kind === 'status' && workflowChipFilter.value === statusId;
                          const sourceActive = workflowChipFilter?.kind === 'source' && workflowChipFilter.value === source.label;
                          const trackActive = workflowChipFilter?.kind === 'track' && workflowChipFilter.value === item.workflowTrackId;
                          const Row = isUrgent && !isClosed ? UrgentRowShell : 'div';
                          const rowProps = isUrgent && !isClosed
                            ? {
                                deadlineAt: item.urgentDeadlineAt,
                                estimateMinutes: urgentEstimateMinutes,
                                className: `group ${isClosed ? 'opacity-75' : ''} ${isWorkflowMappingTarget ? 'map-target-blink' : ''}`,
                              }
                            : {
                                className: `group ${sectionListItemClass} ${isClosed ? 'opacity-75' : ''} ${isWorkflowMappingTarget ? 'map-target-blink' : ''}`,
                              };
                          return (
                            <Row key={item.messageId} {...rowProps}>
                              <div className={sectionRowPadClass}>
                                <div
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => handleWorkflowTicketClick(item)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleWorkflowTicketClick(item);
                                    }
                                  }}
                                  className="min-w-0 flex-1 text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/25 rounded-md"
                                >
                                  {/* Row 1: type · status · #channel/@dm · Track ID */}
                                  <div className="flex flex-wrap items-center gap-1 min-w-0">
                                    {typeDisplay ? (
                                      <button
                                        type="button"
                                        className={`${workflowFilterChipClass(typeActive)} ${chipToneClass(typeDisplay.tone)}`}
                                        title={`Filter by ${typeDisplay.label}`}
                                        aria-pressed={typeActive}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleWorkflowChipFilter('type', typeFilterValue, typeFilterLabel);
                                        }}
                                      >
                                        <span aria-hidden="true">{typeDisplay.emoji}</span>
                                        <span className="truncate">{typeDisplay.label}</span>
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className={`${workflowFilterChipClass(typeActive)} ${chipToneClass('slate')}`}
                                        title="Filter by Workflow"
                                        aria-pressed={typeActive}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleWorkflowChipFilter('type', typeFilterValue, typeFilterLabel);
                                        }}
                                      >
                                        Workflow
                                      </button>
                                    )}
                                    {statusDisplay && (
                                      <button
                                        type="button"
                                        className={`${workflowFilterChipClass(statusActive)} ${chipToneClass(statusDisplay.tone || 'teal')}`}
                                        title={`Filter by ${statusDisplay.label}`}
                                        aria-pressed={statusActive}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleWorkflowChipFilter('status', statusId, statusDisplay.label);
                                        }}
                                      >
                                        <span aria-hidden="true">{statusDisplay.emoji}</span>
                                        <span className="truncate">{statusDisplay.label}</span>
                                      </button>
                                    )}
                                    {isUrgent && !isClosed ? (
                                      <UrgentTag
                                        deadlineAt={item.urgentDeadlineAt}
                                        estimateMinutes={urgentEstimateMinutes}
                                      />
                                    ) : null}
                                    <button
                                      type="button"
                                      className={`${workflowFilterChipClass(sourceActive)} ${
                                        source.kind === 'dm' ? chipToneClass('blue') : chipToneClass('green')
                                      }`}
                                      title={`Filter by ${source.label}`}
                                      aria-pressed={sourceActive}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleWorkflowChipFilter('source', source.label, source.label);
                                      }}
                                    >
                                      {source.label}
                                    </button>
                                    {item.workflowTrackId ? (
                                      <button
                                        type="button"
                                        className={`${workflowFilterChipClass(trackActive)} ${chipToneClass('indigo')}`}
                                        title={item.workflowTrackLink
                                          ? `Filter by ${item.workflowTrackId} (open link from chat)`
                                          : `Filter by ${item.workflowTrackId}`}
                                        aria-pressed={trackActive}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleWorkflowChipFilter('track', item.workflowTrackId, item.workflowTrackId);
                                        }}
                                      >
                                        {item.workflowTrackId}
                                      </button>
                                    ) : null}
                                    {isClosed ? (
                                      <span className="text-[10px] text-gray-400 italic flex-shrink-0">Done</span>
                                    ) : null}
                                 </div>
                                  {/* Row 2: name (N tasks) stays with the title */}
                                  <p
                                    className="mt-1.5 min-w-0 truncate text-[15px] font-bold tracking-tight text-gray-900 dark:text-white leading-snug"
                                    title={`${fullTicketText} (${workflowTaskTotal} ${workflowTaskTotal === 1 ? 'task' : 'tasks'})`}
                                  >
                                    {ticketText}
                                    <span className="ml-1 text-[10px] font-medium text-gray-400 dark:text-gray-500">
                                      ({workflowTaskTotal} {workflowTaskTotal === 1 ? 'task' : 'tasks'})
                                    </span>
                                  </p>
                                  {isUrgent && !isClosed && item.urgentDeadlineAt ? (
                                    <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
                                      <UrgentCountdownBadge
                                        deadlineAt={item.urgentDeadlineAt}
                                        estimateMinutes={urgentEstimateMinutes}
                                        compact
                                      />
                                    </div>
                                  ) : null}
                              </div>
                                <div className="flex items-center flex-shrink-0 ml-3">
                                <div
                                  className="inline-flex flex-col items-end justify-center gap-1 mr-3 px-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {workflowAge ? (
                                    <span className="text-[10px] font-medium leading-none text-gray-400 dark:text-gray-500 whitespace-nowrap">
                                      {workflowAge}
                                    </span>
                                  ) : null}
                                  <TaskMiniProgress percent={workflowProgressPct} />
                                </div>
                                <div className="flex items-center gap-0.5">
                                {!isClosed && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAddingWorkflowTaskForId(item.messageId);
                                    setExpandedWorkflowIds((prev) => {
                                      const next = new Set(prev);
                                      next.add(item.messageId);
                                      return next;
                                    });
                                  }}
                                  className="inline-flex items-center justify-center p-1.5 rounded-lg text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30"
                                  title="Add task"
                                  aria-label="Add task"
                                >
                                  <PlusIcon className="w-3.5 h-3.5" />
                                </button>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleWorkflowMapping(item);
                                  }}
                                  className={`inline-flex items-center justify-center p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors ${
                                    isWorkflowMappingTarget
                                      ? 'bg-yellow-400 text-yellow-900 ring-2 ring-yellow-500 hover:bg-yellow-400 hover:text-yellow-900'
                                      : ''
                                  }`}
                                  title={isWorkflowMappingTarget ? 'Cancel mapping' : 'Map chat to this workflow'}
                                  aria-label={isWorkflowMappingTarget ? 'Cancel mapping' : 'Map chat to this workflow'}
                                  aria-pressed={isWorkflowMappingTarget}
                                >
                                  <MapLinkIcon />
                                </button>
                                {!isClosed && (
                                  <button
                                    type="button"
                                    onClick={(e) => handleCompleteWorkflow(e, item)}
                                    disabled={completingWorkflowId === item.messageId || !canCompleteWorkflow}
                                    className={`inline-flex items-center justify-center p-1.5 rounded-lg transition-all duration-150 disabled:cursor-not-allowed ${
                                      completingWorkflowId === item.messageId
                                        ? 'opacity-100 pointer-events-auto bg-green-500 text-white'
                                        : canCompleteWorkflow
                                          ? 'opacity-100 pointer-events-auto text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-900/30 dark:hover:text-emerald-300'
                                          : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 focus:pointer-events-auto text-gray-400 hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-emerald-900/30 dark:hover:text-emerald-300'
                                    }`}
                                    title={!canCompleteWorkflow ? 'Finish all workflow tasks first' : 'Close workflow'}
                                    aria-label="Mark workflow as complete"
                                  >
                                    {completingWorkflowId === item.messageId ? (
                                      <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                      </svg>
                                    ) : (
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleWorkflowExpansion(item.messageId);
                                  }}
                                  className="inline-flex items-center justify-center p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                                  title={isExpanded ? 'Hide description' : 'Show description'}
                                  aria-expanded={isExpanded}
                                  aria-label={isExpanded ? 'Hide workflow description' : 'Show workflow description'}
                                >
                                  <ChevronDownIcon
                                    className={`w-3.5 h-3.5 transition-transform duration-200 ${
                                      isExpanded ? '' : '-rotate-90'
                                    }`}
                                  />
                                </button>
                                </div>
                                </div>
                          </div>
                              {(isExpanded || addingWorkflowTaskForId === item.messageId) && (
                                <div className="w-full min-w-0 border-t border-gray-100/80 dark:border-gray-700/50">
                                  {displayLinkedTasks.map((task) => (
                                    <TaskSubtaskItem
                                      key={task.id}
                                      subtask={task}
                                      busy={savingQuickTask}
                                      indent
                                      assigneeLabel={assigneeNameForTask(task, users, currentUserId)}
                                      onOpen={() => setViewingPickTodayTask(task)}
                                      onEdit={(payload) => handleQuickEditTask(task, payload)}
                                      onClone={isClosed ? undefined : () => handleCloneWorkflowTask(item, task)}
                                      onDelete={sameUserId(task.createdByUserId, currentUserId)
                                        ? () => handleDeletePendingTask(task, (mappedTasksByTaskId[task.id] || []).filter(isNamedSubtask))
                                        : undefined}
                                      onComplete={isClosed ? undefined : () => handleCompletePickTodayTask({ stopPropagation() {} }, task)}
                                      onEditTime={
                                        viewingTargetUserId
                                          ? undefined
                                          : () => handleEditCompletedTaskTime({ stopPropagation() {} }, task)
                                      }
                                      timerDisabled={!canUseWorkTimer || isClosed}
                                      readOnly={isClosed}
                                    />
                                  ))}
                                  {!isClosed && (
                                    addingWorkflowTaskForId === item.messageId ? (
                                    <QuickTaskComposer
                                      stacked
                                      compact
                                      autoFocus
                                      placeholder="Task title"
                                      submitLabel="Add"
                                      saving={savingQuickTask}
                                      assignees={assigneesForWorkflowItem(item, {
                                        channels,
                                        directMessages,
                                        users,
                                        currentUserId,
                                      })}
                                      defaultAssigneeId={item.senderId || currentUserId || ''}
                                      onSubmit={(payload) => handleQuickCreateWorkflowTask(item, payload)}
                                      onCancel={() => setAddingWorkflowTaskForId(null)}
                                    />
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setAddingWorkflowTaskForId(item.messageId);
                                        }}
                                        className="w-full flex items-center gap-1.5 px-3.5 pl-12 py-2 text-[12px] font-semibold text-primary-600 hover:text-primary-700 hover:bg-primary-50/80 dark:text-primary-400 dark:hover:text-primary-300 dark:hover:bg-primary-900/20"
                                      >
                                        <PlusIcon className="w-3.5 h-3.5" />
                                        Add task
                                      </button>
                                    )
                                  )}
                    </div>
                              )}
                              {isExpanded && (
                                <div
                                  className="border-t border-gray-100 dark:border-gray-700/80 px-3.5 py-2.5 min-w-0 animate-fadeIn"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {(() => {
                                    const expandDescription = [
                                      item.workflowDescription,
                                      item.contentHtml,
                                      item.content,
                                    ].find((value) => typeof value === 'string' && value.trim()) || '';
                                    if (!expandDescription) {
                                      return (
                                        <p className="text-[12px] text-gray-400 dark:text-gray-500">
                                          No description
                                        </p>
                                      );
                                    }
                                    if (expandDescription.trim().startsWith('<') && expandDescription.includes('</')) {
                                      return (
                                        <div
                                          className="ql-editor-content text-[13px] text-gray-700 dark:text-gray-300 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md"
                                          dangerouslySetInnerHTML={{ __html: expandDescription }}
                                          style={{ fontSize: '0.8125rem', lineHeight: '1.6' }}
                                        />
                                      );
                                    }
                                    return (
                                      <p className="text-[13px] text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words">
                                        {expandDescription}
                                      </p>
                                    );
                                  })()}
                    </div>
                              )}
                            </Row>
                          );
                        })
                  )}
                </>
              )}
                    {isPastDayView && !assignedWorkflowsLoading && pendingAssignedWorkflows.length === 0 && completedTodayWorkflows.length === 0 && (
                      <p className={`${emptyTitleClass} px-4 py-8 text-center`}>No workflows for this day</p>
                    )}
                    </>
                    )}
                    </div>
              )}
            </div>
          </>
              )}
        {showDayTimelineLog && (
          <DayActivityLog
            date={selectedDay}
            ownerUserId={viewingTargetUserId}
            completedTasks={completedTodayTasks}
            completedReminders={completedTodayReminders}
            completedWorkflows={completedTodayWorkflows}
          />
        )}
        </div>
      </div>
        </div>
      </div>

      <MagicPickModal
        isOpen={magicPickOpen}
        onClose={() => setMagicPickOpen(false)}
        items={magicPickItems}
        onOpenItem={openMagicPickItem}
      />

      {needToCallDetail ? (
        <NeedToCallDetailModal
          item={needToCallDetail}
          user={users.find((entry) => String(entry.id) === String(needToCallDetail.targetUserId))}
          onClose={() => setNeedToCallDetail(null)}
          onCalled={() => {
            patchNeedToCall(needToCallDetail.id, { done: true, status: 'called' });
            setNeedToCallDetail(null);
          }}
          onSkip={() => {
            patchNeedToCall(needToCallDetail.id, { done: true, status: 'skipped' });
            setNeedToCallDetail(null);
          }}
          onFollowUp={() => {
            patchNeedToCall(needToCallDetail.id, { done: false, status: 'followup' });
            setNeedToCallDetail(null);
          }}
        />
      ) : null}

      <TaskCreationDialog
        isOpen={showTaskCreateDialog}
        onClose={handleCloseCreateTask}
        initialDescription=""
        editingTask={editingTask}
        onSaved={handleTaskCreated}
        initialDelegateId={viewingTargetUserId}
        defaultStatus={TaskStatus.PICK_TODAY}
      />

      <ConfirmModal
        isOpen={Boolean(subtaskToDelete)}
        onClose={() => {
          if (!savingQuickTask) setSubtaskToDelete(null);
        }}
        onConfirm={confirmDeleteSubtask}
        title="Delete subtask"
        message={
          subtaskToDelete?.subtask?.title
            ? `Delete "${subtaskToDelete.subtask.title}"? This cannot be undone.`
            : 'Delete this subtask? This cannot be undone.'
        }
        confirmText={savingQuickTask ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        type="danger"
      />

      {/* Modal */}
      {showAllRemindersModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowAllRemindersModal(false)}
            aria-hidden="true"
          />
          <div
            className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-100 dark:border-gray-700"
            role="dialog"
            aria-modal="true"
            aria-labelledby="all-reminders-title"
          >
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
              <h3 id="all-reminders-title" className="text-lg font-bold text-gray-900 dark:text-white min-w-0 flex-1">
                All reminders
              </h3>
              <button
                type="button"
                onClick={() => setShowAllRemindersModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                aria-label="Close all reminders"
              >
                <CloseIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 pt-3 flex items-center gap-2 border-b border-gray-100 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setAllRemindersModalTab('all')}
                className={`px-3 py-1.5 mb-[-1px] text-sm font-semibold rounded-t-lg border-b-2 ${
                  allRemindersModalTab === 'all'
                    ? 'border-amber-500 text-amber-700 dark:text-amber-300'
                    : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                All
                <span className="ml-1.5 text-xs tabular-nums text-gray-400">{filteredAllReminders.length}</span>
              </button>
              <button
                type="button"
                onClick={() => setAllRemindersModalTab('pendingApprovals')}
                className={`px-3 py-1.5 mb-[-1px] text-sm font-semibold rounded-t-lg border-b-2 ${
                  allRemindersModalTab === 'pendingApprovals'
                    ? 'border-amber-500 text-amber-700 dark:text-amber-300'
                    : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                Pending approvals
                <span className="ml-1.5 text-xs tabular-nums text-gray-400">{filteredPendingApprovals.length}</span>
              </button>
              <button
                type="button"
                onClick={() => setAllRemindersModalTab('history')}
                className={`px-3 py-1.5 mb-[-1px] text-sm font-semibold rounded-t-lg border-b-2 ${
                  allRemindersModalTab === 'history'
                    ? 'border-amber-500 text-amber-700 dark:text-amber-300'
                    : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                History
              </button>
            </div>
            {allRemindersModalTab !== 'history' ? (
              <>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
              <label className="block">
                <span className="sr-only">Search reminders</span>
                <input
                  type="search"
                  value={allRemindersQuery}
                  onChange={(event) => setAllRemindersQuery(event.target.value)}
                  placeholder="Search reminders"
                  className="w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400"
                />
              </label>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-5">
              {allRemindersModalTab === 'pendingApprovals' ? (
                forMeLoading ? (
                  <div className="py-10 text-center text-sm text-gray-500">Loading pending approvals…</div>
                ) : pendingApprovalReminders.length === 0 ? (
                  <div className="py-10 text-center">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">No pending approvals</p>
                    <p className="mt-1 text-xs text-gray-500">Reminders others send you will appear here until you accept or reject them</p>
                  </div>
                ) : filteredPendingApprovals.length === 0 ? (
                  <div className="py-10 text-center">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">No matching reminders</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    {filteredPendingApprovals.map((reminder) => renderReminderRow(reminder, {
                      allowComplete: false,
                      compactActions: true,
                      forMe: true,
                      hideScheduleActions: true,
                      variant: 'card',
                    }))}
                  </div>
                )
              ) : listLoading ? (
                <div className="py-10 text-center text-sm text-gray-500">Loading reminders…</div>
              ) : allRemindersSorted.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">No reminders yet</p>
                  <p className="mt-1 text-xs text-gray-500">Add one from Pending Reminders to get started</p>
                </div>
              ) : filteredAllReminders.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">No matching reminders</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                  {filteredAllReminders.map((reminder) => renderReminderRow(reminder, {
                    allowComplete: true,
                    compactActions: true,
                    hideScheduleActions: true,
                    variant: 'card',
                  }))}
                </div>
              )}
            </div>
              </>
            ) : (
              <div className="flex-1 min-h-0 overflow-hidden p-5">
                <ReminderHistoryReport reminders={allRemindersSorted} />
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-100 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {editingReminder ? 'Edit reminder' : cloneDraft ? 'Clone reminder' : 'New reminder'}
              </h3>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
              <ReminderForm
                initialData={editingReminder || cloneDraft}
                existingReminders={reminders}
                onSubmit={handleFormSubmit}
                onCancel={handleCloseModal}
                saving={saving}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Full-view reminder modal */}
      {viewingReminder && (
        <ReminderTriggeredModal
          reminder={
            displayedReminders.find((item) => item.id === viewingReminder.id)
            || reminders.find((item) => item.id === viewingReminder.id)
            || viewingReminder
          }
          onClose={() => setViewingReminder(null)}
          onComplete={
            viewMode !== 'forMe' && selectedDay <= todayStr
              ? async () => {
                  await handleCompleteReminderForToday(viewingReminder);
                  setViewingReminder(null);
                }
              : undefined
          }
          completing={completingReminderId === viewingReminder.id}
          skipping={skippingReminderId === viewingReminder.id}
          occurrenceDate={selectedDay}
          onSnooze={selectedDay === todayStr ? handleSnoozeReminder : undefined}
          onSkip={
            viewMode !== 'forMe' && selectedDay <= todayStr
              ? () => handleSkipReminderForToday(viewingReminder)
              : undefined
          }
          onToggleTask={
            viewMode !== 'forMe' && selectedDay <= todayStr
              ? (task) => handleToggleReminderTask(
                  displayedReminders.find((item) => item.id === viewingReminder.id)
                  || reminders.find((item) => item.id === viewingReminder.id)
                  || viewingReminder,
                  task,
                )
              : undefined
          }
          togglingTask={Boolean(togglingTaskKey)}
        />
      )}
      {viewingPickTodayTask && (
        <TaskViewDialog
          task={
            pickTodayTasks.find((item) => item.id === viewingPickTodayTask.id)
            || completedTodayTasks.find((item) => item.id === viewingPickTodayTask.id)
            || viewingPickTodayTask
          }
          mappedTasks={mappedTasksByTaskId[viewingPickTodayTask.id] || []}
          mappedMessagesByTaskId={taskMessagesByTaskId}
          onClose={() => setViewingPickTodayTask(null)}
          usePortal
          zIndexClass="z-[1000]"
          onEdit={handleEditTask}
          onProgressChange={(pct) => handleProgressPickTodayTask(viewingPickTodayTask, pct)}
          onAddSubtask={(payload) => handleQuickCreateSubtask(viewingPickTodayTask, payload)}
          onEditSubtask={(subtask, payload) => handleQuickEditSubtask(viewingPickTodayTask, subtask, payload)}
          onDeleteSubtask={
            sameUserId(viewingPickTodayTask.createdByUserId, currentUserId)
              ? (subtask) => handleDeleteSubtask(viewingPickTodayTask, subtask)
              : undefined
          }
          onCompleteSubtask={
            viewingPickTodayTask.status === TaskStatus.COMPLETED
              ? undefined
              : (subtask) => handleCompleteSubtask(viewingPickTodayTask, subtask)
          }
          onCompleteTask={
            viewingPickTodayTask.status === TaskStatus.COMPLETED
              ? undefined
              : () => handleCompletePickTodayTask({ stopPropagation() {} }, viewingPickTodayTask)
          }
          onUndoComplete={
            viewingPickTodayTask.status === TaskStatus.COMPLETED && !viewingTargetUserId
              ? () => handleUndoCompleteTask({ stopPropagation() {} }, viewingPickTodayTask)
              : undefined
          }
          onDeleteTask={
            viewingPickTodayTask.status === TaskStatus.COMPLETED
            || !sameUserId(viewingPickTodayTask.createdByUserId, currentUserId)
              ? undefined
              : () => handleDeletePendingTask(
                viewingPickTodayTask,
                (mappedTasksByTaskId[viewingPickTodayTask.id] || []).filter(isNamedSubtask),
              )
          }
          completing={completingTaskId === viewingPickTodayTask.id}
          savingSubtask={savingQuickTask}
          subtasksReadOnly={viewingPickTodayTask.status === TaskStatus.COMPLETED}
          workTimerDisabled={!canUseWorkTimer}
          onNavigateToMessage={(task) => {
            handleTaskChatClick({
              messageId: task.messageId,
              channelId: task.channelId,
              replyToMessageId: task.replyToMessageId || null,
            });
            setViewingPickTodayTask(null);
          }}
          onOpenWorkflow={(item) => {
            setViewingPickTodayTask(null);
            if (item?.messageId) setViewingWorkflow(item);
          }}
          onDelegated={(updated) => {
            if (!updated?.id) return;
            setViewingPickTodayTask((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
            setOptimisticCompletedTasks((prev) => {
              if (!prev.some((item) => item.id === updated.id)) {
                return Number(updated.completionTimeInMinutes) > 0
                  ? [{ ...updated }, ...prev]
                  : prev;
              }
              return prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item));
            });
            setPickTodayTasks((prev) => prev.map((item) => (
              item.id === updated.id ? { ...item, ...updated } : item
            )));
            window.dispatchEvent(new CustomEvent('workloop:task-updated', { detail: { task: updated } }));
          }}
          footerActions={
              <Link
                to="/chat/tasks"
              className="px-3 py-1.5 text-[12px] font-semibold rounded-lg text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30"
                onClick={() => setViewingPickTodayTask(null)}
              >
              Task Management
              </Link>
          }
        />
      )}
      {viewingWorkflow && (
        <WorkflowViewDialog
          item={(() => {
            const resolved = pendingAssignedWorkflows.find((wf) => wf.messageId === viewingWorkflow.messageId)
              || completedTodayWorkflows.find((wf) => wf.messageId === viewingWorkflow.messageId)
              || assignedWorkflows.find((wf) => wf.messageId === viewingWorkflow.messageId)
              || viewingWorkflow;
            return viewingWorkflow.sourceTask
              ? { ...resolved, sourceTask: viewingWorkflow.sourceTask }
              : resolved;
          })()}
          mappedTasks={uniqueTasksById(
            workflowMappedTasksByMessageId[viewingWorkflow.messageId] || [],
            pickTodayTasks.filter((task) => isWorkflowMappingParent(task, viewingWorkflow.messageId)),
            completedTodayTasks.filter((task) => isWorkflowMappingParent(task, viewingWorkflow.messageId)),
            viewingWorkflow.sourceTask,
          )}
          mappedMessagesByTaskId={taskMessagesByTaskId}
          mappedByTaskId={mappedTasksByTaskId}
          onClose={() => setViewingWorkflow(null)}
          onComplete={
            Object.values(viewingWorkflow.workflowByCategory || {}).includes(WORKFLOW_CLOSED_STATUS_ID)
              ? undefined
              : () => handleCompleteWorkflow({ stopPropagation() {} }, viewingWorkflow)
          }
          onUndoComplete={
            !viewingTargetUserId
              ? () => {
                const resolved = completedTodayWorkflows.find((wf) => wf.messageId === viewingWorkflow.messageId)
                  || assignedWorkflows.find((wf) => wf.messageId === viewingWorkflow.messageId)
                  || viewingWorkflow;
                handleUndoCompleteWorkflow({ stopPropagation() {} }, {
                  ...viewingWorkflow,
                  ...resolved,
                  channelId: resolved.channelId || viewingWorkflow.channelId,
                });
              }
              : undefined
          }
          completing={completingWorkflowId === viewingWorkflow.messageId}
          onAddTask={(payload) => handleQuickCreateWorkflowTask(viewingWorkflow, payload)}
          onEditTask={(task, payload) => handleQuickEditTask(task, payload)}
          onDeleteTask={(task) => handleDeletePendingTask(
            task,
            (mappedTasksByTaskId[task.id] || []).filter(isNamedSubtask),
          )}
          onCompleteTask={(task) => handleCompletePickTodayTask({ stopPropagation() {} }, task)}
          savingTask={savingQuickTask}
          workTimerDisabled={!canUseWorkTimer}
          onNavigateToChat={() => openWorkflowInChat(viewingWorkflow)}
          onViewImage={(images, index) => setViewingWorkflowGallery({ images, index })}
          onDownload={handleWorkflowAttachmentDownload}
        />
      )}
      {viewingWorkflowGallery && (
        <ImageViewerModal
          images={viewingWorkflowGallery.images}
          initialIndex={viewingWorkflowGallery.index}
          onClose={() => setViewingWorkflowGallery(null)}
          onDownload={handleWorkflowAttachmentDownload}
        />
      )}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes mapTargetBlink {
          0%, 100% {
            background-color: rgba(254, 249, 195, 0.45);
            border-color: rgb(234 179 8);
            box-shadow: 0 0 0 0 rgba(234, 179, 8, 0.35);
          }
          50% {
            background-color: rgba(254, 240, 138, 0.85);
            border-color: rgb(202 138 4);
            box-shadow: 0 0 0 3px rgba(234, 179, 8, 0.45);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out forwards;
        }
        .map-target-blink {
          animation: mapTargetBlink 1.1s ease-in-out infinite;
        }
        .dark .map-target-blink {
          animation-name: mapTargetBlinkDark;
        }
        @keyframes mapTargetBlinkDark {
          0%, 100% {
            background-color: rgba(113, 63, 18, 0.35);
            border-color: rgb(202 138 4);
            box-shadow: 0 0 0 0 rgba(234, 179, 8, 0.25);
          }
          50% {
            background-color: rgba(133, 77, 14, 0.55);
            border-color: rgb(234 179 8);
            box-shadow: 0 0 0 3px rgba(234, 179, 8, 0.35);
          }
        }
      `}} />
      {completionTimeDialog}
    </div>
  );
};

export default DailyTasksPanel;
